import React, { useState, useEffect, useRef } from 'react';
import api from '../services/api';
import nextlaneApi from '../services/nextlane';
import planningDocsApi from '../services/planning-documents';
import esignatureApi from '../services/esignature';
import { PretCourtoisieForm, usePretFichiersBuffer, initPret } from './shared/PretCourtoisieForm';
import { anomalieBadge } from './shared/AnomaliePiece';
import { StockCell } from './shared/StockMagasin';
import { ExpeditionChip } from './shared/Expedition';
import carnetCommandeApi from '../services/carnet-commande';
import { getContrastColor } from '../utils/planningColors';
import { COULEURS_CRAVATE, teinteCravate } from '../utils/cravate';
import { libelleStatutDepuis } from '../utils/statutAtelier';
import { HEURES, toYmd, formatDateFr } from '../utils/planningTime';
import MiniReceptionGrid from './planning/MiniReceptionGrid';
import MiniAtelierGrid from './planning/MiniAtelierGrid';
import './InterventionModal.css';
import './EchangesClients.css';
import planningEchangesApi from '../services/planning-echanges';
import { CANAUX, ICONE_CANAL, LIBELLE_CANAL, RESULTATS, libelleQuand, ancienneteContact } from '../utils/echanges';
import './ESignature.css';
import DemandeNumeroOr from './planning/DemandeNumeroOr';
import AnalyseIADossier from './planning/AnalyseIADossier';
// Même règle de mobile que le serveur : un fixe DOM-TOM n'est pas joignable par
// SMS, le bouton doit le savoir avant le clic.
import { estMobileEnvoyable } from '../services/sms';

// ─── PDF thumbnail (lazy-loaded) ───────────────────────────────────────────────
let _pdfjsLib = null;
async function getPdfjsLib() {
  if (!_pdfjsLib) {
    _pdfjsLib = await import('pdfjs-dist/legacy/build/pdf');
    _pdfjsLib.GlobalWorkerOptions.workerSrc = '//unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js';
  }
  return _pdfjsLib;
}

// « 07/01/26 » — format court, la ligne CMD est déjà dense. La chaîne arrive en
// AAAA-MM-JJ : on la découpe plutôt que de la passer à `new Date()`, qui la
// lirait en UTC et reculerait d'un jour le soir en Guadeloupe (même piège que
// la date de planification des notifications).
function formatDateCourte(iso) {
  const s = String(iso || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return '';
  return `${s.slice(8, 10)}/${s.slice(5, 7)}/${s.slice(2, 4)}`;
}

function PdfThumbnail({ url }) {
  const canvasRef = useRef(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    if (!url) return;
    let cancelled = false;
    (async () => {
      try {
        const pdfjs = await getPdfjsLib();
        const pdf = await pdfjs.getDocument({ url, withCredentials: false }).promise;
        if (cancelled) return;
        const page = await pdf.getPage(1);
        if (cancelled) return;
        const canvas = canvasRef.current;
        if (!canvas) return;
        const DPR = window.devicePixelRatio || 1;
        const vp0 = page.getViewport({ scale: 1 });
        const scale = Math.max(165 / vp0.width, 120 / vp0.height) * DPR;
        const vp = page.getViewport({ scale });
        canvas.width = vp.width;
        canvas.height = vp.height;
        canvas.style.width = Math.round(vp.width / DPR) + 'px';
        canvas.style.height = Math.round(vp.height / DPR) + 'px';
        await page.render({ canvasContext: canvas.getContext('2d'), viewport: vp }).promise;
      } catch {
        if (!cancelled) setError(true);
      }
    })();
    return () => { cancelled = true; };
  }, [url]);
  if (error) return <div className="planning-doc-icon">📄</div>;
  return <canvas ref={canvasRef} style={{ objectFit: 'cover', width: '100%', height: '100%' }} />;
}

// ─── Constantes locales ────────────────────────────────────────────────────────
const FALLBACK_POSTES = ['Réception 1', 'Réception 2', 'Réception 3', 'Réception 4', 'Réception 5'];

// Identique à STATUT_LABELS dans ESignature.js — même code couleur, même onglet
// visuellement, juste filtré sur l'OR courant.
const ESIGN_STATUT_LABELS = {
  en_attente: { label: 'En attente', color: 'var(--color-warning)', bg: '#fef3c7' },
  signe:      { label: 'Signé',      color: 'var(--color-success)', bg: '#d1fae5' },
  refuse:     { label: 'Refusé',     color: '#ef4444', bg: '#fee2e2' },
  expire:     { label: 'Expiré',     color: '#6b7280', bg: '#f3f4f6' },
};

// Fin d'intervention calculée : heure de début + durée, arrondie au quart d'heure
// de la grille. Sert de valeur « auto » quand aucune restitution n'est saisie.
const computeFinIntervention = (heureDebut, duree) => {
  const m = /^(\d{1,2}):(\d{2})/.exec(heureDebut || '');
  const d = parseFloat(duree);
  if (!m || !d || d <= 0) return '';
  const total = parseInt(m[1], 10) * 60 + parseInt(m[2], 10) + Math.round(d * 60);
  if (total >= 24 * 60) return '';
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
};

// Fraîcheur d'une position parking : sans l'année quand c'est l'année courante.
const formatMajParking = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const memeAnnee = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', ...(memeAnnee ? {} : { year: 'numeric' }) })
    + ' à ' + d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
};

const formatDuree = (duree) => {
  const d = parseFloat(duree) || 0;
  if (d < 1) return `${Math.round(d * 60)}min`;
  const h = Math.floor(d);
  const m = Math.round((d - h) * 60);
  if (m === 0) return `${h}h`;
  return `${h}h${m}`;
};

// Nom AVANT prénom, comme dans PlanningAtelier (demande métier du 2026-08-09).
const getStaffLabel = (s) => [s.nom, s.prenom].filter(Boolean).join(' ') || s.nom || `Staff #${s.id}`;

const INITIAL_FORM_DATA = {
  client_nom: '',
  client_prenom: '',
  client_tel: '',
  client_tel2: '',
  client_email: '',
  immatriculation: '',
  vin: '',
  kilometrage: '',
  numero_cravate: '',
  cravate_couleur: '',
  poste_reception: null,
  heure_reception: '08:00',
  heure_restitution_prevue: '',   // '' = non renseignée (repli sur la fin d'intervention calculée)
  date_restitution_prevue: '',    // '' = le jour de l'intervention (cas courant)
  client_attend: false,
  a_planifier: false,             // véhicule déposé sans créneau : hors grille, section 🪝 du panneau
  contrat_maintenance: false,     // véhicule sous contrat de maintenance (saisie réception)
  conducteur_nom: '',             // conducteur ≠ client (flottes / clients pros, ponctuel)
  conducteur_prenom: '',
  conducteur_tel: '',
  conducteur_email: '',
  date_reception: toYmd(new Date()),
  mecanicien: '',
  mecanicien_id: null,
  heure_debut: '09:00',
  duree: 2,
  type_intervention: 'Révision',
  commentaire: '',
  date_intervention: toYmd(new Date()),
  pieces: [],
  statut_pieces: '',
  statut_avancement: 'planifie',
  numero_or: '',
  marque: '',
  modele: '',
  statut_atelier_id: null,
  travaux: [],
  nextlane_client_id: '',
  nextlane_veh_id: '',
  nextlane_dealership_id: '',
  nextlane_appointment_id: '',
  nextlane_pushed_at: null,
};

/**
 * InterventionModal — composant indépendant pour la modale dossier d'intervention.
 *
 * Props requises :
 *   - show            : boolean (afficher la modale)
 *   - onClose         : () => void
 *   - onSaved         : (infos?) => void — `infos` = { creation, interventionId, clientNom }
 *                       à la CRÉATION seulement, sinon appelé sans argument.
 *   - intervention    : object | null (si édition, l'objet intervention ; null si création)
 *   - linkedReception : object | null (rdv réception lié)
 *   - user            : object (utilisateur connecté)
 *   - editMode        : boolean
 *   - statutsAtelier  : array
 *   - travauxTypes    : array
 *   - mecaniciens     : array (staff mécaniciens)
 *   - receptionnaires : array (staff réceptionnaires)
 *   - interventions   : array (toutes les interventions — pour historique véhicule)
 *   - initialFormOverrides : object | null (pré-remplissage partiel, ex: drag-create)
 *   - onDelete        : (interventionId, receptionId) => void (optionnel)
 *   - onSummary       : (summaryData) => void (optionnel — afficher le récap après création)
 */
// Champs surveillés pour « modifications non enregistrées ». Un seul extracteur,
// utilisé pour la référence ET pour la comparaison : deux listes distinctes
// finissaient toujours par diverger, et le moindre écart signalait des
// modifications inexistantes.
const champsSuivis = (f) => JSON.stringify({
  client_nom: f.client_nom, client_prenom: f.client_prenom,
  client_tel: f.client_tel, client_tel2: f.client_tel2, client_email: f.client_email,
  immatriculation: f.immatriculation, vin: f.vin,
  poste_reception: f.poste_reception, heure_reception: f.heure_reception,
  heure_restitution_prevue: f.heure_restitution_prevue || '',
  client_attend: f.client_attend === true,
  a_planifier: f.a_planifier === true,
  contrat_maintenance: f.contrat_maintenance === true,
  conducteur_nom: f.conducteur_nom, conducteur_prenom: f.conducteur_prenom,
  conducteur_tel: f.conducteur_tel, conducteur_email: f.conducteur_email,
  mecanicien: f.mecanicien, mecanicien_id: f.mecanicien_id,
  heure_debut: f.heure_debut, duree: f.duree,
  type_intervention: f.type_intervention, commentaire: f.commentaire,
  date_intervention: f.date_intervention, numero_or: f.numero_or,
  marque: f.marque, modele: f.modele,
  kilometrage: f.kilometrage, numero_cravate: f.numero_cravate, cravate_couleur: f.cravate_couleur,
  statut_atelier_id: f.statut_atelier_id, statut_pieces: f.statut_pieces,
  travaux: f.travaux,
});

export default function InterventionModal({
  show,
  onClose,
  onSaved,
  intervention = null,
  linkedReception = null,
  user,
  editMode = false,
  statutsAtelier = [],
  travauxTypes = [],
  mecaniciens = [],
  // Liste COMPLÈTE de la concession, groupée par atelier, pour les sélecteurs
  // d'édition (mécanicien principal, travaux) : on doit pouvoir changer
  // d'atelier depuis la modale. `mecaniciens` reste la liste filtrée par
  // l'atelier affiché — c'est elle que consomme l'aperçu MiniAtelierGrid.
  groupesMecaniciens = null,
  mecaniciensTous = null,
  receptionnaires = [],
  interventions = [],
  rdvReception = [],
  // Créneaux réellement ouverts, calculés par PlanningAtelier depuis les plages
  // d'ouverture des ateliers. Repli sur HEURES (07:00→16:45) si le prop n'est pas
  // fourni — le composant reste utilisable isolément.
  heures: heuresProp = null,
  onQuickMoveIntervention,
  onQuickMoveReception,
  initialFormOverrides = null,
  onStatutAtelierChange = null,
  onDelete,
  onSummary,
  // Mode consultation : le dossier s'ouvre, rien ne s'y modifie. Le verrou réel
  // est côté serveur ; ici on évite simplement de proposer ce qui sera refusé.
  lectureSeule = false,
  // Capacités Planning du compte. Défaut PERMISSIF : la modale est aussi montée
  // par Gestion Parking, qui ne les connaît pas — un défaut restrictif lui
  // retirerait des fonctions existantes.
  peut = () => true,
  // Bouton « Répartir » (Planning uniquement) : découpe la durée du dossier en
  // journées sur le même mécanicien. Absents ailleurs (Gestion Parking…) : pas
  // de bouton.
  calculerRepartition = null,
  horairesDuMeca = null,
  confirmOnlineRdv = null,   // demande de RDV en ligne à confirmer (mode confirmation) ou null
  onOnlineRdvConfirmed,      // callback après confirmation réussie d'une demande en ligne
  // Ouverture du dossier dans le Planning Atelier. Fournie UNIQUEMENT par les
  // ecrans exterieurs (Gestion Parking, OR en cours) qui rendent la modale chez
  // eux : le bouton n'a pas de sens dans le Planning, ou l'on y est deja.
  onOuvrirPlanning = null,
  // Onglet « Analyse IA » (super-admin) : drapeau global lu par PlanningAtelier
  // via /planning/mon-role. Défaut RESTRICTIF : les autres écrans qui montent la
  // modale (Gestion Parking…) ne le connaissent pas, l'onglet n'y apparaît pas.
  analyseIaEnabled = false,
}) {
  // Sélection/résolution des mécaniciens : liste complète si fournie, sinon la
  // liste filtrée (le composant reste utilisable isolément).
  const mecasSelection = (mecaniciensTous && mecaniciensTous.length) ? mecaniciensTous : mecaniciens;
  const isConfirmMode = !!confirmOnlineRdv;
  const heures = (Array.isArray(heuresProp) && heuresProp.length > 0) ? heuresProp : HEURES;
  // ─── State interne ─────────────────────────────────────────────────────────
  const [formData, setFormData] = useState({ ...INITIAL_FORM_DATA });
  const [editingId, setEditingId] = useState(null);
  // Fin d'intervention calculée, affichée comme repère dans « Restitution prévue ».
  const finIntervention = computeFinIntervention(formData.heure_debut, formData.duree);
  const [editingHeaderCard, setEditingHeaderCard] = useState(null); // 'client' | 'vehicle' | null
  // L'OR ne s'édite que via son bouton ✎ (jamais un champ libre en permanence).
  const [orEditing, setOrEditing] = useState(false);
  const [orBackup, setOrBackup] = useState('');
  // La cravate a son propre emplacement en bout de ligne Véhicule et sa propre
  // édition en place : elle ne dépend pas des données du véhicule (elle change
  // à chaque passage, pas quand la voiture change).
  const [cravateEditing, setCravateEditing] = useState(false);
  const [cravateBackup, setCravateBackup] = useState(null); // { num, couleur } — restauré sur Échap
  // Instantané des champs du header au moment d'ouvrir une édition en place,
  // pour pouvoir Annuler (ou Échap) sans perdre le reste du formulaire.
  const [headerEditBackup, setHeaderEditBackup] = useState(null);
  const [modalTab, setModalTab] = useState('intervention');
  const [saveError, setSaveError] = useState(null);

  // ─── Recherche Nextlane / Datacar ──────────────────────────────────────────
  const [showNextlaneSearch, setShowNextlaneSearch] = useState(false);
  const [nlMode, setNlMode] = useState('immat'); // 'immat' | 'nom' | 'tel'
  // Longueur minimale avant de lancer une requête. En téléphone ce sont les
  // CHIFFRES qui comptent : « 06 96 » fait 5 caractères et ne discrimine rien.
  const nlAssezLong = (q, mode) => (mode === 'tel'
    ? (q || '').replace(/\D/g, '').length >= 4
    : (q || '').trim().length >= 3);
  const [nlQuery, setNlQuery] = useState('');
  const [nlConcessions, setNlConcessions] = useState([]);
  const [nlConcession, setNlConcession] = useState('all');
  const [nlResults, setNlResults] = useState([]); // [{dealership, matches: [{vehicle, client}]}]
  const [nlLoading, setNlLoading] = useState(false);
  const nlDebounceRef = useRef(null);
  const nlAbortRef = useRef(null);   // annule les requêtes en vol quand le texte change
  const nlSeqRef = useRef(0);        // ignore les réponses d'une recherche obsolète

  // Push Nextlane
  const [pushingToNextlane, setPushingToNextlane] = useState(false);
  const [pushResult, setPushResult] = useState(null);
  // Menu du statut atelier (pastilles colorées, remplace le <select> natif)
  const [statutMenuOpen, setStatutMenuOpen] = useState(false);

  // « Restitué le 12/08 » / « En intervention depuis le 14/08 » — l'horodatage
  // du statut (2026-08-15, demande utilisateur). C'est LE manque de
  // Mécaplanning : un statut sans date, dont on ignore s'il date d'une heure ou
  // de trois semaines. La donnée existe depuis le 2026-08-14
  // (`statut_atelier_since`, posé par déclencheur en base) : ici on ne fait que
  // l'afficher. `estime` = date reprise d'`updated_at` à la migration, marquée
  // « ~ » comme partout ailleurs (notifications, Tableaux).
  const [statutDepuis, setStatutDepuis] = useState(null); // { date, estime }
  useEffect(() => {
    setStatutDepuis(intervention?.statut_atelier_since
      ? { date: intervention.statut_atelier_since, estime: intervention.statut_atelier_since_estime === true }
      : null);
  }, [intervention]);

  // ─── Onglet Atelier — le compte rendu du compagnon ─────────────────────────
  // Champ libre où le mécanicien écrit ce qu'il a fait sur le véhicule.
  // Enregistré par sa propre route (le compagnon n'a pas le droit de PUT sur le
  // dossier), donc son propre bouton : la barre du bas est celle du formulaire.
  // ⚠️ En state, pas lu directement dans `intervention` : à la CRÉATION, le prop
  // reste `null` même après l'enregistrement — la modale bascule en édition
  // (`editingId`) sans être remontée avec le dossier créé. L'onglet serait resté
  // invisible sur un dossier qu'on vient de saisir. La réponse de création
  // renvoie l'intervention complète, drapeau compris : on le reprend là.
  const [ongletAtelier, setOngletAtelier] = useState(false);
  // Textes préremplis du compte rendu. Ce sont des « messages types » de canal
  // `atelier` : même table, même écran de configuration que les SMS et e-mails.
  const [textesAtelier, setTextesAtelier] = useState([]);
  const compteRenduRef2 = useRef(null);
  // [16] Prépicking : un fait daté, posé d'un clic depuis la barre de statut, à
  // droite des Pièces — c'est le même sujet vu par le magasin.
  // [18] Envoi d'un SMS depuis l'onglet Échanges clients. Réutilise la route
  // d'envoi du Planning, qui rattache désormais l'envoi au dossier — le message
  // reviendra donc dans ce même fil, marqué « exact ».
  // ⚠️ Un clic sur un canal ouvre un BROUILLON local, il n'écrit rien.
  // Avant (2026-09-05) il créait la ligne aussitôt : 99 des 115 échanges de la
  // base étaient vides — 86 % de bruit, laissé par des clics sans suite.
  // L'heure est FIGÉE au clic : c'est le moment de l'appel qui compte, pas
  // celui où l'on finit de taper.
  const [brouillonEchange, setBrouillonEchange] = useState(null); // { canal, sens, quand, resume, busy }
  const [smsEchange, setSmsEchange] = useState(null); // { message, envoi } | null
  // Modèles de SMS de la concession DU DOSSIER, variables déjà substituées par
  // le serveur (`{CLIENT}`, `{IMMAT}`, `{HEURE_RDV}`…). Même route que le menu
  // contextuel du Planning : un seul endroit rend les modèles, jamais deux.
  const [modelesSms, setModelesSms] = useState(null);
  const [smsEchangeBusy, setSmsEchangeBusy] = useState(false);
  const [prepicking, setPrepicking] = useState(null); // { at, par_nom } | null
  const [prepickingBusy, setPrepickingBusy] = useState(false);
  const [compteRendu, setCompteRendu] = useState('');
  const [compteRenduRef, setCompteRenduRef] = useState('');   // dernier état connu du serveur
  const [compteRenduEtat, setCompteRenduEtat] = useState(null); // 'saving' | 'ok' | 'erreur'
  useEffect(() => {
    if (modalTab !== 'atelier' || textesAtelier.length > 0) return;
    api.get('/planning/messages-types')
      .then(r => setTextesAtelier((r.data.messages || [])
        .filter(m => m.canal === 'atelier' && m.actif !== false)))
      .catch(() => setTextesAtelier([]));
  }, [modalTab, textesAtelier.length]);

  // Le bouton INSÈRE, il ne remplace pas : le texte s'ajoute là où est le
  // curseur et le mécanicien continue à écrire à la suite. Remplacer effacerait
  // ce qu'il vient d'écrire — c'est la seule version non destructrice.
  // Balises horodatantes, remplacées AU MOMENT DE L'INSERTION et non à
  // l'enregistrement : ce qu'on veut savoir, c'est quand la ligne a été écrite.
  // `{DATE_AJOUT}` → 04/09/2026, `{HEURE_AJOUT}` → 18:32.
  const remplirBalises = (texte) => {
    const n = new Date();
    return String(texte || '')
      .replace(/\{DATE_AJOUT\}/g, n.toLocaleDateString('fr-FR'))
      .replace(/\{HEURE_AJOUT\}/g, n.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }));
  };

  const insererTexteAtelier = (brut) => {
    const texte = remplirBalises(brut);
    const zone = compteRenduRef2.current;
    const pos = zone ? zone.selectionStart : compteRendu.length;
    const avant = compteRendu.slice(0, pos);
    const apres = compteRendu.slice(pos);
    // Une espace de séparation seulement si le voisin n'en porte pas déjà.
    const sep = avant && !/\s$/.test(avant) ? ' ' : '';
    const suivant = `${avant}${sep}${texte} ${apres}`;
    setCompteRendu(suivant);
    setCompteRenduEtat(null);
    requestAnimationFrame(() => {
      if (!zone) return;
      const curseur = (avant + sep + texte + ' ').length;
      zone.focus();
      zone.setSelectionRange(curseur, curseur);
    });
  };

  // Chargés à la première ouverture du composeur seulement : inutile de les
  // demander à chaque dossier ouvert, la plupart ne partiront pas de SMS.
  const ouvrirComposeurSms = () => {
    setSmsEchange({ message: '', envoi: null });
    if (modelesSms !== null || !editingId) return;
    api.get(`/planning/interventions/${editingId.interventionId}/messages`)
      .then(r => setModelesSms((r.data.messages || []).filter(m => m.canal === 'sms' || m.canal === 'les_deux')))
      .catch(() => setModelesSms([]));
  };

  const envoyerSmsEchange = async () => {
    if (!editingId || smsEchangeBusy) return;
    const texte = (smsEchange?.message || '').trim();
    if (!texte) return;
    setSmsEchangeBusy(true);
    try {
      await api.post(`/planning/interventions/${editingId.interventionId}/sms`, { message: texte });
      setSmsEchange(null);
      // Le fil se recharge : le SMS y revient par la fusion serveur, à sa
      // place chronologique — on n'insère rien à la main, il n'y aurait pas
      // deux vérités.
      // Rechargement du fil : le SMS y revient par la fusion serveur, à sa place
      // chronologique et marqué « envoyé depuis ce dossier ».
      const r = await planningEchangesApi.list(editingId.interventionId);
      setEchanges(r.data.echanges || []);
    } catch (err) {
      setSmsEchange(m => (m ? { ...m, envoi: err.response?.data?.error || 'Envoi impossible' } : m));
    } finally {
      setSmsEchangeBusy(false);
    }
  };

  const basculerPrepicking = async () => {
    if (!editingId || prepickingBusy) return;
    const pose = !prepicking;
    setPrepickingBusy(true);
    try {
      const { data } = await api.patch(
        `/planning/interventions/${editingId.interventionId}/prepicking`, { pose });
      setPrepicking(data.prepicking_at ? { at: data.prepicking_at, par_nom: null } : null);
    } catch (err) {
      console.error('[prepicking]', err);
    } finally {
      setPrepickingBusy(false);
    }
  };

  const enregistrerCompteRendu = async () => {
    if (!editingId) return;
    setCompteRenduEtat('saving');
    try {
      const { data } = await api.patch(
        `/planning/interventions/${editingId.interventionId}/compte-rendu`,
        { compte_rendu_atelier: compteRendu });
      const v = data.compte_rendu_atelier || '';
      setCompteRendu(v); setCompteRenduRef(v); setCompteRenduEtat('ok');
    } catch (err) {
      console.error('[compte-rendu]', err);
      setCompteRenduEtat('erreur');
    }
  };

  // Choix d'un statut atelier — effet IMMÉDIAT sur un dossier existant, même
  // endpoint que le menu contextuel de la grille. La pastille invite au geste
  // « 1 clic » : il doit être tenu. Les tags, juste à côté, persistent déjà
  // ainsi ; un statut resté brouillon de formulaire se perdait à la fermeture,
  // ou déclenchait « modifications non enregistrées » alors qu'on n'avait rien
  // d'autre à enregistrer. En création, le dossier n'existe pas : le statut
  // reste dans le formulaire et part avec lui.
  const choisirStatut = async (statutId) => {
    setStatutMenuOpen(false);
    // Le point délicat : après persistance, la référence « propre » du
    // formulaire doit intégrer le nouveau statut SANS blanchir les autres
    // modifications en attente — on retouche la seule clé concernée dans
    // l'instantané, jamais l'instantané entier.
    const poserLocalement = (id) => {
      setFormData(prev => ({ ...prev, statut_atelier_id: id }));
      if (editingId && initialFormRef.current) {
        try {
          const ref = JSON.parse(initialFormRef.current);
          ref.statut_atelier_id = id;
          initialFormRef.current = JSON.stringify(ref);
        } catch { /* référence illisible : tant pis, le formulaire s'annoncera modifié */ }
      }
    };
    const avant = formData.statut_atelier_id || null;
    const avantDepuis = statutDepuis;
    poserLocalement(statutId);
    if (!editingId || statutId === avant) return;
    // Le déclencheur en base pose `statut_atelier_since = NOW()` sur tout vrai
    // changement : l'affichage local le reflète sans aller-retour.
    setStatutDepuis(statutId ? { date: new Date().toISOString(), estime: false } : null);
    try {
      await api.patch(`/planning/interventions/${editingId.interventionId}/statut-atelier`, { statut_atelier_id: statutId });
      // Même signal que la sauvegarde : le parent propose le SMS « véhicule
      // prêt » au passage en Facturé.
      if (onStatutAtelierChange) onStatutAtelierChange(editingId.interventionId, statutId);
      // Le N° d'OR est visible dans l'en-tête, mais un dossier reçu sans OR y
      // affiche « OR à préparer » — discret au moment où l'on pense au statut.
      // On le réclame, comme depuis la grille et le Kanban.
      if (statutId && !String(formData.numero_or || '').trim()) setDemandeOr(true);
    } catch (e) {
      console.error('[modale/statut-atelier]', e);
      poserLocalement(avant); // l'écran ne doit pas montrer un statut refusé en base
      setStatutDepuis(avantDepuis);
    }
  };

  // Texte de l'horodatage, accolé au nom du statut dans la pastille — logique
  // partagée avec Gestion Parking (utils/statutAtelier.js).
  // ⚠️ Pas `currentStatut` ici : il est déclaré plus bas dans le composant (TDZ).
  const statutDepuisTexte = (() => {
    const statutCourant = statutsAtelier.find(s => s.id === formData.statut_atelier_id);
    const libelle = libelleStatutDepuis(statutCourant?.nom, statutDepuis?.date, statutDepuis?.estime);
    return libelle ? ` ${libelle}` : '';
  })();

  // ── Tags du dossier, gérés DEPUIS la modale (2026-08-15, demande utilisateur)
  // Jusqu'ici il fallait fermer la modale et faire clic droit sur la carte du
  // planning. Même endpoint que le clic droit (PATCH /interventions/:id/tags/
  // :tagId) : pose/retrait immédiats, journalisés, diffusés en SSE — la grille
  // derrière se met à jour toute seule.
  const [tagsDossier, setTagsDossier] = useState([]);
  const [tagMenuOpen, setTagMenuOpen] = useState(false);
  const [tagsDispo, setTagsDispo] = useState(null); // null = référentiel pas encore chargé
  useEffect(() => { setTagsDossier(intervention?.tags || []); }, [intervention]);

  const ouvrirTagMenu = async () => {
    if (tagMenuOpen) { setTagMenuOpen(false); return; }
    setTagMenuOpen(true);
    if (tagsDispo === null) {
      try {
        const { data } = await api.get('/planning/tags');
        const tous = data.tags || [];
        // Les tags sont cloisonnés par concession : on ne propose que ceux du
        // dossier (un super-admin sans filtre reçoit tous les référentiels).
        setTagsDispo(intervention?.concession_id
          ? tous.filter(t => t.concession_id === intervention.concession_id)
          : tous);
      } catch { setTagsDispo([]); }
    }
  };

  const toggleTagDossier = async (tag, pose) => {
    const avant = tagsDossier;
    // Optimiste, rollback si le serveur refuse — le même contrat que le clic
    // droit : l'écran ne doit pas montrer un tag qui n'existe pas en base.
    setTagsDossier(pose
      ? [...avant.filter(t => t.id !== tag.id), { id: tag.id, nom: tag.nom, couleur: tag.couleur }]
      : avant.filter(t => t.id !== tag.id));
    try {
      await api.patch(`/planning/interventions/${editingId.interventionId}/tags/${tag.id}`, { pose });
    } catch (e) {
      console.error('[modale/tags]', e);
      setTagsDossier(avant);
    }
  };

  // Badges contextuels (litige, parking, courtoisie)
  const [badgeLitige, setBadgeLitige] = useState(false);
  const [badgeParking, setBadgeParking] = useState(false);
  const [badgeCourtoisie, setBadgeCourtoisie] = useState(null);
  const [badgeRoole, setBadgeRoole] = useState(false);
  // Position parking du véhicule : où il est garé, et depuis quand.
  const [parking, setParking] = useState(null); // { id, statut, heure_arrivee, heure_prise_en_charge }

  // Sous-données chargées à l'ouverture
  const [interventionLogs, setInterventionLogs] = useState([]);
  const [interventionCommentaires, setInterventionCommentaires] = useState([]);
  const [newCommentText, setNewCommentText] = useState('');
  const [showAllLogs, setShowAllLogs] = useState(false);
  const [showHistorique, setShowHistorique] = useState(false);
  // Aperçus planning : MASQUÉS par défaut (demande utilisateur 2026-08-03) —
  // ils s'ouvrent uniquement via leur bouton « Afficher ».
  const [showReceptionApercu, setShowReceptionApercu] = useState(false);
  const [showAtelierApercu, setShowAtelierApercu] = useState(false);

  // Carte active (bordure accent) : 'reception' | 'travaux' | null. Purement
  // visuel — c'est le repère « vous êtes ici » dans une modale devenue longue.
  const [activeBlock, setActiveBlock] = useState(null);

  // Les aperçus suivent le bloc dans lequel on travaille : UN SEUL visible à la
  // fois, celui du bloc qui a le focus — sinon la modale s'allonge et on se perd.
  // Le masquage volontaire reste manuel (bouton « Masquer ») : refermer sur
  // `onBlur` ferait clignoter l'aperçu à chaque passage d'un champ au suivant.
  const handleFocusReception = () => {
    setShowReceptionApercu(true);
    setShowAtelierApercu(false);
  };
  // ⚠️ Le planning ATELIER ne s'ouvre plus au clic sur le bloc Travaux
  // (2026-09-04, demande utilisateur) : il n'est pas intuitif, il allongeait la
  // modale sans servir, et il est promis à la suppression. Il reste accessible
  // par « Afficher ». Celui de la RÉCEPTION, lui, garde son ouverture
  // automatique — c'est le repère qui sert vraiment au réceptionnaire.
  const handleFocusTravaux = () => {
    setShowReceptionApercu(false);
  };

  // Appuyer N'IMPORTE OÙ dans une carte y bascule le travail — pas seulement dans
  // un champ : le titre, un libellé ou une zone vide de la carte suffisent.
  // `mousedown` plutôt que `click` : ça réagit dès l'appui, y compris quand le
  // geste se poursuit en glisser-déposer dans l'aperçu.
  // Seul le bouton « Masquer » est exclu — sinon il refermerait l'aperçu que ce
  // même appui viendrait de rouvrir, et le bouton deviendrait inopérant.
  const handleEnterBlock = (bloc) => (e) => {
    if (e.target?.closest?.('.im-section-link')) { setActiveBlock(bloc); return; }
    setActiveBlock(bloc);
    if (bloc === 'reception') handleFocusReception();
    else handleFocusTravaux();
  };

  // Travaux
  const [expandedTravaux, setExpandedTravaux] = useState({});
  const [showAutreTravail, setShowAutreTravail] = useState(false);
  const [autreTravailLibelle, setAutreTravailLibelle] = useState('');
  const [customDureeIdx, setCustomDureeIdx] = useState(null);
  // Répartition sur plusieurs jours : null, { busy }, { erreur } ou l'aperçu
  // calculé { total, segments, reste } en attente de « Appliquer ».
  const [repartition, setRepartition] = useState(null);

  // Photos
  const [atelierPhotos, setAtelierPhotos] = useState([]);
  // Une visionneuse ouvre une GALERIE, pas une photo isolée : { items, index }.
  // L'URL d'une photo « Tour du véhicule » n'est connue qu'une fois son base64
  // chargé, donc un item garde soit la photo Atelier complète, soit la clé de
  // cache — résolue au rendu, pour que la navigation marche aussi sur une photo
  // encore en cours de chargement.
  // Vrai quand un statut vient d'être posé sur ce dossier alors qu'il n'a pas
  // de N° d'OR. Le dossier concerné est forcément celui qui est ouvert.
  const [demandeOr, setDemandeOr] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const vignetteActiveRef = useRef(null);
  const [tourVehiculeDossiers, setTourVehiculeDossiers] = useState([]); // groupés par type (reception/restitution)
  const [tourVehiculePhotoCache, setTourVehiculePhotoCache] = useState({}); // { [dossierId__photoId]: base64 }

  // Courtoisie — prêt lié à cet OR (récap RGPD-safe : ni documents, ni naissance/permis)
  const [pretCourtoisie, setPretCourtoisie] = useState(null);
  const [pretCourtoisieLoading, setPretCourtoisieLoading] = useState(false);
  const [showPretForm, setShowPretForm] = useState(false);
  const [pretModeIntervention, setPretModeIntervention] = useState('interne');
  const [formPretIntervention, setFormPretIntervention] = useState(initPret);
  const [savingPretIntervention, setSavingPretIntervention] = useState(false);
  const [vehiculesCourtoisie, setVehiculesCourtoisie] = useState([]);
  const [loueursCourtoisie, setLoueursCourtoisie] = useState([]);
  const {
    pretFichiersBuffer: pretFichiersBufferIntervention,
    addPretFichierBuffer: addPretFichierBufferIntervention,
    removePretFichierBuffer: removePretFichierBufferIntervention,
    clearPretFichiersBuffer: clearPretFichiersBufferIntervention,
  } = usePretFichiersBuffer();

  // Documents
  const [planningDocs, setPlanningDocs] = useState([]);
  // ── Échanges clients ──
  const [echanges, setEchanges] = useState([]);
  const [echangesCharges, setEchangesCharges] = useState(false);
  const [echangeEdite, setEchangeEdite] = useState(null);   // id en cours d'édition
  const [echangeDrag, setEchangeDrag] = useState(null);     // id survolé par un fichier
  const [docViewer, setDocViewer] = useState(null);
  const [docDragging, setDocDragging] = useState(false);
  const [renamingDocId, setRenamingDocId] = useState(null);

  // Carnet de commande
  const [carnetData, setCarnetData] = useState(null);
  const [carnetLoading, setCarnetLoading] = useState(false);
  const [stockMap, setStockMap] = useState({});
  const [stockLoading, setStockLoading] = useState(false);

  // ── Impression de documents types ──────────────────────────────────────────
  // printMenu : le menu sous le bouton 🖨️ (liste chargée à l'ouverture — les
  // documents changent rarement, mais un menu figé au montage de la modale
  // afficherait un référentiel périmé toute la session).
  // docApercu : l'aperçu plein écran du document rendu, d'où part l'impression.
  const [printMenu, setPrintMenu] = useState({ open: false, chargement: false, documents: [] });
  const [docApercu, setDocApercu] = useState(null); // { docId, libelle, html, chargement }
  const docApercuFrameRef = useRef(null);

  const ouvrirMenuImpression = async () => {
    if (printMenu.open) { setPrintMenu(p => ({ ...p, open: false })); return; }
    setPrintMenu({ open: true, chargement: true, documents: [] });
    try {
      const { data } = await api.get(`/planning/interventions/${editingId.interventionId}/documents`);
      setPrintMenu({ open: true, chargement: false, documents: data.documents || [] });
    } catch {
      setPrintMenu({ open: true, chargement: false, documents: [] });
    }
  };

  const ouvrirApercuDocument = async (doc) => {
    setPrintMenu(p => ({ ...p, open: false }));
    setDocApercu({ docId: doc.id, libelle: doc.libelle, html: '', chargement: true });
    try {
      const { data } = await api.get(`/planning/interventions/${editingId.interventionId}/documents/${doc.id}/rendu`);
      setDocApercu({ docId: doc.id, libelle: data.libelle, html: data.html, chargement: false });
    } catch (e) {
      setDocApercu(null);
      alert(e.response?.data?.error || 'Impossible de préparer le document');
    }
  };

  const imprimerDocApercu = () => {
    const frame = docApercuFrameRef.current;
    if (!frame?.contentWindow) return;
    frame.contentWindow.focus();
    frame.contentWindow.print();
    // Trace au journal du dossier : on saura qu'une confirmation a été remise.
    // L'échec du log ne doit pas empêcher l'impression, déjà partie.
    api.post(`/planning/interventions/${editingId.interventionId}/documents/${docApercu.docId}/impression`)
      .catch(() => {});
  };

  // Signature électronique
  const [esignList, setEsignList] = useState([]);
  const [esignLoading, setEsignLoading] = useState(false);
  const [showEsignForm, setShowEsignForm] = useState(false);
  const [esignTitre, setEsignTitre] = useState('');
  const [esignClientNom, setEsignClientNom] = useState('');
  const [esignClientEmail, setEsignClientEmail] = useState('');
  const [esignClientTel, setEsignClientTel] = useState('');
  const [esignFile, setEsignFile] = useState(null);
  const [esignSaving, setEsignSaving] = useState(false);
  const [esignError, setEsignError] = useState('');
  const [esignDetail, setEsignDetail] = useState(null);
  const [esignCopied, setEsignCopied] = useState(null);
  const [esignSendResult, setEsignSendResult] = useState(null);

  // Snapshot du formData initial pour détecter les modifications non sauvegardées
  const initialFormRef = useRef(null);
  const formRef = useRef(null); // Ctrl+S → requestSubmit()
  const hasSavedRef = useRef(false);

  // AbortController pour les fetchs
  const abortRef = useRef(null);

  // ─── Effet : initialiser la modale à l'ouverture ────────────────────────────
  useEffect(() => {
    if (!show) return;

    // Annuler les fetchs précédents
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    const signal = ctrl.signal;
    const isAbort = (err) => err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED';

    // Reset du state
    setModalTab('intervention');
    setEditingHeaderCard(null);
    setHeaderEditBackup(null);
    setOrEditing(false);
    setSaveError(null);
    setShowAutreTravail(false);
    setCustomDureeIdx(null);
    setRepartition(null);
    setAutreTravailLibelle('');
    setInterventionLogs([]);
    setInterventionCommentaires([]);
    setPlanningDocs([]);
    setEchanges([]); setEchangesCharges(false); setEchangeEdite(null);
    setAtelierPhotos([]);
    setLightbox(null);
    setPartage(null);
    setZipEnCours(null); setZipErreur(null);
    setCarnetData(null);
    setNewCommentText('');
    setShowAllLogs(false);
    setShowHistorique(false);
    // Les deux aperçus partent fermés. Celui de la RÉCEPTION s'ouvre au clic
    // sur son bloc (`handleFocusReception`) ; celui de l'ATELIER ne s'ouvre
    // plus tout seul du tout — il faut le demander (2026-09-04).
    setShowReceptionApercu(false);
    setShowAtelierApercu(false);
    setActiveBlock(null);
    setEsignList([]);
    setShowEsignForm(false);
    setEsignTitre('');
    setEsignClientNom('');
    setEsignClientEmail('');
    setEsignClientTel('');
    setEsignFile(null);
    setEsignError('');
    setEsignDetail(null);
    setEsignCopied(null);
    setDocViewer(null);
    setRenamingDocId(null);
    setBadgeLitige(false);
    setBadgeParking(false);
    setBadgeCourtoisie(null);
    setBadgeRoole(false);
    setPushingToNextlane(false);
    setPushResult(null);
    setStatutMenuOpen(false);
    setTagMenuOpen(false);
    hasSavedRef.current = false;
    setOngletAtelier(false);
    setPrepicking(null); setPrepickingBusy(false);
    setSmsEchange(null); setSmsEchangeBusy(false); setModelesSms(null);
    setBrouillonEchange(null);
    setCompteRendu(''); setCompteRenduRef(''); setCompteRenduEtat(null);

    if (intervention) {
      // ── Mode édition ──
      const isComplement = intervention.is_complement;
      const parentId = isComplement ? intervention.parent_intervention_id : intervention.id;

      const loadSource = async () => {
        let source = intervention;
        if (isComplement) {
          try {
            const r = await api.get(`/planning/interventions/${parentId}`, { signal });
            if (r.data.intervention) source = r.data.intervention;
          } catch (e) { if (!isAbort(e)) console.error('[complement/load parent]', e); }
          if (signal.aborted) return;
        }

        const formInitial = {
          client_nom: source.client_nom || '',
          client_prenom: source.client_prenom || '',
          client_tel: source.client_tel || linkedReception?.tel || '',
          client_tel2: source.client_tel2 || linkedReception?.client_tel2 || '',
          client_email: source.client_email || linkedReception?.email || '',
          immatriculation: source.immatriculation || source.immat || '',
          vin: source.vin || '',
          poste_reception: source.poste_reception || linkedReception?.poste || null,
          // Mémoire du réceptionnaire porté par le dossier : sert quand il n'est pas
          // dans la liste de la concession sélectionnée (dossier ouvert depuis une
          // autre vue) — pour l'afficher, et pour ne pas l'effacer à l'enregistrement.
          _poste_reception_origine: source.poste_reception || linkedReception?.poste || null,
          _receptionnaire_label: source.receptionnaire_label || null,
          heure_reception: source.heure_reception || linkedReception?.heure || '08:00',
          heure_restitution_prevue: source.heure_restitution_prevue || '',
          date_restitution_prevue: (source.date_restitution_prevue || '').slice(0, 10),
          client_attend: source.client_attend === true,
          a_planifier: source.a_planifier === true,
          contrat_maintenance: source.contrat_maintenance === true,
          conducteur_nom: source.conducteur_nom || '',
          conducteur_prenom: source.conducteur_prenom || '',
          conducteur_tel: source.conducteur_tel || '',
          conducteur_email: source.conducteur_email || '',
          // Priorité : le RDV de réception lié, sinon la date de réception
          // ENREGISTRÉE sur le dossier (indispensable pour les dossiers sans
          // planning_rdv — majorité des imports Mécaplanning — sinon la date
          // modifiée « revenait » à date_intervention), sinon la date d'intervention.
          date_reception: linkedReception?.date || source.date_reception || source.date_intervention || source.date || toYmd(new Date()),
          mecanicien: source.mecanicien,
          mecanicien_id: source.mecanicien_id || null,
          heure_debut: source.heure_debut,
          duree: source.duree,
          type_intervention: source.type_intervention || source.description || '',
          commentaire: source.commentaire || '',
          date_intervention: source.date_intervention || source.date || '',
          pieces: source.pieces || [],
          statut_pieces: source.statut_pieces || '',
          statut_avancement: source.statut_avancement || 'planifie',
          numero_or: source.numero_or || '',
          concession_id: source.concession_id || null,
          marque: source.marque || '',
          modele: source.modele || '',
          kilometrage: source.kilometrage ?? '',
          numero_cravate: source.numero_cravate || '',
          cravate_couleur: source.cravate_couleur || '',
          statut_atelier_id: source.statut_atelier_id || null,
          travaux: source.travaux || [],
          nextlane_client_id: source.nextlane_client_id || '',
          nextlane_veh_id: source.nextlane_veh_id || '',
          nextlane_dealership_id: source.nextlane_dealership_id || '',
          nextlane_appointment_id: source.nextlane_appointment_id || '',
          nextlane_pushed_at: source.nextlane_pushed_at || null,
        };
        setFormData(formInitial);
        // Onglet Atelier — repris de `source` et non du prop : sur une carte
        // COMPLÉMENTAIRE, le prop est le complément, qui ne porte ni le drapeau
        // ni le compte rendu ; `source` est le dossier parent, re-chargé.
        setOngletAtelier(source.atelier_onglet_enabled === true);
        setPrepicking(source.prepicking_at
          ? { at: source.prepicking_at, par_nom: source.prepicking_par_nom || null } : null);
        setCompteRendu(source.compte_rendu_atelier || '');
        setCompteRenduRef(source.compte_rendu_atelier || '');
        setCompteRenduEtat(null);
        // La référence dérive du MÊME objet : impossible qu'elle en diverge.
        initialFormRef.current = champsSuivis(formInitial);

        setEditingId({
          interventionId: parentId,
          receptionId: linkedReception?.id || null
        });

        // Charger les données secondaires en parallèle
        api.get(`/planning/interventions/${parentId}/logs`, { signal })
          .then(r => setInterventionLogs(r.data.logs || []))
          .catch(err => { if (!isAbort(err)) console.error('[intervention/logs]', err); });


        api.get(`/planning/interventions/${parentId}/complements`, { signal })
          .then(r => {
            const synthetic = r.data.syntheticTravaux || [];
            if (synthetic.length > 0) {
              // Le formulaire se complète TOUT SEUL : la référence doit suivre,
              // sinon fermer sans avoir rien touché réclamait un enregistrement.
              setFormData(prev => {
                const next = { ...prev, travaux: [...prev.travaux, ...synthetic] };
                if (initialFormRef.current) initialFormRef.current = champsSuivis(next);
                return next;
              });
            }
          })
          .catch(err => { if (!isAbort(err)) console.error('[intervention/complements]', err); });

        if (source.numero_or) {
          api.get(`/atelier-photos/by-or/${source.numero_or}`, { signal })
            .then(r => setAtelierPhotos(r.data.photos || []))
            .catch(err => { if (!isAbort(err)) setAtelierPhotos([]); });

          api.get(`/tour-vehicule/by-or/${source.numero_or}`, { signal })
            .then(r => setTourVehiculeDossiers(r.data.dossiers || []))
            .catch(err => { if (!isAbort(err)) setTourVehiculeDossiers([]); });

          setPretCourtoisieLoading(true);
          api.get('/courtoisie/prets', { params: { or_numero: source.numero_or }, signal })
            .then(r => setPretCourtoisie((r.data.prets || [])[0] || null))
            .catch(err => { if (!isAbort(err)) setPretCourtoisie(null); })
            .finally(() => { if (!signal?.aborted) setPretCourtoisieLoading(false); });
        }

        planningDocsApi.getAll(parentId, { signal })
          .then(r => setPlanningDocs(r.data.documents || []))
          .catch(err => { if (!isAbort(err)) setPlanningDocs([]); });

        // Échanges : jamais demandés sans la capacité — le serveur répondrait
        // 403 et on polluerait la console d'erreurs à chaque ouverture.
        if (peut('client.echanges_voir')) {
          planningEchangesApi.list(parentId)
            .then(r => { setEchanges(r.data.echanges || []); setEchangesCharges(true); })
            .catch(err => { if (!isAbort(err)) { setEchanges([]); setEchangesCharges(true); } });
        }

        api.get(`/planning/interventions/${parentId}/commentaires`, { signal })
          .then(r => setInterventionCommentaires(r.data.commentaires || []))
          .catch(err => { if (!isAbort(err)) setInterventionCommentaires([]); });

        // Les badges contextuels (litige, parking, courtoisie, Roole) ne sont plus
        // chargés ici : ils suivent désormais l'immatriculation/l'OR SAISIS, via
        // l'effet dédié plus bas — sinon rien ne s'affichait tant que le dossier
        // n'était pas enregistré, ni après un changement de véhicule.

        // Le statut réception (file d'attente) n'est plus chargé : son badge a
        // quitté la barre de statuts (2026-08-15, demande utilisateur) — les
        // cartes du panneau Réception portent déjà « arrivé depuis » / « en
        // cours », un emplacement fixe dans la modale ne se justifiait pas.
      };

      loadSource();
    } else if (confirmOnlineRdv) {
      // ── Mode confirmation d'une demande de RDV en ligne ──
      // Pré-remplissage depuis la demande ; mécanicien + réceptionnaire restent
      // volontairement vides (c'est ce que le réceptionnaire doit affecter, en
      // s'appuyant sur les aperçus de disponibilité de la modale).
      const ro = confirmOnlineRdv;
      const dateStr = ro.date_souhaitee ? String(ro.date_souhaitee).slice(0, 10) : toYmd(new Date());
      const rawHeure = (ro.heure_souhaitee || '09:00').slice(0, 5);
      const toMin = (s) => parseInt(s.slice(0, 2), 10) * 60 + parseInt(s.slice(3, 5), 10);
      const heure = heures.includes(rawHeure)
        ? rawHeure
        : heures.reduce((best, h) => (Math.abs(toMin(h) - toMin(rawHeure)) < Math.abs(toMin(best) - toMin(rawHeure)) ? h : best), heures[0]);
      const travaux = (ro.prestations || []).map(p => ({
        travaux_type_id: p.travaux_type_id || null,
        libelle: p.libelle,
        duree: parseFloat(p.duree) || 1,
      }));
      const newForm = {
        ...INITIAL_FORM_DATA,
        client_nom: ro.client_nom || '',
        client_prenom: ro.client_prenom || '',
        client_tel: ro.client_tel || '',
        client_email: ro.client_email || '',
        immatriculation: ro.immatriculation || '',
        marque: ro.marque || '',
        modele: ro.modele || '',
        date_reception: dateStr,
        date_intervention: dateStr,
        heure_reception: heure,
        heure_debut: heure,
        duree: parseFloat(ro.duree_totale) || (travaux.reduce((s, t) => s + (t.duree || 0), 0) || 2),
        travaux,
        type_intervention: travaux.map(t => t.libelle).join(' + ') || ro.type_service || '',
        commentaire: ro.commentaire || '',
        statut_atelier_id: null,   // « Non défini » par défaut (carte blanche)
      };
      setFormData(newForm);
      initialFormRef.current = JSON.stringify(newForm);
      setEditingId(null);
    } else {
      // ── Mode création ──
      const today = toYmd(new Date());
      const newForm = {
        ...INITIAL_FORM_DATA,
        date_reception: today,
        date_intervention: today,
        statut_atelier_id: null,   // « Non défini » par défaut (carte blanche)
        ...(initialFormOverrides || {}),
      };
      // Garde-fou : un appelant qui pose date_intervention sans date_reception
      // laisserait la réception datée d'aujourd'hui — le RDV « s'enregistrait à
      // la date du jour » quand on créait pour un autre jour (cas réel).
      if (initialFormOverrides?.date_intervention && !initialFormOverrides.date_reception) {
        newForm.date_reception = initialFormOverrides.date_intervention;
      }
      setFormData(newForm);
      initialFormRef.current = JSON.stringify(newForm);
      setEditingId(null);
    }

    return () => { ctrl.abort(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, intervention?.id, confirmOnlineRdv?.id]);

  // ─── Badges contextuels : interrogation À LA VOLÉE ────────────────────────
  // Ils suivent l'immatriculation et l'OR **saisis**, pas le dossier enregistré :
  // un client Roole est donc signalé dès la saisie de sa plaque, avant toute
  // sauvegarde, et le badge se met à jour si on change de véhicule en cours de
  // route. Debounce court parce que l'immat s'écrit caractère par caractère.
  useEffect(() => {
    const immat = (formData.immatriculation || '').trim();
    const or = (formData.numero_or || '').trim();
    if (!show || (!immat && !or)) {
      setBadgeLitige(false); setBadgeParking(false); setBadgeCourtoisie(null); setBadgeRoole(false);
      return;
    }
    const ctrl = new AbortController();
    const t = setTimeout(() => {
      api.get('/planning/badges', { params: { immat, numero_or: or }, signal: ctrl.signal })
        .then(r => {
          setBadgeLitige(r.data.litige === true);
          setBadgeParking(r.data.parking === true);
          setBadgeCourtoisie(r.data.courtoisie ? {} : null);
          setBadgeRoole(r.data.roole === true);
        })
        .catch(() => { /* requête annulée ou en échec : on garde l'état précédent */ });
    }, 350);
    return () => { clearTimeout(t); ctrl.abort(); };
  }, [show, formData.immatriculation, formData.numero_or]);

  // ─── Position parking ─────────────────────────────────────────────────────
  // Chargée pour un dossier ENREGISTRÉ : la position est portée par le véhicule
  // dans l'inventaire du parking, pas par le formulaire en cours de saisie.
  useEffect(() => {
    if (!show || !editingId?.interventionId) { setParking(null); return; }
    const ctrl = new AbortController();
    api.get(`/planning/interventions/${editingId.interventionId}/parking`, { signal: ctrl.signal })
      .then(r => setParking(r.data))
      .catch(() => { /* annulé ou indisponible : on n'affiche simplement rien */ });
    return () => ctrl.abort();
  }, [show, editingId?.interventionId]);

  // ─── Charger le carnet de commande quand le numéro OR change ──────────────
  useEffect(() => {
    if (!show || !editingId || !formData.numero_or) {
      setCarnetData(null);
      return;
    }
    setCarnetLoading(true);
    api.get(`/carnet-commande/by-or/${formData.numero_or}`)
      .then(r => setCarnetData(r.data || null))
      .catch(() => setCarnetData(null))
      .finally(() => setCarnetLoading(false));
  }, [show, editingId, formData.numero_or]);

  // ─── Stock magasin des pièces de l'OR ──────────────────────────────────────
  // Une seule requête, portée aux références de CET OR, déclenchée quand le
  // carnet est là — le même geste que le Carnet de commande de Suivi Pièces
  // Atelier. Le stock est relatif à une concession : celle du carnet, renvoyée
  // par `by-or`, et non celle de l'utilisateur, qui peut consulter un dossier
  // d'une autre concession.
  useEffect(() => {
    const lignes = carnetData?.lignes;
    if (!lignes || lignes.length === 0) { setStockMap({}); return; }
    const refs = Array.from(new Set(lignes.map(l => (l.reference || '').trim()).filter(Boolean)));
    if (refs.length === 0) { setStockMap({}); return; }
    let annule = false;
    setStockLoading(true);
    carnetCommandeApi.lookupStock(refs, carnetData.concession_id)
      .then(r => { if (!annule) setStockMap(r.data || {}); })
      .catch(() => { if (!annule) setStockMap({}); })
      .finally(() => { if (!annule) setStockLoading(false); });
    return () => { annule = true; };
  }, [carnetData]);

  // ─── Signature électronique : demandes déjà envoyées pour cet OR ──────────
  // Même droit que le reste de la modale (permission `planning`) — pas de
  // permission séparée requise, cf. checkPageAccessAny côté backend.
  useEffect(() => {
    if (!show || !editingId || !formData.numero_or) {
      setEsignList([]);
      return;
    }
    setEsignLoading(true);
    esignatureApi.getAll({ numero_or: formData.numero_or })
      .then(r => setEsignList(r.data?.esignatures || r.data || []))
      .catch(() => setEsignList([]))
      .finally(() => setEsignLoading(false));
  }, [show, editingId, formData.numero_or]);

  // ─── Handlers ──────────────────────────────────────────────────────────────
  const handleOpenEsignForm = () => {
    setEsignTitre(formData.numero_or ? `OR ${formData.numero_or}` : '');
    setEsignClientNom([formData.client_prenom, formData.client_nom].filter(Boolean).join(' '));
    setEsignClientEmail(formData.client_email || '');
    setEsignClientTel(formData.client_tel || '');
    setEsignFile(null);
    setEsignError('');
    setEsignSendResult(null);
    setShowEsignForm(true);
  };

  const handleSendEsignature = async () => {
    if (!esignFile) { setEsignError('Sélectionnez un document PDF.'); return; }
    if (!esignTitre.trim() || !esignClientNom.trim()) { setEsignError('Titre et nom du client requis.'); return; }
    setEsignSaving(true);
    setEsignError('');
    try {
      const fd = new FormData();
      fd.append('document', esignFile);
      fd.append('titre', esignTitre.trim());
      fd.append('client_nom', esignClientNom.trim());
      if (esignClientEmail.trim()) fd.append('client_email', esignClientEmail.trim());
      if (esignClientTel.trim()) fd.append('client_telephone', esignClientTel.trim());
      if (formData.numero_or) fd.append('numero_or', formData.numero_or);
      if (formData.concession_id) fd.append('concession_id', formData.concession_id); // concession de l'intervention (pour l'envoi SMS)
      fd.append('notify', '1'); // demande l'envoi effectif du lien (email/SMS)
      const res = await esignatureApi.create(fd);
      setShowEsignForm(false);
      setEsignSendResult({
        url: res.data?.signature_url || '',
        sent: res.data?.sent || {},
        email: esignClientEmail.trim(),
        tel: esignClientTel.trim(),
      });
      await reloadEsignList();
    } catch (err) {
      setEsignError(err.response?.data?.error || 'Erreur lors de l\'envoi.');
    } finally {
      setEsignSaving(false);
      setEsignLoading(false);
    }
  };

  const reloadEsignList = async () => {
    if (!formData.numero_or) return;
    setEsignLoading(true);
    try {
      const r = await esignatureApi.getAll({ numero_or: formData.numero_or });
      setEsignList(r.data?.esignatures || r.data || []);
    } catch { /* liste laissée telle quelle en cas d'erreur */ }
    finally { setEsignLoading(false); }
  };

  const handleViewEsignDetail = async (id) => {
    try {
      const r = await esignatureApi.getById(id);
      setEsignDetail(r.data);
    } catch (err) {
      console.error('[esignature/detail]', err);
    }
  };

  const copyEsignLink = (text, id) => {
    navigator.clipboard.writeText(text).then(() => {
      setEsignCopied(id);
      setTimeout(() => setEsignCopied(null), 2000);
    });
  };

  const handleRenvoyerEsign = async (id) => {
    try {
      const r = await esignatureApi.renvoyer(id, { expires_days: 7 });
      copyEsignLink(r.data.signature_url, id);
      reloadEsignList();
    } catch (err) {
      console.error('[esignature/renvoyer]', err);
    }
  };

  const handleDeleteEsign = async (id) => {
    if (!window.confirm('Supprimer cette demande de signature ?')) return;
    try {
      await esignatureApi.delete(id);
      setEsignList(prev => prev.filter(e => e.id !== id));
      if (esignDetail?.esignature?.id === id) setEsignDetail(null);
    } catch (err) {
      console.error('[esignature/delete]', err);
    }
  };

  const formatEsignDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  }) : '—';

  const hasUnsavedChanges = () => {
    if (hasSavedRef.current) return false;
    if (!initialFormRef.current) return false;
    return champsSuivis(formData) !== initialFormRef.current;
  };

  const handleCloseModal = () => {
    if (hasUnsavedChanges()) {
      if (!window.confirm('Vous avez des modifications non enregistrées. Fermer quand même ?')) return;
    }
    if (abortRef.current) { abortRef.current.abort(); abortRef.current = null; }
    onClose();
  };

  // Échap ferme — d'abord ce qui est ouvert PAR-DESSUS (menus, aperçus), puis
  // la modale elle-même, protégée par le garde-fou ci-dessus. La modale n'a
  // jamais eu de gestionnaire Échap : le pied affichait pourtant « Esc fermer »
  // jusqu'au 2026-08-15 — un raccourci mort. Pour un réceptionnaire qui ouvre
  // des dizaines de dossiers par jour, le clavier n'est pas un luxe.
  // Les champs à édition en place (OR, cravate, bandeau) posent stopPropagation
  // sur LEUR Échap : il annule la saisie sans remonter jusqu'ici.
  useEffect(() => {
    if (!show) return;
    const surEchap = (e) => {
      if (e.key !== 'Escape') return;
      if (statutMenuOpen) { setStatutMenuOpen(false); return; }
      if (tagMenuOpen) { setTagMenuOpen(false); return; }
      if (printMenu.open) { setPrintMenu(p => ({ ...p, open: false })); return; }
      if (docApercu) { setDocApercu(null); return; }
      if (lightbox) { setLightbox(null); return; }
      if (docViewer) { setDocViewer(null); return; }
      handleCloseModal();
    };
    // Ctrl+S / Cmd+S : enregistrer sans quitter le clavier — le geste que fait
    // déjà tout le monde par réflexe, intercepté avant le « Enregistrer la
    // page » du navigateur. Passe par requestSubmit() : même validation et
    // même chemin que le bouton Enregistrer.
    const surCtrlS = (e) => {
      if (!(e.ctrlKey || e.metaKey) || e.key.toLowerCase() !== 's') return;
      e.preventDefault();
      if (lectureSeule) return;
      formRef.current?.requestSubmit();
    };
    document.addEventListener('keydown', surEchap);
    document.addEventListener('keydown', surCtrlS);
    return () => {
      document.removeEventListener('keydown', surEchap);
      document.removeEventListener('keydown', surCtrlS);
    };
  });

  // Flèches ← → dans la visionneuse. Enregistré seulement quand elle est
  // ouverte, sinon les flèches captureraient la navigation dans les champs du
  // formulaire qui vit derrière.
  useEffect(() => {
    if (!lightbox) return undefined;
    const surFleche = (e) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      e.preventDefault();
      setLightbox(l => {
        if (!l) return l;
        const i = Math.min(Math.max(l.index + (e.key === 'ArrowLeft' ? -1 : 1), 0), l.items.length - 1);
        return i === l.index ? l : { ...l, index: i };
      });
    };
    document.addEventListener('keydown', surFleche);
    return () => document.removeEventListener('keydown', surFleche);
  }, [lightbox]);

  // La vignette courante doit rester visible : sans ça, passer à la photo 15 la
  // sélectionne hors de l'écran et la bande semble ne pas avoir bougé.
  useEffect(() => {
    if (!lightbox) return;
    vignetteActiveRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  }, [lightbox]);

  // Construit le payload intervention à partir du formData courant (réutilisé par
  // la sauvegarde standard et par le push Nextlane, qui sauvegarde avant d'envoyer).
  // ═══ Durée du dossier vs travaux complémentaires ═══════════════════════
  //
  // Un travail « complémentaire » est planifié AILLEURS que le dossier : autre
  // jour, autre heure, ou autre mécanicien. Le serveur lui crée sa propre carte
  // (`planning_interventions_complement`, cf. `syncTravauxComplements`) — sa
  // durée ne doit donc PAS être comptée dans celle du dossier, sinon le temps
  // est réservé deux fois : une fois sur la carte du complément, une fois en
  // allongeant la carte du mécanicien initial.
  //
  // ⚠️ Cette règle était recopiée à SEPT endroits, et deux d'entre eux
  // divergeaient : l'heure n'était pas prise en compte (alors que le serveur la
  // prend), et le sélecteur d'heure ne recalculait rien du tout. Résultat
  // mesuré : ajouter un 2e travail faisait passer un dossier de 1h30 à 3h30 sur
  // le mécanicien initial. Une seule définition, utilisée partout.
  const estTravailComplementaire = (t) => !!(
    (t.date_intervention && t.date_intervention !== formData.date_intervention)
    || (t.heure_debut && t.heure_debut !== formData.heure_debut)
    || (t.mecanicien_id && t.mecanicien_id !== formData.mecanicien_id)
  );

  // ⚠️ RECALCULER la durée depuis les travaux ÉCRASE une durée posée à la main.
  // Mesuré en base : 2 122 dossiers sur 3 952 (54 %) ont une durée différente de
  // la somme de leurs travaux, dont 881 SUPÉRIEURE — un recalcul les raboterait.
  // Là où le geste ne fait que sortir (ou ramener) un travail du dossier, on
  // ajuste donc par DIFFÉRENCE au lieu de recalculer : la durée propre du
  // dossier est préservée, seul le temps qui part en complément est rendu.
  const dureeApresBascule = (avant, apres, travail) => {
    const d = travail?.duree || 0;
    const actuelle = parseFloat(formData.duree) || 0;
    if (!avant && apres) return Math.max(0.5, Math.round((actuelle - d) * 100) / 100);  // il quitte le dossier
    if (avant && !apres) return Math.round((actuelle + d) * 100) / 100;                 // il y revient
    return actuelle;                                                                    // rien n'a basculé
  };


  // Changer la durée d'un travail reporte l'ÉCART sur le dossier — sauf si ce
  // travail est déjà parti en complément : il n'occupe alors plus la carte.
  const dureeApresChangement = (ancienne, nouvelle, travail) => {
    const actuelle = parseFloat(formData.duree) || 0;
    if (estTravailComplementaire(travail)) return actuelle;
    return Math.max(0.5, Math.round((actuelle + ((nouvelle || 0) - (ancienne || 0))) * 100) / 100);
  };

  // Poste à enregistrer : un poste absent de la liste = manquant, JAMAIS de
  // substitution silencieuse par le premier de la liste — sauf s'il s'agit du
  // réceptionnaire d'origine du dossier, qu'on ne doit pas effacer parce que la
  // vue courante ne le liste pas.
  const posteValide = () => {
    const v = formData.poste_reception;
    if (!v) return null;
    if (receptionnaires.some(s => s.id === v)) return v;
    if (receptionnaires.length > 0 && v === formData._poste_reception_origine) return v;
    return null;
  };

  const buildInterventionPayload = () => {
    // Un poste absent de la liste = manquant : jamais de substitution
    // silencieuse par le premier réceptionnaire de la liste.
    const validPoste = posteValide();

    return {
      mecanicien: formData.mecanicien,
      mecanicien_id: formData.mecanicien_id,
      heure_debut: formData.heure_debut,
      duree: formData.duree,
      date_intervention: formData.date_intervention,
      client_nom: formData.client_nom,
      client_prenom: formData.client_prenom,
      client_tel: formData.client_tel,
      client_tel2: formData.client_tel2,
      client_email: formData.client_email,
      immatriculation: formData.immatriculation,
      vin: formData.vin,
      marque: formData.marque,
      modele: formData.modele,
      kilometrage: formData.kilometrage ? parseInt(formData.kilometrage, 10) : null,
      numero_cravate: formData.numero_cravate || null,
      cravate_couleur: formData.cravate_couleur || null,
      type_intervention: formData.type_intervention,
      commentaire: formData.commentaire,
      pieces: formData.pieces,
      statut_pieces: formData.statut_pieces,
      statut_avancement: formData.statut_avancement,
      numero_or: formData.numero_or,
      heure_reception: formData.heure_reception,
      heure_restitution_prevue: formData.heure_restitution_prevue || '',
      date_restitution_prevue: formData.date_restitution_prevue || null,
      client_attend: formData.client_attend === true,
      a_planifier: formData.a_planifier === true,
      contrat_maintenance: formData.contrat_maintenance === true,
      conducteur_nom: formData.conducteur_nom || '',
      conducteur_prenom: formData.conducteur_prenom || '',
      conducteur_tel: formData.conducteur_tel || '',
      conducteur_email: formData.conducteur_email || '',
      date_reception: formData.date_reception,
      poste_reception: validPoste,
      statut_atelier_id: formData.statut_atelier_id,
      travaux: formData.travaux,
      nextlane_client_id: formData.nextlane_client_id,
      nextlane_veh_id: formData.nextlane_veh_id,
      nextlane_dealership_id: formData.nextlane_dealership_id,
    };
  };

  // Payload RDV (réception) — réutilisé par la sauvegarde et le push à la création.
  const buildRdvPayload = () => {
    // Un poste absent de la liste = manquant : jamais de substitution
    // silencieuse par le premier réceptionnaire de la liste.
    const validPoste = posteValide();
    return {
      poste: validPoste,
      heure: formData.heure_reception,
      date_rdv: formData.date_reception || formData.date_intervention,
      client_nom: formData.client_nom,
      client_prenom: formData.client_prenom,
      client_tel: formData.client_tel,
      client_tel2: formData.client_tel2,
      client_email: formData.client_email,
      immatriculation: formData.immatriculation,
      mecanicien: formData.mecanicien,
      mecanicien_id: formData.mecanicien_id,
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaveError(null);

    // Mode confirmation d'une demande en ligne : mécanicien ET réceptionnaire obligatoires.
    if (isConfirmMode) {
      const manque = [];
      if (!formData.poste_reception) manque.push('un réceptionnaire');
      if (!formData.mecanicien_id) manque.push('un mécanicien');
      if (manque.length) {
        setSaveError(`Pour confirmer le rendez-vous, affectez ${manque.join(' et ')}.`);
        return;
      }
    }

    // Un poste absent de la liste = manquant : jamais de substitution
    // silencieuse par le premier réceptionnaire de la liste.
    const validPoste = posteValide();

    // Réceptionnaire obligatoire — seule la mise « à planifier » lève l'obligation.
    if (!validPoste && formData.a_planifier !== true) {
      setSaveError('Sélectionnez un réceptionnaire (ou cochez « À planifier »).');
      return;
    }

    // Garde-fou : l'intervention ne peut pas être datée AVANT la réception — on
    // n'intervient pas sur un véhicule pas encore reçu. La date de réception et la
    // date d'intervention étant indépendantes (reprogrammer la réception ne bouge
    // pas l'intervention), on bloque l'enregistrement incohérent. Ignoré « à planifier ».
    if (formData.a_planifier !== true && formData.date_reception && formData.date_intervention) {
      const dtRecep = `${formData.date_reception}T${formData.heure_reception || '00:00'}`;
      const dtInterv = `${formData.date_intervention}T${formData.heure_debut || '00:00'}`;
      if (dtInterv < dtRecep) {
        const jjmm = (d, h) => { const [, m, j] = d.split('-'); return `${j}/${m}${h ? ` à ${h}` : ''}`; };
        setSaveError(`L'intervention (${jjmm(formData.date_intervention, formData.heure_debut)}) ne peut pas être avant la réception (${jjmm(formData.date_reception, formData.heure_reception)}). Ajustez la date d'intervention ou de réception.`);
        return;
      }
    }

    const interventionPayload = buildInterventionPayload();
    const rdvPayload = buildRdvPayload();
    let creationInfos = null;

    try {
      if (editingId) {
        await api.put(`/planning/interventions/${editingId.interventionId}`, interventionPayload);
        if (editingId.receptionId) {
          await api.put(`/planning/rdv/${editingId.receptionId}`, rdvPayload);
        }
        // Le statut atelier vient de changer via cette sauvegarde : prévenir le
        // parent (proposition de SMS « véhicule prêt » au passage en Facturé).
        if (onStatutAtelierChange && formData.statut_atelier_id !== (intervention?.statut_atelier_id ?? null)) {
          onStatutAtelierChange(editingId.interventionId, formData.statut_atelier_id || null);
        }
      } else {
        const res = await api.post('/planning/interventions/with-rdv', {
          intervention: interventionPayload,
          rdv: rdvPayload,
          ...(isConfirmMode ? { rdv_online_id: confirmOnlineRdv.id } : {}),
        });
        creationInfos = {
          creation: true,
          interventionId: res?.data?.intervention?.id || null,
          clientNom: [formData.client_prenom, formData.client_nom].filter(Boolean).join(' ').trim(),
          // Ce chemin affiche le récapitulatif « Rendez-vous créé » : la
          // proposition de SMS s'y greffe au lieu de s'empiler par-dessus.
          avecRecap: true,
        };

        if (onSummary) {
          const mecaStaff = mecasSelection.find(m => m.id === formData.mecanicien_id);
          const recepStaff = receptionnaires.find(s => s.id === validPoste);
          onSummary({
            client_prenom: formData.client_prenom || '',
            client_nom: formData.client_nom || '',
            client_tel: formData.client_tel || '',
            immatriculation: formData.immatriculation || '',
            marque: formData.marque || '',
            modele: formData.modele || '',
            date_intervention: formData.date_intervention,
            heure_debut: formData.heure_debut,
            duree: formData.duree,
            heure_reception: formData.heure_reception,
            mecanicien_label: mecaStaff ? getStaffLabel(mecaStaff) : (formData.mecanicien || '—'),
            receptionnaire_label: recepStaff ? getStaffLabel(recepStaff) : '—',
            type_intervention: formData.type_intervention || '',
            travaux: Array.isArray(formData.travaux) ? formData.travaux.filter(t => t && (t.libelle || t.travaux_type_id)) : [],
            numero_or: formData.numero_or || '',
            commentaire: formData.commentaire || '',
          });
        }
      }

      hasSavedRef.current = true;
      if (isConfirmMode && onOnlineRdvConfirmed) onOnlineRdvConfirmed();
      // `creation` permet au parent de proposer le SMS de confirmation — et
      // seulement à la création : le proposer à chaque enregistrement en ferait
      // un réflexe qu'on ferme sans lire.
      else onSaved(creationInfos);
      handleCloseModal();
    } catch (err) {
      console.error('[InterventionModal/handleSubmit] Erreur:', err);
      const msg = err?.response?.data?.error || err?.message || 'Erreur inconnue';
      setSaveError(`Erreur : ${msg}`);
    }
  };

  const handleDeleteIntervention = async () => {
    if (!editingId) return;
    if (!window.confirm('Supprimer cette intervention ?')) return;
    try {
      // Le RDV de reception lie part avec le dossier cote serveur.
      await api.delete(`/planning/interventions/${editingId.interventionId}`);
      if (onDelete) onDelete(editingId.interventionId, editingId.receptionId);
      handleCloseModal();
    } catch (err) {
      console.error('[InterventionModal/handleDelete] Erreur:', err);
      alert('Erreur lors de la suppression.');
    }
  };

  // ─── Courtoisie — ouvrir le formulaire de prêt (préremplissage depuis l'OR) ──
  const handleOpenPretForm = async (mode) => {
    setPretModeIntervention(mode);
    setFormPretIntervention({
      ...initPret,
      or_numero: formData.numero_or || '',
      client_nom: (formData.client_nom || '').toUpperCase(),
      client_prenom: formData.client_prenom || '',
      client_telephone: formData.client_tel || '',
      client_email: formData.client_email || '',
    });
    clearPretFichiersBufferIntervention();
    setShowPretForm(true);
    if (vehiculesCourtoisie.length === 0) {
      api.get('/courtoisie/vehicules').then(r => setVehiculesCourtoisie(r.data.vehicules || [])).catch(() => {});
    }
    if (loueursCourtoisie.length === 0) {
      api.get('/courtoisie/loueurs').then(r => setLoueursCourtoisie(r.data.loueurs || [])).catch(() => {});
    }
  };

  const handleSavePretIntervention = async () => {
    setSavingPretIntervention(true);
    try {
      const normImmat = (s) => (s || '').toUpperCase().replace(/[\s-]/g, '');
      const payload = {
        ...formPretIntervention,
        immat_client: normImmat(formPretIntervention.immat_client),
        loueur_immat: normImmat(formPretIntervention.loueur_immat),
        client_nom: (formPretIntervention.client_nom || '').toUpperCase(),
      };
      const res = pretModeIntervention === 'location'
        ? await api.post('/courtoisie/prets/location', payload)
        : await api.post(`/courtoisie/vehicules/${formPretIntervention.vehicule_id}/pret`, payload);
      const nouveauPret = res?.data?.pret;
      if (nouveauPret?.id && pretFichiersBufferIntervention.length) {
        for (const item of pretFichiersBufferIntervention) {
          try {
            const fd = new FormData();
            fd.append('file', item.file);
            await api.post(`/courtoisie/prets/${nouveauPret.id}/fichiers`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
          } catch { /* le prêt reste créé même si une pièce jointe échoue */ }
        }
      }
      clearPretFichiersBufferIntervention();
      setShowPretForm(false);
      const r = await api.get('/courtoisie/prets', { params: { or_numero: formData.numero_or } });
      setPretCourtoisie((r.data.prets || [])[0] || null);
    } catch (err) {
      alert('Erreur : ' + (err.response?.data?.error || err.message));
    } finally {
      setSavingPretIntervention(false);
    }
  };

  // ─── Photos Tour du véhicule — chargement à la demande (au dépli du groupe) ─
  // Mêmes endpoints/format que la page dédiée TourVehicule.js (image_data en
  // base64 brut, sans préfixe data:, ajouté au rendu).
  const loadTourVehiculeDossierPhotos = (dossierId, photoIds) => {
    (photoIds || []).forEach(photoId => {
      const key = `${dossierId}__${photoId}`;
      if (tourVehiculePhotoCache[key] !== undefined) return;
      setTourVehiculePhotoCache(c => ({ ...c, [key]: null }));
      api.get(`/tour-vehicule/${dossierId}/photo/${photoId}`)
        .then(r => setTourVehiculePhotoCache(c => ({ ...c, [key]: r.data.image_data })))
        .catch(() => setTourVehiculePhotoCache(c => ({ ...c, [key]: false })));
    });
  };

  // ─── Onglet Photos : archive ZIP et partage par e-mail ─────────────────────
  // Les trois rubriques ont deux stockages différents (base pour le Tour du
  // véhicule, S3 pour l'Atelier) : c'est le serveur qui va chercher les octets,
  // ici on ne fait que demander.
  const [zipEnCours, setZipEnCours] = useState(null);   // rubrique en cours
  const [zipErreur, setZipErreur] = useState(null);

  const telechargerZip = async (rubrique) => {
    if (!formData.numero_or || zipEnCours) return;
    setZipEnCours(rubrique); setZipErreur(null);
    try {
      const r = await api.get('/medias-dossier/zip', {
        params: { numero_or: formData.numero_or, rubrique },
        responseType: 'blob',
      });
      // Le nom de l'archive (« N°OR - IMMAT ») est décidé PAR LE SERVEUR : il
      // seul connaît l'immatriculation du dossier, et un nom fabriqué côté
      // client se désaccorderait du contenu.
      const cd = r.headers['content-disposition'] || '';
      const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(cd);
      const brut = /filename="([^"]+)"/i.exec(cd);
      const nom = utf8 ? decodeURIComponent(utf8[1]) : (brut ? brut[1] : `${formData.numero_or}.zip`);
      const url = window.URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = nom;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      // La réponse d'erreur est un Blob (responseType), pas du JSON : la relire.
      let msg = 'Téléchargement impossible';
      try { msg = JSON.parse(await err.response?.data?.text())?.error || msg; } catch (_) {}
      console.error('[medias/zip]', err);
      setZipErreur({ rubrique, msg });
    } finally {
      setZipEnCours(null);
    }
  };

  // Fenêtre de partage : { rubrique, medias, selection:Set, destinataires,
  // message, etat: null|'envoi'|'ok', erreur }
  const [partage, setPartage] = useState(null);

  const ouvrirPartage = async (rubrique) => {
    if (!formData.numero_or) return;
    setPartage({ rubrique, medias: null, selection: new Set(), destinataires: '', message: '', etat: null, erreur: null });
    // Les vignettes du Tour du véhicule vivent dans le cache de l'onglet : les
    // précharger ici, la fenêtre s'ouvrant souvent sans que la rubrique ait été
    // dépliée (le chargement est déclenché au survol).
    if (rubrique !== 'atelier') {
      tourVehiculeDossiers.filter(d => d.type === rubrique)
        .forEach(d => loadTourVehiculeDossierPhotos(d.id, d.photo_ids));
    }
    try {
      const { data } = await api.get('/medias-dossier/liste', {
        params: { numero_or: formData.numero_or, rubrique },
      });
      setPartage(p => (p && p.rubrique === rubrique
        ? { ...p, medias: data.medias || [], plafond: data.plafond_pj_octets }
        : p));
    } catch (err) {
      console.error('[medias/liste]', err);
      setPartage(p => (p && p.rubrique === rubrique
        ? { ...p, medias: [], erreur: 'Chargement impossible' } : p));
    }
  };

  const basculerSelection = (cle) => setPartage(p => {
    if (!p) return p;
    const s = new Set(p.selection);
    if (s.has(cle)) s.delete(cle); else s.add(cle);
    return { ...p, selection: s, erreur: null, etat: null };
  });

  const envoyerPartage = async () => {
    if (!partage || partage.etat === 'envoi') return;
    setPartage(p => ({ ...p, etat: 'envoi', erreur: null }));
    try {
      const { data } = await api.post('/medias-dossier/partager', {
        numero_or: formData.numero_or,
        rubrique: partage.rubrique,
        cles: [...partage.selection],
        destinataires: partage.destinataires,
        message: partage.message,
      });
      setPartage(p => ({ ...p, etat: 'ok', resultat: data }));
    } catch (err) {
      setPartage(p => ({ ...p, etat: null, erreur: err.response?.data?.error || 'Envoi impossible' }));
    }
  };

  // ─── Visionneuse (galerie) ──────────────────────────────────────────────────
  // Un item vaut soit { photo } (média Atelier, URL directe), soit { cle }
  // (photo Tour du véhicule, base64 en cache). La résolution se fait au rendu :
  // une photo encore en chargement occupe sa place dans la galerie au lieu de la
  // décaler, et son image apparaît dès qu'elle arrive.
  const resoudreMedia = (item) => {
    if (!item) return null;
    if (item.photo) {
      const p = item.photo;
      return {
        url: p.url,
        type: p.content_type || '',
        legende: `${p.created_by || '—'} · ${new Date(p.created_at).toLocaleDateString('fr-FR')}`,
        pret: true,
      };
    }
    const data = tourVehiculePhotoCache[item.cle];
    return {
      url: data && data !== false ? `data:image/jpeg;base64,${data}` : null,
      type: '',
      legende: item.legende || '',
      pret: !!data && data !== false,
      erreur: data === false,
    };
  };

  const ouvrirLightbox = (items, index) => setLightbox({ items, index: Math.max(0, index) });

  // Bornée aux extrémités plutôt que circulaire : arriver à la dernière photo et
  // se retrouver sur la première sans l'avoir demandé fait perdre le fil.
  const allerLightbox = (pas) => setLightbox(l => {
    if (!l) return l;
    const i = Math.min(Math.max(l.index + pas, 0), l.items.length - 1);
    return i === l.index ? l : { ...l, index: i };
  });

  // ─── Rendu ──────────────────────────────────────────────────────────────────
  if (!show) return null;

  // Données dérivées
  const carnetLignes = carnetData?.lignes || [];
  const computedStatutPieces = (() => {
    if (carnetLignes.length === 0) return '';
    if (carnetLignes.every(l => l.statut_livraison === 'livre')) return 'disponible';
    if (carnetLignes.some(l => l.statut_livraison === 'en_commande')) return 'en_commande';
    return 'en_commande';
  })();

  if (computedStatutPieces !== formData.statut_pieces) {
    // Recalcul automatique depuis le carnet de commande, sans geste de
    // l'utilisateur : la référence se déplace avec, faute de quoi toute modale
    // dont le statut pièces diffère du carnet s'annonçait modifiée à l'ouverture.
    setTimeout(() => setFormData(prev => {
      const next = { ...prev, statut_pieces: computedStatutPieces };
      if (initialFormRef.current) initialFormRef.current = champsSuivis(next);
      return next;
    }), 0);
  }

  const vehiculeHistory = formData.immatriculation
    ? interventions
        .filter(i =>
          (i.immat === formData.immatriculation || i.immatriculation === formData.immatriculation) &&
          (!editingId || i.id !== editingId.interventionId)
        )
        .sort((a, b) => new Date(b.date || b.date_intervention) - new Date(a.date || a.date_intervention))
    : [];

  const logsToShow = showAllLogs ? interventionLogs : interventionLogs.slice(0, 5);

  const staffRecepBase = receptionnaires.length > 0
    ? receptionnaires.map(r => ({ value: r.id, label: getStaffLabel(r) }))
    : FALLBACK_POSTES.map((label, i) => ({ value: i + 1, label }));
  // Le réceptionnaire du dossier n'est pas dans la liste (autre concession que
  // celle sélectionnée dans le Planning, fiche désactivée…) : on l'ajoute tel
  // que le serveur le nomme, sinon le champ afficherait « — » alors que le
  // dossier en a un. Il reste sélectionnable ; le changer reste possible.
  const posteHorsListe = formData.poste_reception
    && receptionnaires.length > 0
    && !receptionnaires.some(s => s.id === formData.poste_reception)
    && formData.poste_reception === formData._poste_reception_origine;
  const staffRecep = posteHorsListe
    ? [...staffRecepBase, { value: formData.poste_reception, label: `${formData._receptionnaire_label || `Réceptionnaire #${formData.poste_reception}`} (hors liste)` }]
    : staffRecepBase;
  // Nom du réceptionnaire tel que Mécaplanning l'a transmis (« [API Mecaplanning #…] |
  // Réceptionnaire: X | … »), utile seulement quand le rapprochement a échoué.
  const recepMecaplanningInconnu = (() => {
    const m = String(formData.commentaire || '').match(/^\[API Mecaplanning #[^\]]*\][^|]*\|\s*Réceptionnaire:\s*([^|]+)/);
    return m && m[1].trim() ? m[1].trim() : null;
  })();

  // ─── Édition en place du header (cartes Client / Véhicule) ───────────────
  const HEADER_FIELDS = {
    client: ['client_prenom', 'client_nom', 'client_tel', 'client_email'],
    // `numero_cravate` n'est PLUS listé ici : le champ a quitté le panneau
    // véhicule pour son emplacement dédié. L'y laisser ferait restaurer une
    // cravate saisie juste avant, si on annule une édition du véhicule.
    vehicle: ['immatriculation', 'marque', 'modele', 'vin', 'kilometrage'],
    conducteur: ['conducteur_prenom', 'conducteur_nom', 'conducteur_tel', 'conducteur_email'],
  };
  const openHeaderEdit = (card) => {
    setHeaderEditBackup({ ...formData });
    setEditingHeaderCard(card);
  };
  const validateHeaderEdit = () => { setEditingHeaderCard(null); setHeaderEditBackup(null); };
  const cancelHeaderEdit = () => {
    // Ne restaure QUE les champs de la carte éditée : une saisie faite ailleurs
    // dans la modale pendant l'édition ne doit pas être écrasée.
    if (headerEditBackup && editingHeaderCard) {
      const keys = HEADER_FIELDS[editingHeaderCard] || [];
      setFormData(prev => {
        const next = { ...prev };
        keys.forEach(k => { next[k] = headerEditBackup[k]; });
        return next;
      });
    }
    setEditingHeaderCard(null);
    setHeaderEditBackup(null);
  };
  // Entrée = valider, Échap = annuler (stopPropagation : l'Échap global ferme la modale)
  const headerEditKeys = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); validateHeaderEdit(); }
    if (e.key === 'Escape') { e.stopPropagation(); cancelHeaderEdit(); }
  };

  // Dérivés du bandeau d'identité
  const hasHeaderClient = !!(formData.client_prenom || formData.client_nom);
  const hasHeaderVehicle = !!formData.immatriculation;
  const headerClientName = `${formData.client_prenom || ''} ${(formData.client_nom || '').toUpperCase()}`.trim();
  // « SUZUKI SUZUKI IGNIS… » : beaucoup de fiches DMS répètent la marque en
  // tête du modèle. Dédoublonnée à l'AFFICHAGE seulement — la donnée reste
  // intacte, c'est le libellé du bandeau qui n'a pas à bégayer.
  const headerVehLabel = (() => {
    const marque = (formData.marque || '').trim();
    let modele = (formData.modele || '').trim();
    if (marque && modele.toUpperCase().startsWith(marque.toUpperCase() + ' ')) {
      modele = modele.slice(marque.length + 1);
    }
    return [marque, modele].filter(Boolean).join(' ');
  })();
  const hasConducteur = !!(formData.conducteur_nom || formData.conducteur_prenom || formData.conducteur_tel || formData.conducteur_email);
  const headerConducteurName = `${formData.conducteur_prenom || ''} ${(formData.conducteur_nom || '').toUpperCase()}`.trim();
  // « Retirer » depuis la ligne d'édition : le conducteur est ponctuel, on efface tout
  const removeConducteur = () => {
    setFormData(prev => ({ ...prev, conducteur_nom: '', conducteur_prenom: '', conducteur_tel: '', conducteur_email: '' }));
    setEditingHeaderCard(null);
    setHeaderEditBackup(null);
  };

  // Créneaux « en cours de saisie » dessinés en fantômes dans l'aperçu planning
  // intervention : celui de l'intervention principale, PLUS un par travail affecté
  // à son propre mécanicien / sa propre date. Sans ça, un dossier réparti sur deux
  // mécaniciens n'en montrait qu'un dans l'aperçu, alors que l'enregistrement
  // produit bien deux cartes. Les travaux sans planification propre appartiennent
  // à la carte principale : ils ne donnent pas de fantôme supplémentaire.
  const apercuCreneaux = [
    {
      date: formData.date_intervention,
      heure_debut: formData.heure_debut,
      mecanicien_id: formData.mecanicien_id,
      duree: parseFloat(formData.duree) || 1,
      label: headerClientName || formData.immatriculation || 'Ce dossier',
    },
    ...(Array.isArray(formData.travaux) ? formData.travaux : [])
      .slice(1)
      .filter(t => t && (t.mecanicien_id || t.date_intervention || t.heure_debut))
      .map(t => ({
        date: t.date_intervention || formData.date_intervention,
        heure_debut: t.heure_debut || formData.heure_debut,
        mecanicien_id: t.mecanicien_id || formData.mecanicien_id,
        duree: parseFloat(t.duree) || 1,
        label: `${headerClientName || formData.immatriculation || 'Ce dossier'} · ${t.libelle || 'travail'}`,
      })),
  ];
  const openOrEdit = () => {
    if (!peut('dossier.numero_or')) return;   // le serveur refuse aussi le PATCH
    setOrBackup(formData.numero_or || ''); setOrEditing(true);
  };
  const orKeys = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); setOrEditing(false); }
    if (e.key === 'Escape') {
      e.stopPropagation();
      setFormData(prev => ({ ...prev, numero_or: orBackup }));
      setOrEditing(false);
    }
  };
  // Cravate : même contrat que l'OR — clic pour ouvrir, Entrée valide,
  // Échap restaure la valeur d'avant la saisie.
  // La teinte suit la couleur enregistrée ; une cravate d'avant le choix des
  // couleurs (colonne vide) reste ambre, elle ne change donc pas d'aspect.
  const teinte = teinteCravate(formData.cravate_couleur);
  const openCravateEdit = () => {
    setCravateBackup({ num: formData.numero_cravate || '', couleur: formData.cravate_couleur || '' });
    setCravateEditing(true);
  };
  const cravateKeys = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); setCravateEditing(false); }
    if (e.key === 'Escape') {
      e.stopPropagation();
      // Échap restaure le numéro ET la couleur : la teinte se change pendant la
      // même saisie, elle doit s'annuler avec elle.
      setFormData(prev => ({
        ...prev,
        numero_cravate: cravateBackup?.num ?? '',
        cravate_couleur: cravateBackup?.couleur ?? '',
      }));
      setCravateEditing(false);
    }
  };

  const toggleNextlaneSearch = () => (showNextlaneSearch ? setShowNextlaneSearch(false) : openNextlaneSearch());

  // Prérequis métier du push Datacar (exigence utilisateur) : client, véhicule,
  // heure de réception ET heure d'intervention. L'association Datacar elle-même
  // est vérifiée dans handlePushToNextlane (message dédié si absente).
  const nextlaneMissing = [
    !hasHeaderClient && 'le client',
    !hasHeaderVehicle && 'le véhicule',
    !formData.heure_reception && "l'heure de réception",
    !formData.heure_debut && "l'heure d'intervention",
  ].filter(Boolean);
  const nextlaneReady = nextlaneMissing.length === 0;

  // ─── Nextlane search helpers ─────────────────────────────────────────────
  // Concession effective du dossier : celle de l'intervention (ou de la demande
  // en ligne), sinon la concession globale sélectionnée, sinon celle du compte.
  const effectiveConcessionId =
    confirmOnlineRdv?.concession_id
    || intervention?.concession_id
    || Number(localStorage.getItem('crm-global-concession'))
    || user?.concession_id
    || null;

  const openNextlaneSearch = () => {
    // La liste est rechargée à CHAQUE ouverture, scopée sur la concession du
    // dossier : la recherche d'un dossier BLUE ne propose que le DMS BLUE
    // (« aucune possibilité de se tromper »). Bénéfice collatéral : un seul
    // appel au lieu d'un éventail sur les 8 DMS — résultats immédiats.
    nextlaneApi.getConcessions(effectiveConcessionId)
      .then(r => {
        const list = r.data || [];
        setNlConcessions(list);
        setNlConcession(list.length === 1 ? list[0].id : 'all');
      })
      .catch(() => {});
    setShowNextlaneSearch(true);
    setNlQuery('');
    setNlResults([]);
  };

  // Recherche LIVE en éventail : une requête par concession, rendu progressif
  // (chaque concession s'affiche dès qu'elle répond), annulation des requêtes
  // précédentes dès que le texte change.
  const doNlSearch = (q, concessionId, mode) => {
    const query = (q || '').trim();
    if (!nlAssezLong(query, mode)) {
      if (nlAbortRef.current) nlAbortRef.current.abort();
      setNlResults([]); setNlLoading(false);
      return;
    }
    if (nlAbortRef.current) nlAbortRef.current.abort();
    const controller = new AbortController();
    nlAbortRef.current = controller;
    const seq = ++nlSeqRef.current;

    const targets = (concessionId && concessionId !== 'all')
      ? nlConcessions.filter(c => c.id === concessionId)
      : nlConcessions;
    if (targets.length === 0) return;

    setNlResults([]);
    setNlLoading(true);
    let pending = targets.length;

    targets.forEach(async (c) => {
      try {
        const { data } = await nextlaneApi.search(query, mode, c.id, controller.signal);
        if (seq !== nlSeqRef.current) return; // recherche obsolète
        const group = (data.results || [])[0];
        if (group && group.matches?.length) {
          setNlResults(prev => [
            ...prev.filter(g => g.dealership?.id !== c.id),
            group,
          ].sort((a, b) => (a.dealership?.name || '').localeCompare(b.dealership?.name || '')));
        }
      } catch { /* abort ou erreur réseau — on ignore */ }
      finally {
        if (seq === nlSeqRef.current) {
          pending -= 1;
          if (pending <= 0) setNlLoading(false);
        }
      }
    });
  };

  const handleNlQueryChange = (val) => {
    setNlQuery(val);
    if (nlDebounceRef.current) clearTimeout(nlDebounceRef.current);
    nlDebounceRef.current = setTimeout(() => doNlSearch(val, nlConcession, nlMode), 350);
  };

  const handleNlConcessionChange = (val) => {
    setNlConcession(val);
    if (nlAssezLong(nlQuery, nlMode)) doNlSearch(nlQuery, val, nlMode);
  };

  const handleNlModeChange = (mode) => {
    setNlMode(mode);
    setNlResults([]);
    if (nlAssezLong(nlQuery, mode)) doNlSearch(nlQuery, nlConcession, mode);
  };

  const applyNlMatch = (client, vehicle, dealership) => {
    setFormData(prev => ({
      ...prev,
      client_nom: (client?.LastName || '').toUpperCase().trim(),
      client_prenom: (client?.FirstName || '').toUpperCase().trim(),
      client_tel: client?.Phone || prev.client_tel,
      client_tel2: client?.Phone2 || prev.client_tel2,
      client_email: client?.Email || prev.client_email,
      immatriculation: (vehicle?.RegistrationNumber || '').toUpperCase().replace(/[\s\-_]/g, ''),
      marque: (vehicle?.BrandDescr || '').toUpperCase().trim(),
      modele: (vehicle?.ModelDescr || '').replace(/^\*\s?/, '').toUpperCase().trim(),
      vin: (vehicle?.VIN || '').toUpperCase().trim(),
      nextlane_client_id: client?.InternalClientID || '',
      nextlane_veh_id: vehicle?.InternalVehID || '',
      nextlane_dealership_id: dealership?.id || '',
    }));
    setShowNextlaneSearch(false);
  };

  // ─── Push vers Nextlane ──────────────────────────────────────────────────
  const handlePushToNextlane = async () => {
    if (formData.nextlane_appointment_id) {
      if (!window.confirm('Ce RDV a déjà été envoyé vers Nextlane. Voulez-vous le renvoyer ?')) return;
    }
    if (!formData.nextlane_client_id || !formData.nextlane_veh_id) {
      setPushResult({ type: 'error', message: 'Associez d\'abord le client et le véhicule via la recherche Datacar (loupe en haut du modal).' });
      setTimeout(() => setPushResult(null), 8000);
      return;
    }
    // En création, le bouton « Envoyer » est hors <form> → il faut valider
    // manuellement les champs requis du RDV (sinon la création échoue côté base).
    if (!editingId?.interventionId) {
      const manque = [];
      if (!formData.poste_reception) manque.push('réceptionnaire');
      if (!(formData.date_reception || formData.date_intervention)) manque.push('date');
      if (!formData.heure_reception) manque.push('heure de réception');
      if (manque.length > 0) {
        setPushResult({ type: 'error', message: `Renseignez ${manque.join(', ')} avant d'envoyer le RDV.` });
        setTimeout(() => setPushResult(null), 8000);
        return;
      }
    }
    setPushingToNextlane(true);
    setPushResult(null);
    try {
      // Le push lit la ligne en base : il faut une intervention enregistrée avec
      // les identifiants Datacar (+ date/travaux). On enregistre d'abord —
      // PUT si elle existe, sinon on la CRÉE (avec son RDV) et on récupère l'id.
      let interventionId = editingId?.interventionId;
      if (interventionId) {
        await api.put(`/planning/interventions/${interventionId}`, buildInterventionPayload());
      } else {
        const { data: created } = await api.post('/planning/interventions/with-rdv', {
          intervention: buildInterventionPayload(),
          rdv: buildRdvPayload(),
        });
        interventionId = created?.intervention?.id;
        if (!interventionId) throw new Error('Création de l\'intervention impossible');
        // Bascule en mode édition + rafraîchit le planning derrière la modale
        setEditingId({ interventionId, receptionId: created?.rdv?.id || null });
        // Le dossier existe : son onglet Atelier peut s'ouvrir (cf. `ongletAtelier`).
        setOngletAtelier(created?.intervention?.atelier_onglet_enabled === true);
        hasSavedRef.current = true;
        // Chemin Datacar : pas de récapitulatif, la proposition s'ouvre seule.
        if (onSaved) onSaved({
          creation: true,
          interventionId,
          clientNom: [formData.client_prenom, formData.client_nom].filter(Boolean).join(' ').trim(),
          avecRecap: false,
        });
      }
      const { data } = await nextlaneApi.pushAppointment(interventionId);
      if (data.success) {
        setFormData(prev => ({
          ...prev,
          nextlane_appointment_id: data.appointmentId,
          nextlane_pushed_at: new Date().toISOString(),
        }));
        setPushResult({ type: 'success', message: `RDV créé dans Nextlane (ID: ${data.appointmentId})` });
      } else {
        setPushResult({ type: 'error', message: data.error || 'Erreur inconnue' });
      }
    } catch (err) {
      const msg = err.response?.data?.error || err.message;
      setPushResult({ type: 'error', message: msg });
    } finally {
      setPushingToNextlane(false);
      setTimeout(() => setPushResult(null), 8000);
    }
  };

  // Helper : date relative
  const timeAgo = (dateStr) => {
    const d = new Date(dateStr);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return "à l'instant";
    if (diff < 3600) return `il y a ${Math.floor(diff / 60)} min`;
    if (diff < 86400) return `il y a ${Math.floor(diff / 3600)}h`;
    return d.toLocaleDateString('fr-FR');
  };

  // Sidebar nav items
  // Icones sidebar (SVG inline, stroke only, 16x16)
  // Pictos en EMOJI couleur (2026-08-15, maquette validée) : les SVG
  // monochromes étaient l'exception dans un CRM entièrement aux emojis
  // (onglets, menus — cf. conventions du design system). La couleur aide
  // le repérage ; l'item actif reste marqué par le filet et l'accent.

  // ═══ Échanges clients ═══════════════════════════════════════════════════
  // Le geste : un clic sur un canal crée la ligne, datée de maintenant, et
  // ouvre le champ. Pas de formulaire, pas de bouton « Enregistrer ».
  const ouvrirBrouillon = (canal, sens) => {
    if (!editingId?.interventionId || lectureSeule) return;
    setBrouillonEchange({ canal, sens, quand: new Date().toISOString(), resume: '', busy: false });
  };

  const enregistrerBrouillon = async () => {
    if (!brouillonEchange || brouillonEchange.busy) return;
    setBrouillonEchange(b => ({ ...b, busy: true }));
    try {
      const r = await planningEchangesApi.create(editingId.interventionId, {
        canal: brouillonEchange.canal,
        sens: brouillonEchange.sens,
        contact_le: brouillonEchange.quand,
        resume: brouillonEchange.resume,
      });
      setEchanges(prev => [r.data.echange, ...prev]);
      setBrouillonEchange(null);
    } catch (err) {
      console.error('[echanges/creation]', err);
      setBrouillonEchange(b => (b ? { ...b, busy: false } : b));
      window.alert("Impossible d'enregistrer cet échange.");
    }
  };

  // Enregistrement à la SORTIE du champ (ou Ctrl+Entrée) — jamais pendant la
  // frappe. Une sauvegarde différée en cours de saisie soumet des valeurs
  // intermédiaires que personne n'a validées.
  const majEchange = async (id, champs) => {
    const avant = echanges.find(e => e.id === id);
    if (!avant) return;
    const inchange = Object.entries(champs).every(([k, v]) => String(avant[k] ?? '') === String(v ?? ''));
    if (inchange) return;
    setEchanges(prev => prev.map(e => (e.id === id ? { ...e, ...champs } : e)));
    try {
      const r = await planningEchangesApi.update(id, champs);
      setEchanges(prev => prev.map(e => (e.id === id ? { ...e, ...r.data.echange } : e)));
    } catch (err) {
      console.error('[echanges/maj]', err);
      setEchanges(prev => prev.map(e => (e.id === id ? avant : e)));   // on remet l'état d'avant
      window.alert('Modification non enregistrée.');
    }
  };

  // Pièce jointe : le fichier va sur S3 et reste un document DU DOSSIER, avec
  // en plus le rattachement à l'échange. Il apparaît donc dans les deux vues.
  const joindreAEchange = async (echangeId, files) => {
    for (const file of Array.from(files || [])) {
      try {
        const { data: pre } = await planningDocsApi.presign(editingId.interventionId, {
          filename: file.name, contentType: file.type || 'application/octet-stream',
        });
        await fetch(pre.uploadUrl, { method: 'PUT', body: file, headers: { 'Content-Type': file.type || 'application/octet-stream' } });
        const { data: conf } = await planningDocsApi.confirm(editingId.interventionId, {
          key: pre.key, filename: file.name, contentType: file.type, size: file.size, echange_id: echangeId,
        });
        setEchanges(prev => prev.map(e => (e.id === echangeId
          ? { ...e, documents: [...(e.documents || []), conf.document], nb_documents: (e.nb_documents || 0) + 1 }
          : e)));
        setPlanningDocs(prev => [...prev, conf.document]);
      } catch (err) {
        console.error('[echanges/piece-jointe]', err);
        window.alert(`Pièce jointe impossible : ${file.name}`);
      }
    }
  };

  const dernierContact = echanges.length ? echanges[0].contact_le : null;

  const sidebarIcons = {
    intervention: <span className="im-sidebar-emoji">🔧</span>,
    atelier: <span className="im-sidebar-emoji">🔧</span>,
    pieces: <span className="im-sidebar-emoji">⚙️</span>,
    vehicule: <span className="im-sidebar-emoji">🚗</span>,
    courtoisie: <span className="im-sidebar-emoji">🤝</span>,
    signature: <span className="im-sidebar-emoji">✍️</span>,
    photos: <span className="im-sidebar-emoji">📷</span>,
    documents: <span className="im-sidebar-emoji">📎</span>,
    echanges: <span className="im-sidebar-emoji">💬</span>,
    'analyse-ia': <span className="im-sidebar-emoji">🤖</span>,
  };

  // Analyse IA : super-admin seulement, drapeau global activé, et dossier déjà
  // enregistré (le serveur relit tout le dossier par son id). Double garde,
  // comme JDMAPage : la route renvoie 403 de toute façon.
  const analyseIaVisible = user?.role === 'super-admin' && analyseIaEnabled && !!editingId?.interventionId;

  // ═══ Répartition sur plusieurs jours ═════════════════════════════════════
  // Un dossier plus long que la journée de son atelier se découpe, SUR DEMANDE
  // (bouton « Répartir », décision du 30/09), en journées sur le même
  // mécanicien : le 1er jour jusqu'à la fermeture, puis des journées pleines,
  // le reliquat au dernier. Chaque jour suivant devient une ligne de travaux
  // planifiée à part (`repartition: true`) — le mécanisme existant des
  // planifications complémentaires, synchronisé à l'enregistrement. Rien ne
  // part en base avant « Enregistrer ».
  const estLigneRepartition = (t) => !!t && t.repartition === true;
  const lignesRepartition = (formData.travaux || []).filter(estLigneRepartition);
  const horairesMain = (horairesDuMeca && formData.mecanicien_id) ? horairesDuMeca(formData.mecanicien_id) : null;
  const minutesDe = (hhmm) => {
    const m = /^(\d{1,2}):(\d{2})/.exec(hhmm || '');
    return m ? parseInt(m[1], 10) * 60 + parseInt(m[2], 10) : null;
  };
  const depasseJournee = !!horairesMain && minutesDe(formData.heure_debut) !== null
    && minutesDe(formData.heure_debut) + Math.round((parseFloat(formData.duree) || 0) * 60) > minutesDe(horairesMain.fermeture);
  const repartitionProposee = !!calculerRepartition && !lectureSeule && !!formData.mecanicien_id
    && !!formData.date_intervention && !!formData.heure_debut
    && (depasseJournee || lignesRepartition.length > 0);
  const dureeARepartir = Math.round(((parseFloat(formData.duree) || 0)
    + lignesRepartition.reduce((n, t) => n + (parseFloat(t.duree) || 0), 0)) * 100) / 100;

  const preparerRepartition = async () => {
    setRepartition({ busy: true });
    try {
      const r = await calculerRepartition({
        date: formData.date_intervention, heure: formData.heure_debut, duree: dureeARepartir,
        mecanicienId: formData.mecanicien_id, concessionId: formData.concession_id,
      });
      setRepartition({ total: dureeARepartir, segments: r.segments, reste: r.reste });
    } catch (e) {
      console.error('[repartition]', e);
      setRepartition({ erreur: 'Calcul impossible' });
    }
  };

  const appliquerRepartition = () => {
    const { segments } = repartition || {};
    if (!segments?.length) return;
    const n = segments.length;
    const meca = mecasSelection.find(m => m.id === formData.mecanicien_id);
    setFormData(prev => {
      // Une nouvelle répartition remplace la précédente, jamais ne s'y ajoute.
      const autres = (prev.travaux || []).filter(t => !estLigneRepartition(t));
      // La 1re ligne de travaux porte la durée du dossier (la modifier la
      // recopie dans `duree`) : elle suit le 1er jour, sinon le TOTAL des
      // travaux compterait deux fois les heures réparties.
      if (autres.length && Math.abs((parseFloat(autres[0].duree) || 0) - (parseFloat(prev.duree) || 0)) < 0.01) {
        autres[0] = { ...autres[0], duree: segments[0].duree };
      }
      const suites = segments.slice(1).map((sg, i) => ({
        travaux_type_id: null,
        libelle: `Suite — jour ${i + 2}/${n}`,
        duree: sg.duree,
        date_intervention: sg.date,
        heure_debut: sg.heure,
        mecanicien_id: prev.mecanicien_id,
        mecanicien: meca ? `${meca.prenom || ''} ${meca.nom || ''}`.trim() : (prev.mecanicien || null),
        repartition: true,
      }));
      return { ...prev, duree: segments[0].duree, travaux: [...autres, ...suites] };
    });
    setRepartition(null);
  };

  // Onglet affiché dont une zone s'écrit avec un droit propre, même en
  // consultation (voir le <fieldset> du corps de la modale).
  const ongletAZoneLibre = (modalTab === 'atelier' && peut('dossier.compte_rendu'))
    || (modalTab === 'intervention' && !!editingId && peut('dossier.commentaire'));

  const sidebarItems = [
    { key: 'intervention', label: 'Intervention', count: formData.travaux.length || null, section: 'dossier' },
    // Opt-in de la concession DU DOSSIER, et seulement sur un dossier existant :
    // en création il n'y a pas encore d'id à qui adresser le compte rendu.
    ...(ongletAtelier && editingId && peut('dossier.compte_rendu')
      ? [{ key: 'atelier', label: 'Atelier', count: compteRendu.trim() ? 1 : null, section: 'dossier' }]
      : []),
    ...(peut('pieces.carnet')
      ? [{ key: 'pieces', label: 'Pièces', count: carnetLignes.length || null, dot: computedStatutPieces === 'en_commande' ? '#f59e0b' : null, section: 'dossier' }]
      : []),
    { key: 'vehicule', label: 'Véhicule', section: 'dossier' },
    { key: 'courtoisie', label: 'Courtoisie', count: pretCourtoisie ? 1 : null, section: 'dossier' },
    { key: 'signature', label: 'Signature', count: esignList.length || null, section: 'dossier' },
    // Sans `client.echanges_voir` : ni l'onglet, ni la pastille.
    ...(peut('client.echanges_voir') ? [{ key: 'echanges', label: 'Échanges clients', count: echanges.length || null, section: 'dossier' }] : []),
    ...(analyseIaVisible ? [{ key: 'analyse-ia', label: 'Analyse IA 🔒', section: 'dossier' }] : []),
    { key: 'photos', label: 'Photos', count: atelierPhotos.length || null, section: 'pj' },
    { key: 'documents', label: 'Documents', count: planningDocs.length || null, section: 'pj' },
  ];

  // Statut atelier courant
  const currentStatut = statutsAtelier.find(s => s.id === formData.statut_atelier_id);
  const piecesLabel = { disponible: 'Disponible', en_commande: 'En attente', indisponible: 'Indisponible' };

  return (
    <div className="im-overlay" onClick={handleCloseModal}>
      <div className="im-shell" onClick={(e) => e.stopPropagation()}>
        <form ref={formRef} onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>

          {/* ── Header (pleine largeur) ── */}
          <div className="im-header">
            <div className="im-idb">
              <div className="im-idb-line">
                <div className="im-idb-main">
                  {isConfirmMode && <span className="im-idb-chip">📥 Demande en ligne — à confirmer</span>}
                  {editingHeaderCard === 'client' ? (
                    <span className="im-idb-dim">Modification du client…</span>
                  ) : editingHeaderCard === 'vehicle' ? (
                    <span className="im-idb-dim">Modification du véhicule…</span>
                  ) : editingHeaderCard === 'conducteur' ? (
                    <span className="im-idb-dim">Saisie du conducteur…</span>
                  ) : (!hasHeaderClient && !hasHeaderVehicle) ? (
                    <>
                      <span className="im-idb-label">Nouveau dossier —</span>
                      <button type="button" className="im-idb-btn im-idb-btn--datacar" onClick={toggleNextlaneSearch}>
                        🔍 Rechercher le client Datacar
                      </button>
                      <span className="im-idb-label">ou</span>
                      <button type="button" className="im-idb-btn" onClick={() => openHeaderEdit('client')}>Saisie manuelle</button>
                    </>
                  ) : (
                    <div className="im-idb-rows">
                      <div className="im-idb-row">
                        <span className="im-idb-rowlabel">Client</span>
                        <span className="im-idb-zone">
                          {hasHeaderClient ? (
                            <>
                              <span className="im-idb-strong im-idb-trunc" title={headerClientName}>{headerClientName}</span>
                              {formData.client_tel && (
                                <>
                                  <span className="im-idb-sep">·</span>
                                  <span>{formData.client_tel}</span>
                                </>
                              )}
                              {formData.client_tel2 && (
                                <>
                                  <span className="im-idb-sep">·</span>
                                  <span>{formData.client_tel2}</span>
                                </>
                              )}
                              {formData.client_email && (
                                <>
                                  <span className="im-idb-sep">·</span>
                                  <span className="im-idb-trunc" title={formData.client_email}>{formData.client_email}</span>
                                </>
                              )}
                            </>
                          ) : (
                            <span className="im-idb-dim">Client à renseigner</span>
                          )}
                          <button type="button" className="im-idb-pen" onClick={() => openHeaderEdit('client')} title="Modifier le client" aria-label="Modifier le client">✎</button>
                        </span>
                        {/* Les drapeaux contextuels (litige, parking, courtoisie,
                            ROOLE) ne vivent plus ici : accrochés en bout de ligne
                            CLIENT, ils passaient à la ligne dès que la place
                            manquait et coupaient l'identité en deux (constaté sur
                            un dossier parking + ROOLE + cravate). Ils ont rejoint
                            la barre d'état, à droite — sombre = identité, clair =
                            état (2026-08-15, maquette validée). */}
                      </div>
                      <div className="im-idb-row">
                        <span className="im-idb-rowlabel">Véhicule</span>
                        <span className="im-idb-zone">
                          {hasHeaderVehicle ? (
                            <>
                              <span className="im-idb-strong">{formData.immatriculation}</span>
                              {/* VIN : il n'existait qu'en SAISIE, dans le panneau
                                  d'édition — jamais en lecture. Il se lit souvent
                                  (garantie, commande de pièces, DMS), d'où sa place
                                  ici. En chasse fixe et discret : c'est une
                                  référence à recopier, pas une information à lire.
                                  Rien n'est affiché s'il est vide, pour ne pas
                                  meubler le bandeau d'un tiret. */}
                              {formData.vin && (
                                <>
                                  <span className="im-idb-sep">·</span>
                                  <span className="im-idb-vin" title={`VIN ${formData.vin}`}>{formData.vin}</span>
                                </>
                              )}
                              {headerVehLabel && (
                                <>
                                  <span className="im-idb-sep">·</span>
                                  <span className="im-idb-trunc" title={headerVehLabel}>{headerVehLabel}</span>
                                </>
                              )}
                              {formData.kilometrage && (
                                <>
                                  <span className="im-idb-sep">·</span>
                                  <span>{Number(formData.kilometrage).toLocaleString('fr-FR')} km</span>
                                </>
                              )}
                            </>
                          ) : (
                            <span className="im-idb-dim">Véhicule à renseigner</span>
                          )}
                          <button type="button" className="im-idb-pen" onClick={() => openHeaderEdit('vehicle')} title="Modifier le véhicule" aria-label="Modifier le véhicule">✎</button>
                        </span>
                        {!hasConducteur && (
                          <button type="button" className="im-idb-addbtn" onClick={() => openHeaderEdit('conducteur')}
                            title="Ajouter un conducteur différent du client (flottes, clients professionnels)">
                            + Conducteur
                          </button>
                        )}
                      </div>
                      {hasConducteur && (
                        <div className="im-idb-row">
                          <span className="im-idb-rowlabel">Conducteur</span>
                          <span className="im-idb-zone">
                            {headerConducteurName && (
                              <span className="im-idb-strong im-idb-trunc" title={headerConducteurName}>{headerConducteurName}</span>
                            )}
                            {formData.conducteur_tel && (
                              <>
                                {headerConducteurName && <span className="im-idb-sep">·</span>}
                                <span>{formData.conducteur_tel}</span>
                              </>
                            )}
                            {formData.conducteur_email && (
                              <>
                                {(headerConducteurName || formData.conducteur_tel) && <span className="im-idb-sep">·</span>}
                                <span className="im-idb-trunc" title={formData.conducteur_email}>{formData.conducteur_email}</span>
                              </>
                            )}
                            <button type="button" className="im-idb-pen" onClick={() => openHeaderEdit('conducteur')} title="Modifier le conducteur" aria-label="Modifier le conducteur">✎</button>
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div className="im-idb-right">
                  <div className="im-idb-right-top">
                  {orEditing ? (
                    <input
                      className="im-idb-or-input"
                      autoFocus
                      value={formData.numero_or}
                      placeholder="N° OR"
                      onChange={(e) => setFormData({ ...formData, numero_or: e.target.value.toUpperCase() })}
                      onKeyDown={orKeys}
                      onBlur={() => setOrEditing(false)}
                    />
                  ) : formData.numero_or ? (
                    <span className="im-idb-strong">OR {formData.numero_or}</span>
                  ) : (
                    <span className="im-idb-dim">OR à préparer</span>
                  )}
                  {!orEditing && (
                    <button type="button" className="im-idb-pen" onClick={openOrEdit} title={formData.numero_or ? 'Modifier le N° OR' : 'Saisir le N° OR'} aria-label="Modifier le N° OR">✎</button>
                  )}
                  {/* Lien avec le DMS : remplace le badge « NEXTLANE ✓ Envoyé »
                      de la barre de statuts, que personne ne comprenait. Posé
                      sur le n° d'OR car c'est de LUI qu'il parle : ce dossier
                      est relié à un RDV Datacar. L'infobulle explique, et le
                      renvoi passe par une CONFIRMATION — l'ancien badge
                      renvoyait au clic sec, or renvoyer crée un SECOND RDV
                      dans le DMS de production. */}
                  {formData.nextlane_appointment_id && (
                    <button
                      type="button"
                      className="im-idb-datacar"
                      title={`Relié au RDV Datacar n° ${formData.nextlane_appointment_id}${formData.nextlane_pushed_at ? `, envoyé le ${new Date(formData.nextlane_pushed_at).toLocaleDateString('fr-FR')}` : ''}. Cliquer pour renvoyer.`}
                      onClick={() => {
                        if (window.confirm('Ce dossier est déjà relié à un RDV Datacar.\nRenvoyer créera un SECOND rendez-vous dans le DMS.\n\nRenvoyer quand même ?')) {
                          handlePushToNextlane();
                        }
                      }}
                    >
                      ⇄ Datacar
                    </button>
                  )}
                  <button type="button" className="im-idb-close" onClick={handleCloseModal} title="Fermer (Échap)" aria-label="Fermer">✕</button>
                  </div>
                  {/* La cravate se range sous l'OR, contre le bord droit du
                      bandeau : la colonne de droite porte les deux repères qu'on
                      cherche du regard, et l'un est toujours sous l'autre. */}
                  <div className="im-idb-slot">
                    {cravateEditing ? (
                      <span className="im-cravate-edition">
                        <span className="im-cravate im-cravate--editing" style={{ background: teinte.fond, color: teinte.texte }}>
                          <span className="im-cravate-hole" />
                          <input
                            className="im-cravate-input"
                            autoFocus
                            maxLength={20}
                            placeholder="N°"
                            value={formData.numero_cravate || ''}
                            onChange={(e) => setFormData({ ...formData, numero_cravate: e.target.value.toUpperCase() })}
                            onKeyDown={cravateKeys}
                            onBlur={() => setCravateEditing(false)}
                          />
                        </span>
                        {/* ⚠️ `onMouseDown` + `preventDefault` : sans lui, le clic sur
                            une pastille fait perdre le focus au champ, dont le `onBlur`
                            referme la saisie AVANT que le clic ne soit traité. */}
                        <span className="im-cravate-palette">
                          {COULEURS_CRAVATE.map(c => (
                            <button
                              key={c.fond}
                              type="button"
                              className={`im-cravate-teinte${teinte.fond === c.fond ? ' is-active' : ''}`}
                              style={{ background: c.fond }}
                              title={c.nom}
                              aria-label={`Cravate ${c.nom.toLowerCase()}`}
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => setFormData({ ...formData, cravate_couleur: c.fond })}
                            />
                          ))}
                        </span>
                      </span>
                    ) : formData.numero_cravate ? (
                      <button type="button" className="im-cravate" onClick={openCravateEdit}
                        style={{ background: teinte.fond, color: teinte.texte }}
                        title={`Modifier le numéro de cravate (${teinte.nom.toLowerCase()})`}>
                        <span className="im-cravate-hole" />
                        <span className="im-cravate-label">Cravate</span>
                        <span className="im-cravate-num">{formData.numero_cravate}</span>
                      </button>
                    ) : (
                      <button type="button" className="im-cravate im-cravate--empty" onClick={openCravateEdit}
                        style={{ background: teinte.fond, color: teinte.texte }}
                        title="Saisir le numéro de cravate">
                        <span className="im-cravate-hole" />
                        <span className="im-cravate-label">Cravate</span>
                        <span className="im-cravate-vide">+</span>
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {editingHeaderCard === 'client' && (
                <div className="im-idb-edit" onKeyDown={headerEditKeys}>
                  <input className="im-idb-edit-input" placeholder="Prénom" autoFocus value={formData.client_prenom || ''}
                    onChange={(e) => setFormData({ ...formData, client_prenom: e.target.value.toUpperCase() })} />
                  <input className="im-idb-edit-input" placeholder="Nom" value={formData.client_nom || ''}
                    onChange={(e) => setFormData({ ...formData, client_nom: e.target.value.toUpperCase() })} />
                  <input className="im-idb-edit-input" type="tel" placeholder="Téléphone" value={formData.client_tel || ''}
                    onChange={(e) => setFormData({ ...formData, client_tel: e.target.value })} />
                  <input className="im-idb-edit-input" type="tel" placeholder="Téléphone 2" value={formData.client_tel2 || ''}
                    onChange={(e) => setFormData({ ...formData, client_tel2: e.target.value })} />
                  <input className="im-idb-edit-input" type="email" placeholder="Email" value={formData.client_email || ''}
                    onChange={(e) => setFormData({ ...formData, client_email: e.target.value })} />
                  <button type="button" className="im-idb-btn im-idb-btn--datacar" onClick={toggleNextlaneSearch}>🔍 Remplacer via Datacar</button>
                  <button type="button" className="im-idb-btn" onClick={cancelHeaderEdit}>Annuler</button>
                  <button type="button" className="im-idb-btn im-idb-btn--primary" onClick={validateHeaderEdit}>Valider</button>
                </div>
              )}
              {editingHeaderCard === 'vehicle' && (
                <div className="im-idb-edit" onKeyDown={headerEditKeys}>
                  <input className="im-idb-edit-input" placeholder="Immatriculation" autoFocus value={formData.immatriculation || ''}
                    onChange={(e) => setFormData({ ...formData, immatriculation: e.target.value.toUpperCase().replace(/[\s\-_]/g, '') })} />
                  <input className="im-idb-edit-input" placeholder="Marque" value={formData.marque || ''}
                    onChange={(e) => setFormData({ ...formData, marque: e.target.value.toUpperCase() })} />
                  <input className="im-idb-edit-input" placeholder="Modèle" value={formData.modele || ''}
                    onChange={(e) => setFormData({ ...formData, modele: e.target.value.toUpperCase() })} />
                  <input className="im-idb-edit-input" placeholder="VIN" maxLength={17} value={formData.vin || ''}
                    onChange={(e) => setFormData({ ...formData, vin: e.target.value.toUpperCase() })} />
                  <input className="im-idb-edit-input" type="number" min="0" placeholder="Kilométrage" value={formData.kilometrage ?? ''}
                    onChange={(e) => setFormData({ ...formData, kilometrage: e.target.value })} />
                  {/* Le N° de cravate est sorti d'ici : il se saisit sur son
                      emplacement dédié, en bout de ligne Véhicule. */}
                  <button type="button" className="im-idb-btn" onClick={cancelHeaderEdit}>Annuler</button>
                  <button type="button" className="im-idb-btn im-idb-btn--primary" onClick={validateHeaderEdit}>Valider</button>
                </div>
              )}
              {editingHeaderCard === 'conducteur' && (
                <div className="im-idb-edit" onKeyDown={headerEditKeys}>
                  <input className="im-idb-edit-input" placeholder="Prénom" autoFocus value={formData.conducteur_prenom || ''}
                    onChange={(e) => setFormData({ ...formData, conducteur_prenom: e.target.value.toUpperCase() })} />
                  <input className="im-idb-edit-input" placeholder="Nom" value={formData.conducteur_nom || ''}
                    onChange={(e) => setFormData({ ...formData, conducteur_nom: e.target.value.toUpperCase() })} />
                  <input className="im-idb-edit-input" type="tel" placeholder="Téléphone" value={formData.conducteur_tel || ''}
                    onChange={(e) => setFormData({ ...formData, conducteur_tel: e.target.value })} />
                  <input className="im-idb-edit-input" type="email" placeholder="Email" value={formData.conducteur_email || ''}
                    onChange={(e) => setFormData({ ...formData, conducteur_email: e.target.value })} />
                  <button type="button" className="im-idb-btn" onClick={removeConducteur}>Retirer</button>
                  <button type="button" className="im-idb-btn" onClick={cancelHeaderEdit}>Annuler</button>
                  <button type="button" className="im-idb-btn im-idb-btn--primary" onClick={validateHeaderEdit}>Valider</button>
                </div>
              )}
            </div>
          </div>

            {/* ── Panneau recherche Nextlane / Datacar ── */}
            {showNextlaneSearch && (
              <div className="nl-search-panel">
                <div className="nl-search-header">
                  <div className="nl-mode-toggle">
                    <button type="button" className={`nl-mode-btn ${nlMode === 'immat' ? 'active' : ''}`} onClick={() => handleNlModeChange('immat')}>Immat</button>
                    <button type="button" className={`nl-mode-btn ${nlMode === 'nom' ? 'active' : ''}`} onClick={() => handleNlModeChange('nom')}>Nom</button>
                    <button type="button" className={`nl-mode-btn ${nlMode === 'tel' ? 'active' : ''}`} onClick={() => handleNlModeChange('tel')}>Téléphone</button>
                  </div>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                  <input
                    className="nl-search-input"
                    placeholder={
                      nlMode === 'immat' ? 'Rechercher par immatriculation...'
                        : nlMode === 'tel' ? 'Rechercher par téléphone (06…, ou les derniers chiffres)'
                          : 'Rechercher par nom client...'
                    }
                    value={nlQuery}
                    onChange={(e) => handleNlQueryChange(e.target.value)}
                    autoFocus
                  />
                  <select
                    className="nl-search-select"
                    value={nlConcession}
                    onChange={(e) => handleNlConcessionChange(e.target.value)}
                  >
                    {/* Un seul DMS dans le périmètre (ex. dossier BLUE) : pas d'option
                        « Toutes », le select est verrouillé de fait sur ce DMS. */}
                    {nlConcessions.length > 1 && <option value="all">Toutes concessions</option>}
                    {nlConcessions.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                  <button type="button" className="nl-search-close" onClick={() => setShowNextlaneSearch(false)}>✕</button>
                </div>

                <div className="nl-search-body">
                  {nlQuery.trim().length > 0 && !nlAssezLong(nlQuery, nlMode) && (
                    <div className="nl-search-msg">
                      {nlMode === 'tel' ? 'Saisissez au moins 4 chiffres…' : 'Saisissez au moins 3 caractères…'}
                    </div>
                  )}

                  {/* Rendu progressif : chaque concession s'affiche dès qu'elle répond */}
                  {nlResults.map(group => {
                    if (!group.matches?.length) return null;
                    return (
                      <div key={group.dealership?.id} className="nl-search-group">
                        <div className="nl-search-group-label">{group.dealership?.name} ({group.matches.length})</div>
                        <table className="nl-search-table">
                          <thead>
                            <tr><th>Immat</th><th>Marque / Modèle</th><th>Km</th><th>Client</th><th>Tél</th><th></th></tr>
                          </thead>
                          <tbody>
                            {group.matches.map(({ vehicle, client }, idx) => (
                              <tr key={(vehicle?.InternalVehID || vehicle?.VIN || client?.InternalClientID || '') + '-' + idx} className="nl-search-row">
                                <td style={{ fontWeight: 600 }}>{vehicle?.RegistrationNumber || '—'}</td>
                                <td>{vehicle ? `${vehicle.BrandDescr || ''} ${(vehicle.ModelDescr || '').replace(/^\*\s?/, '')}` : '—'}</td>
                                <td>{vehicle?.MileageValue ? `${Number(vehicle.MileageValue).toLocaleString('fr-FR')} km` : '—'}</td>
                                <td>{client ? `${client.FirstName} ${client.LastName}` : <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
                                <td>
                                  {Array.isArray(client?.Phones) && client.Phones.length > 0
                                    ? client.Phones.map((p, i) => (
                                        <div key={i} style={{ whiteSpace: 'nowrap' }}>
                                          {p.value}
                                          {p.usage && <span style={{ color: 'var(--text-muted)', fontSize: 11 }}> {p.usage === 'Mobile' ? '(mobile)' : p.usage === 'Home' ? '(dom.)' : p.usage === 'Work' ? '(trav.)' : `(${p.usage})`}</span>}
                                        </div>
                                      ))
                                    : (client?.Phone || '—')}
                                </td>
                                <td>
                                  {client ? (
                                    <button type="button" className="nl-btn-use nl-btn-use-primary" onClick={() => applyNlMatch(client, vehicle, group.dealership)}>
                                      Utiliser
                                    </button>
                                  ) : (
                                    <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>Client inconnu</span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    );
                  })}

                  {nlLoading && <div className="nl-search-msg">🔄 Recherche en cours…</div>}

                  {!nlLoading && nlAssezLong(nlQuery, nlMode) && nlResults.every(g => !g.matches?.length) && (
                    <div className="nl-search-msg">Aucun résultat pour "{nlQuery}"</div>
                  )}
                </div>
              </div>
            )}

            {/* ── Barre de statuts ──
                Allégée le 2026-08-15 (demande utilisateur) : le badge RÉCEPTION
                est parti (les cartes du panneau portent déjà « arrivé depuis » /
                « en cours »), et l'état Nextlane est devenu une pastille sur le
                n° d'OR du bandeau. Ne restent ici que le statut atelier — le
                seul ÉDITABLE — les tags et l'état des pièces. */}
            <div className="im-status-bar">
              <div className="im-status-item">
                <span>ATELIER</span>
                {/* Menu maison plutôt que <select> : le rendu des <option>
                    colorées dépend du navigateur et de l'OS — ici chaque statut
                    est une vraie pastille à sa couleur, partout pareil. */}
                <div className="im-statut-wrap">
                  <button
                    type="button"
                    className="im-statut-courant"
                    disabled={lectureSeule}
                    style={currentStatut
                      ? { background: currentStatut.couleur, color: currentStatut.text_color || getContrastColor(currentStatut.couleur) }
                      : { background: 'var(--bg-surface)', color: 'var(--text-muted)', border: '1px dashed var(--border-strong)' }}
                    title="Changer le statut atelier du dossier"
                    onClick={() => setStatutMenuOpen(o => !o)}
                  >
                    {currentStatut ? currentStatut.nom : 'Choisir un statut'}
                    {statutDepuisTexte && (
                      <span className="im-statut-depuis"
                        title={statutDepuis?.estime
                          ? 'Date approchée : reprise de la dernière modification connue, avant l\'horodatage des statuts'
                          : 'Date de passage dans ce statut'}>
                        {statutDepuisTexte}
                      </span>
                    )}
                    <span className="im-statut-chevron">▾</span>
                  </button>
                  {statutMenuOpen && (
                    <>
                      <div className="im-print-backdrop" onClick={() => setStatutMenuOpen(false)} />
                      <div className="im-statut-menu">
                        <button type="button" className="im-statut-choix im-statut-choix--aucun"
                          onClick={() => choisirStatut(null)}>
                          Aucun statut
                        </button>
                        {statutsAtelier.map(s => (
                          <button key={s.id} type="button" className="im-statut-choix"
                            onClick={() => choisirStatut(s.id)}>
                            <span className="im-statut-pastille"
                              style={{ background: s.couleur, color: s.text_color || getContrastColor(s.couleur) }}>
                              {s.nom}
                            </span>
                            {formData.statut_atelier_id === s.id && <span className="im-statut-coche">✓</span>}
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </div>
              <div className="im-status-item">
                <span>PIÈCES</span>
                <span className="im-status-badge"
                  title="État calculé depuis le carnet de commande de l'OR (onglet Pièces)"
                  style={
                  formData.statut_pieces === 'en_commande' ? { background: '#fef3c7', color: '#92400e' }
                  : formData.statut_pieces === 'disponible' ? { background: '#dcfce7', color: '#166534' }
                  : formData.statut_pieces === 'indisponible' ? { background: '#fee2e2', color: '#991b1b' }
                  : { background: 'var(--bg-surface)', color: 'var(--text-muted)', border: '1px solid var(--border-default)' }
                }>
                  {formData.statut_pieces === 'disponible' ? 'Disponible'
                   : formData.statut_pieces === 'en_commande' ? 'En attente'
                   : formData.statut_pieces === 'indisponible' ? 'Indisponible'
                   /* « — » seul ne disait rien : l'absence de commande est une
                      information en soi, on l'écrit (2026-08-15). */
                   : 'Pas de pièces en commande'}
                </span>
                {/* [16] Prépicking, à droite du statut Pièces : même sujet, autre
                    métier. Bascule — un second clic annule, une erreur de clic
                    doit se corriger aussi vite qu'elle se commet. Dossier
                    enregistré seulement : il n'y a pas d'id avant. */}
                {editingId && !lectureSeule && (
                  <button type="button"
                    className={`im-prepicking${prepicking ? ' is-on' : ''}`}
                    disabled={prepickingBusy}
                    onClick={basculerPrepicking}
                    title={prepicking
                      ? `Pièces préparées le ${new Date(prepicking.at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}${prepicking.par_nom ? ` par ${prepicking.par_nom}` : ''} — cliquer pour annuler`
                      : 'Déclarer que les pièces sont préparées (prépicking)'}>
                    🛒 {prepicking ? 'Pièces préparées' : 'Prépicking'}
                  </button>
                )}
              </div>
              {/* Tags : gérés ICI depuis 2026-08-15 — le + ajoute, le ✕ retire,
                  mêmes effets immédiats que le clic droit du planning (endpoint
                  partagé). Dossiers enregistrés seulement : la pose écrit en
                  base tout de suite. */}
              {editingId && (
                <div className="im-status-item">
                  <span>TAGS</span>
                  <div className="im-tags-ligne">
                    {tagsDossier.map(t => (
                      <span key={t.id} className="im-tag-puce"
                        style={{ background: t.couleur, color: getContrastColor(t.couleur) }}>
                        {t.nom}
                        {!lectureSeule && (
                          <button type="button" className="im-tag-x" title={`Retirer « ${t.nom} »`}
                            onClick={() => toggleTagDossier(t, false)}>✕</button>
                        )}
                      </span>
                    ))}
                    {!lectureSeule && (
                      <div className="im-statut-wrap">
                        <button type="button" className="im-tag-add" title="Ajouter un tag" onClick={ouvrirTagMenu}>+</button>
                        {tagMenuOpen && (
                          <>
                            <div className="im-print-backdrop" onClick={() => setTagMenuOpen(false)} />
                            <div className="im-statut-menu">
                              {tagsDispo === null && <div className="im-print-menu-vide">Chargement…</div>}
                              {Array.isArray(tagsDispo) && tagsDispo.length === 0 && (
                                <div className="im-print-menu-vide">
                                  Aucun tag configuré.<br />
                                  <span style={{ fontSize: 11 }}>Configuration Planning › 🔖 Tags</span>
                                </div>
                              )}
                              {Array.isArray(tagsDispo) && tagsDispo.map(t => {
                                const pose = tagsDossier.some(x => x.id === t.id);
                                return (
                                  <button key={t.id} type="button" className="im-statut-choix"
                                    onClick={() => toggleTagDossier(t, !pose)}>
                                    <span className="im-statut-pastille"
                                      style={{ background: t.couleur, color: getContrastColor(t.couleur) }}>
                                      {t.nom}
                                    </span>
                                    {pose && <span className="im-statut-coche">✓</span>}
                                  </button>
                                );
                              })}
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
              <div className="im-status-spacer" />
              {/* Drapeaux contextuels, poussés à droite : ce qu'on CONSTATE
                  (parking, ROOLE, litige, courtoisie), face à ce qu'on PILOTE à
                  gauche. Ils viennent du bandeau, où ils coupaient l'identité. */}
              {/* Le « dernier contact » entre dans la condition : sans lui, la rangée
                  entière disparaissait sur un dossier sans litige ni parking ni
                  courtoisie — c'est-à-dire sur la majorité des dossiers. */}
              {(badgeLitige || badgeParking || parking?.position?.zone || badgeCourtoisie || badgeRoole
                || (peut('client.echanges_voir') && editingId && echangesCharges)) && (
                <div className="im-status-drapeaux">
                  {badgeLitige && <span className="im-badge im-badge-litige">⚠️ Litige</span>}
                  {(parking?.position?.zone || badgeParking) && (
                    <span className="im-badge im-badge-parking"
                      title={parking?.position?.maj
                        ? `Position déclarée le ${formatMajParking(parking.position.maj)}${parking.position.par ? ` par ${parking.position.par}` : ''}`
                        : 'Véhicule repéré au parking'}>
                      📍 {parking?.position?.zone || 'Au parking'}
                      {parking?.position?.maj && (
                        <span className="im-badge-maj"> · {formatMajParking(parking.position.maj)}</span>
                      )}
                    </span>
                  )}
                  {/* Dernier contact client — même grammaire que « Statut depuis ».
                      Masqué sans la capacité, comme l'onglet qui l'alimente. */}
                  {peut('client.echanges_voir') && editingId && echangesCharges && (() => {
                    const a = ancienneteContact(dernierContact);
                    return (
                      <span
                        className={`im-badge im-badge-contact${a.alerte ? ' im-badge-contact--alerte' : ''}`}
                        title={dernierContact
                          ? `Dernier échange client : ${libelleQuand(dernierContact)}`
                          : 'Aucun échange client enregistré sur ce dossier'}
                        onClick={() => setModalTab('echanges')}
                        style={{ cursor: 'pointer' }}
                      >💬 {dernierContact ? `Contact ${a.texte}` : 'Jamais contacté'}</span>
                    );
                  })()}
                  {badgeCourtoisie && <span className="im-badge im-badge-courtoisie">🚗 Courtoisie</span>}
                  {badgeRoole && <span className="im-badge im-badge-roole">ROOLE</span>}
                </div>
              )}
            </div>

          {/* ── Body row (sidebar + contenu) ── */}
          <div className="im-body-row">
            {/* ═══ SIDEBAR ═══
                Sans intitulés de rubrique (2026-08-15, demande utilisateur) :
                « Dossier » et « Pièces jointes » n'apprenaient rien sur sept
                items. Un simple filet sépare les pièces jointes du dossier. */}
            <nav className="im-sidebar">
              <div className="im-sidebar-section">
                {sidebarItems.filter(i => i.section === 'dossier').map(item => (
                  <div key={item.key} className={`im-sidebar-item${modalTab === item.key ? ' active' : ''}`}
                    onClick={() => {
                      setModalTab(item.key);
                    }}
                  >
                    {sidebarIcons[item.key]}
                    <span>{item.label}</span>
                    {item.dot && <span className="im-sidebar-dot" style={{ background: item.dot }} />}
                    {item.count > 0 && <span className="im-sidebar-badge">{item.count}</span>}
                  </div>
                ))}
              </div>
              <div className="im-sidebar-section im-sidebar-section--pj">
                {sidebarItems.filter(i => i.section === 'pj').map(item => (
                  <div key={item.key} className={`im-sidebar-item${modalTab === item.key ? ' active' : ''}`}
                    onClick={() => {
                      setModalTab(item.key);
                    }}
                  >
                    {sidebarIcons[item.key]}
                    <span>{item.label}</span>
                    {item.count > 0 && <span className="im-sidebar-badge">{item.count}</span>}
                  </div>
                ))}
              </div>
            </nav>

            {/* ═══ MAIN (contenu scrollable) ═══ */}
            <div className="im-main">
            {/* <fieldset> et non <div> : `disabled` désactive d'un coup TOUS les
                champs en mode consultation — un seul point de contrôle, impossible
                à oublier en ajoutant un champ. Sa mise à zéro (bordure, marge,
                largeur minimale) est dans `.im-body`, pas ici. */}
            {/* En consultation, deux zones restent inscriptibles pour qui en a le
                droit propre : le compte rendu (onglet Atelier) et les commentaires
                internes. Le rôle Technicien, sans « replanifier », ouvre les dossiers
                en consultation — sans cette exception, il ne pouvait plus rien
                écrire du tout (signalé chez CENTRAUTODIS le 30/09). Le reste de
                l'onglet Intervention garde son propre <fieldset disabled>. */}
            <fieldset className="im-body" disabled={lectureSeule && !ongletAZoneLibre} style={modalTab === 'analyse-ia' ? { display: 'none' } : undefined}>

              {/* ═══ INTERVENTION ═══ */}
              {modalTab === 'intervention' && (
                <>
                  <fieldset className="im-fieldset-nu" disabled={lectureSeule}>
                  {/* ═══ Bloc RÉCEPTION (carte) ═══
                      Une étape = une carte : les champs ET leur aperçu planning
                      vivent dans le même contour, alors qu'ils formaient avant deux
                      sections indépendantes séparées par une simple marge. La carte
                      s'éclaire (bordure accent) quand le focus est dedans. */}
                  <div
                    className={`im-block${activeBlock === 'reception' ? ' is-active' : ''}`}
                    onFocus={() => setActiveBlock('reception')}
                    onMouseDown={handleEnterBlock('reception')}
                  >
                    <div className="im-block-title">Réception</div>
                    {/* Entrer dans n'importe quel champ du bloc ouvre l'aperçu planning
                        réception (le focus remonte depuis les enfants) — voir
                        `handleFocusTravaux` pour le pendant côté Travaux. */}
                    <div className="im-reception-row" onFocus={handleFocusReception}>
                      {/* Réception et Restitution ont désormais la MÊME forme :
                          un jour et une heure côte à côte, aux mêmes largeurs
                          (2026-09-05, demande utilisateur). Deux libellés pour
                          dire une seule chose — « Date » puis « Heure
                          réception » — n'apprenaient rien et cassaient la
                          symétrie des deux moments du dossier. */}
                      <div className="im-field im-field--restit">
                        <label>Réception <span className="im-required">*</span></label>
                        <div className="im-restit">
                          <input type="date" className="im-restit-date" value={formData.date_reception}
                            title="Jour de réception du véhicule"
                            onChange={(e) => setFormData({ ...formData, date_reception: e.target.value })}
                            required />
                          <select value={formData.heure_reception}
                            aria-label="Heure de réception"
                            title="Heure de réception"
                            onChange={(e) => setFormData({ ...formData, heure_reception: e.target.value })}>
                            {heures.map(h => <option key={h} value={h}>{h}</option>)}
                          </select>
                        </div>
                      </div>
                      {/* Restitution PRÉVUE : ce qu'on annonce au client. Vide = on retombe
                          sur la fin d'intervention calculée (heure de début + durée), y
                          compris pour le DeliveryDate envoyé à Datacar. */}
                      <div className="im-field im-field--restit">
                        <label>Restitution prévue</label>
                        {/* [13] La DATE peut désormais différer du jour même
                            (2026-09-05, demande utilisateur) : un véhicule
                            immobilisé en attente de pièce n'est pas rendu le
                            soir. Vide = le jour de l'intervention, ce qui reste
                            le cas courant et évite d'imposer une saisie. */}
                        <div className="im-restit">
                          <input type="date" className="im-restit-date"
                            value={formData.date_restitution_prevue || ''}
                            title="Jour de restitution — vide = le jour de l'intervention"
                            onChange={(e) => setFormData({ ...formData, date_restitution_prevue: e.target.value })} />
                          <select value={formData.heure_restitution_prevue || ''}
                            onChange={(e) => setFormData({ ...formData, heure_restitution_prevue: e.target.value })}>
                            <option value="">— {finIntervention || 'auto'} —</option>
                            {heures.map(h => <option key={h} value={h}>{h}</option>)}
                          </select>
                        </div>
                      </div>
                      <div className="im-field im-field--recep">
                        <label>Réceptionnaire <span className="im-required">*</span></label>
                        <select value={formData.poste_reception ?? ''}
                          onChange={(e) => setFormData({ ...formData, poste_reception: e.target.value ? parseInt(e.target.value) : null })}>
                          <option value="">—</option>
                          {staffRecep.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                        </select>
                        {/* Dossier importé de Mécaplanning dont le réceptionnaire n'a pas été
                            reconnu dans le personnel : le nom est dans le commentaire d'import,
                            on le montre plutôt que de laisser un champ vide inexpliqué. */}
                        {!formData.poste_reception && recepMecaplanningInconnu && (
                          <div className="im-field-hint">
                            Mécaplanning indique « {recepMecaplanningInconnu} », introuvable parmi les réceptionnaires de la
                            concession. Choisissez-le ci-dessus, ou ajoutez-le dans Configuration Planning ▸ Mécaniciens & Réceptionnaires.
                          </div>
                        )}
                      </div>
                    </div>
                    {/* Le client patiente au comptoir : le dossier doit passer en priorité.
                        Remonte en picto ⏱️ sur la carte du planning. */}
                    <label className="im-attend-check">
                      <input type="checkbox" checked={formData.client_attend === true}
                        onChange={(e) => setFormData({ ...formData, client_attend: e.target.checked })} />
                      <span>⏱️ Le client attend sur place</span>
                    </label>
                    {/* Véhicule déposé sans créneau : le dossier sort de la grille
                        et remonte dans la section 🪝 À planifier du panneau, d'où
                        on le glisse sur la grille le moment venu. */}
                    <label className="im-attend-check im-aplanifier-check">
                      <input type="checkbox" checked={formData.a_planifier === true}
                        onChange={(e) => setFormData({ ...formData, a_planifier: e.target.checked })} />
                      <span>🪝 À planifier</span>
                    </label>
                    <label className="im-attend-check im-aplanifier-check">
                      <input type="checkbox" checked={formData.contrat_maintenance === true}
                        onChange={(e) => setFormData({ ...formData, contrat_maintenance: e.target.checked })} />
                      <span>📄 Contrat de maintenance</span>
                    </label>

                  {/* Aperçu planning réception — imbriqué dans la carte Réception */}
                  <div className={`im-block-sub${showReceptionApercu ? '' : ' is-collapsed'}`}>
                    <div className="im-section-header">
                      <button type="button" className="im-section-link" onClick={() => setShowReceptionApercu(v => !v)}>
                        {showReceptionApercu ? 'Masquer' : 'Afficher'}
                      </button>
                    </div>
                    {showReceptionApercu && (
                      <MiniReceptionGrid
                        date={formData.date_reception}
                        receptionnaires={receptionnaires}
                        rdvReception={rdvReception}
                        heures={heures}
                        currentInterventionId={editingId?.interventionId}
                        current={{
                          date: formData.date_reception,
                          heure: formData.heure_reception,
                          poste: formData.poste_reception,
                          label: headerClientName || formData.immatriculation || 'Ce dossier',
                        }}
                        onMove={(rdvId, changes) => onQuickMoveReception && onQuickMoveReception(rdvId, changes)}
                        onPickSlot={(pickedDate, heure, posteId) => setFormData(prev => ({ ...prev, date_reception: pickedDate, heure_reception: heure, poste_reception: posteId }))}
                      />
                    )}
                  </div>{/* fin im-block-sub */}
                  </div>{/* fin bloc Réception */}

                  {/* ═══ Bloc TRAVAUX (carte) ═══ même principe que Réception */}
                  <div
                    className={`im-block${activeBlock === 'travaux' ? ' is-active' : ''}`}
                    onFocus={() => setActiveBlock('travaux')}
                    onMouseDown={handleEnterBlock('travaux')}
                  >
                    <div className="im-travaux-header">
                      <span className="im-block-title">Travaux à réaliser</span>
                      <span className="im-travaux-total">
                        TOTAL {formatDuree(formData.travaux.reduce((s, t) => s + (t.duree || 0), 0))}
                      </span>
                    </div>
                    <div className="im-travaux-wrapper" onFocus={handleFocusTravaux}>
                    <div className="im-travaux-head">
                      <span className="im-tw-prestation">Prestation</span>
                      <span className="im-tw-duree">Durée</span>
                      <span className="im-tw-date">Date</span>
                      <span className="im-tw-heure">Heure</span>
                      <span className="im-tw-meca">Mécanicien</span>
                      <span className="im-tw-actions"></span>
                    </div>
                    {formData.travaux.map((t, idx) => {
                      const isFirst = idx === 0;
                      // Un travail sans mécanicien ni date propres suit l'intervention
                      // principale. On nomme ce mécanicien dans la liste plutôt que
                      // d'afficher « (princ.) », qui n'explique rien.
                      const mainMeca = mecasSelection.find(m => m.id === formData.mecanicien_id);
                      const mainMecaLabel = mainMeca ? getStaffLabel(mainMeca) : (formData.mecanicien || '');
                      // Repère : ce travail partira sur une carte à lui.
                      const carteSeparee = !isFirst && !!(t.mecanicien_id || t.date_intervention || t.heure_debut);
                      const mecaId = isFirst ? formData.mecanicien_id : t.mecanicien_id;
                      const dateVal = isFirst ? formData.date_intervention : t.date_intervention;
                      const heureVal = isFirst ? formData.heure_debut : t.heure_debut;
                      const dureeOptions = [0.25, 0.5, 0.75, 1, 1.5, 2, 2.5, 3, 4, 5, 6, 7, 8];
                      const dureeInOptions = dureeOptions.includes(t.duree);
                      return (
                        <div className="im-travaux-row" key={idx}>
                          <div className="im-tw-prestation">
                            {t.libelle}
                            {carteSeparee && (
                              <span className="im-tw-split" title="Ce travail aura sa propre carte sur le planning">🔄</span>
                            )}
                          </div>
                          <div className="im-tw-duree">
                            {customDureeIdx === idx ? (
                              <input type="number" step="0.25" min="0.25" autoFocus defaultValue={t.duree}
                                onBlur={(e) => { const val = parseFloat(e.target.value) || 0.5; const updated = [...formData.travaux]; const ancienne = updated[idx].duree; updated[idx] = { ...updated[idx], duree: Math.max(0.25, val) }; const mainDuree = isFirst ? updated[0].duree : dureeApresChangement(ancienne, updated[idx].duree, updated[idx]); setFormData({ ...formData, travaux: updated, duree: isFirst ? updated[0].duree : mainDuree }); setCustomDureeIdx(null); }}
                                onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                              />
                            ) : (
                              <select value={dureeInOptions ? t.duree : '__custom__'}
                                onChange={(e) => { if (e.target.value === '__custom__') { setCustomDureeIdx(idx); return; } const updated = [...formData.travaux]; const ancienne = updated[idx].duree; updated[idx] = { ...updated[idx], duree: parseFloat(e.target.value) }; const mainDuree = isFirst ? updated[0].duree : dureeApresChangement(ancienne, updated[idx].duree, updated[idx]); setFormData({ ...formData, travaux: updated, duree: isFirst ? updated[0].duree : mainDuree }); }}
                              >
                                {!dureeInOptions && <option value="__custom__">{formatDuree(t.duree)}</option>}
                                <option value="0.25">15min</option><option value="0.5">30min</option><option value="0.75">45min</option>
                                <option value="1">1h</option><option value="1.5">1h30</option><option value="2">2h</option>
                                <option value="2.5">2h30</option><option value="3">3h</option><option value="4">4h</option>
                                <option value="5">5h</option><option value="6">6h</option><option value="7">7h</option><option value="8">8h</option>
                                <option value="__custom__">Autre</option>
                              </select>
                            )}
                          </div>
                          {/* Champ date natif, piloté par la valeur ISO. Auparavant c'était un
                              `type="text"` basculé en `type="date"` au focus par manipulation
                              directe du DOM : le sélecteur natif renvoyant de l'ISO, le onChange
                              — qui n'acceptait que `jj/mm/aa` — sortait sans rien enregistrer et
                              effaçait même la valeur affichée. Le navigateur se charge seul du
                              format d'affichage (jj/mm/aaaa en français). */}
                          <div className="im-tw-date">
                            <input type="date"
                              value={dateVal || ''}
                              onChange={(e) => {
                                const iso = e.target.value || null;
                                if (isFirst) {
                                  // La date de l'intervention principale est obligatoire :
                                  // on ignore un effacement plutôt que de la vider.
                                  // La réception SUIT tant qu'elle n'a pas été dissociée
                                  // (même jour = cas nominal ; sinon le RDV resterait daté
                                  // de l'ancien jour, le « bug de dates » constaté).
                                  // La réception ne suit PLUS la date d'intervention :
                                  // le véhicule est très souvent déjà réceptionné, et la
                                  // déplacer gonflerait à tort la charge du planning de
                                  // réception. La date de réception ne change qu'via son
                                  // propre sélecteur, jamais en planifiant un travail.
                                  if (iso) setFormData({ ...formData, date_intervention: iso });
                                } else {
                                  const updated = [...formData.travaux];
                                  const avant = estTravailComplementaire(updated[idx]);
                                  updated[idx] = { ...updated[idx], date_intervention: iso };
                                  const apres = estTravailComplementaire(updated[idx]);
                                  setFormData({ ...formData, travaux: updated, duree: dureeApresBascule(avant, apres, updated[idx]) });
                                }
                              }}
                            />
                          </div>
                          <div className="im-tw-heure">
                            <select value={heureVal || ''}
                              onChange={(e) => { if (isFirst) setFormData({ ...formData, heure_debut: e.target.value }); else { const updated = [...formData.travaux]; const avant = estTravailComplementaire(updated[idx]); updated[idx] = { ...updated[idx], heure_debut: e.target.value || null }; const apres = estTravailComplementaire(updated[idx]); setFormData({ ...formData, travaux: updated, duree: dureeApresBascule(avant, apres, updated[idx]) }); } }}
                            >
                              {!isFirst && <option value="">—</option>}
                              {heures.map(h => <option key={h} value={h}>{h}</option>)}
                            </select>
                          </div>
                          <div className="im-tw-meca">
                            <div className="im-meca-cell">
                              <select className="im-meca-select" value={mecaId || ''}
                                onChange={(e) => { const staffId = parseInt(e.target.value) || null; const staff = mecasSelection.find(m => m.id === staffId); if (isFirst) setFormData({ ...formData, mecanicien_id: staffId, mecanicien: staff ? getStaffLabel(staff) : '' }); else { const updated = [...formData.travaux]; const avant = estTravailComplementaire(updated[idx]); updated[idx] = { ...updated[idx], mecanicien_id: staffId, mecanicien: staff ? `${staff.prenom} ${staff.nom}`.trim() : null }; const apres = estTravailComplementaire(updated[idx]); setFormData({ ...formData, travaux: updated, duree: dureeApresBascule(avant, apres, updated[idx]) }); } }}
                              >
                                <option value="">
                                  {isFirst ? '—' : (mainMecaLabel ? `↳ ${mainMecaLabel} (principal)` : '(principal)')}
                                </option>
                                {groupesMecaniciens
                                  ? groupesMecaniciens.map(g => (
                                      <optgroup key={g.atelier} label={g.atelier}>
                                        {g.mecas.map(m => <option key={m.id} value={m.id}>{getStaffLabel(m)}</option>)}
                                      </optgroup>
                                    ))
                                  : mecaniciens.map(m => <option key={m.id} value={m.id}>{getStaffLabel(m)}</option>)}
                              </select>
                            </div>
                          </div>
                          <div className="im-tw-actions">
                            <button type="button" className="im-travaux-delete" onClick={() => { const updated = formData.travaux.filter((_, i) => i !== idx); const mainDuree = updated.length > 0 ? (updated[0].duree || 0.5) : 0.5; setFormData({ ...formData, travaux: updated, type_intervention: updated.map(t => t.libelle).join(' + ') || '', duree: mainDuree }); }}>✕</button>
                          </div>
                        </div>
                      );
                    })}
                    {/* Ajouter un travail */}
                    {!showAutreTravail ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px' }}>
                        <select value="" className="im-travaux-add" style={{ flex: 1, border: 'none', background: 'none', cursor: 'pointer', fontSize: 13, color: 'var(--color-accent)', fontWeight: 600 }}
                          onChange={(e) => { if (!e.target.value) return; if (e.target.value === '__autre__') { setShowAutreTravail(true); setAutreTravailLibelle(''); return; } const tt = travauxTypes.find(t => t.id === parseInt(e.target.value)); if (!tt) return; setFormData(prev => { const updated = [...prev.travaux, { travaux_type_id: tt.id, libelle: tt.libelle, duree: parseFloat(tt.duree_defaut) }]; // Ajouter un travail AJOUTE son temps, il ne recalcule pas le dossier :
                          // une durée posée à la main (54 % des dossiers) serait sinon écrasée.
                          const mainDuree = updated.length === 1
                            ? updated[0].duree
                            : Math.round(((parseFloat(prev.duree) || 0) + (updated[updated.length - 1].duree || 0)) * 100) / 100; return { ...prev, travaux: updated, type_intervention: updated.map(t => t.libelle).join(' + '), duree: mainDuree }; }); }}
                        >
                          <option value="">+ Ajouter un travail</option>
                          {travauxTypes.map(tt => <option key={tt.id} value={tt.id}>{tt.libelle} ({formatDuree(tt.duree_defaut)})</option>)}
                          <option value="__autre__">Autre...</option>
                        </select>
                      </div>
                    ) : (
                      <div className="im-autre-travail">
                        <input type="text" placeholder="Libellé du travail..." value={autreTravailLibelle} onChange={(e) => setAutreTravailLibelle(e.target.value)} autoFocus
                          onKeyDown={(e) => { if (e.key === 'Enter' && autreTravailLibelle.trim()) { setFormData(prev => { const updated = [...prev.travaux, { travaux_type_id: null, libelle: autreTravailLibelle.trim(), duree: 1 }]; // Ajouter un travail AJOUTE son temps, il ne recalcule pas le dossier :
                          // une durée posée à la main (54 % des dossiers) serait sinon écrasée.
                          const mainDuree = updated.length === 1
                            ? updated[0].duree
                            : Math.round(((parseFloat(prev.duree) || 0) + (updated[updated.length - 1].duree || 0)) * 100) / 100; return { ...prev, travaux: updated, type_intervention: updated.map(t => t.libelle).join(' + '), duree: mainDuree }; }); setShowAutreTravail(false); setAutreTravailLibelle(''); } else if (e.key === 'Escape') setShowAutreTravail(false); }}
                        />
                        <button type="button" className="im-autre-travail-ok" disabled={!autreTravailLibelle.trim()} onClick={() => { if (!autreTravailLibelle.trim()) return; setFormData(prev => { const updated = [...prev.travaux, { travaux_type_id: null, libelle: autreTravailLibelle.trim(), duree: 1 }]; // Ajouter un travail AJOUTE son temps, il ne recalcule pas le dossier :
                          // une durée posée à la main (54 % des dossiers) serait sinon écrasée.
                          const mainDuree = updated.length === 1
                            ? updated[0].duree
                            : Math.round(((parseFloat(prev.duree) || 0) + (updated[updated.length - 1].duree || 0)) * 100) / 100; return { ...prev, travaux: updated, type_intervention: updated.map(t => t.libelle).join(' + '), duree: mainDuree }; }); setShowAutreTravail(false); setAutreTravailLibelle(''); }}>OK</button>
                        <button type="button" className="im-autre-travail-cancel" onClick={() => setShowAutreTravail(false)}>✕</button>
                      </div>
                    )}
                  </div>{/* fin im-travaux-wrapper */}

                  {repartitionProposee && (
                    <div className="im-repartition">
                      {!repartition?.segments ? (
                        <div className="im-repartition-ligne">
                          <span className="im-repartition-texte">
                            {depasseJournee
                              ? `${formatDuree(formData.duree)} dépassent la journée : l'atelier ferme à ${horairesMain.fermeture}.`
                              : `Dossier réparti sur ${lignesRepartition.length + 1} jours (${formatDuree(dureeARepartir)}).`}
                            {repartition?.erreur && <span className="im-repartition-alerte"> {repartition.erreur}</span>}
                          </span>
                          <button type="button" className="btn-secondary im-repartition-btn"
                            disabled={!!repartition?.busy} onClick={preparerRepartition}>
                            {repartition?.busy ? 'Calcul…' : (lignesRepartition.length ? 'Répartir à nouveau' : 'Répartir sur plusieurs jours')}
                          </button>
                        </div>
                      ) : (
                        <>
                          <div className="im-repartition-titre">
                            {formatDuree(repartition.total)} → {repartition.segments.length} jour{repartition.segments.length > 1 ? 's' : ''}, même mécanicien
                          </div>
                          <ul className="im-repartition-liste">
                            {repartition.segments.map(sg => (
                              <li key={sg.date}>
                                <span>{formatDateFr(sg.date, { weekday: 'short', day: '2-digit', month: '2-digit' })}</span>
                                <span>à {sg.heure}</span>
                                <strong>{formatDuree(sg.duree)}</strong>
                              </li>
                            ))}
                          </ul>
                          {repartition.reste > 0 && (
                            <div className="im-repartition-alerte">
                              {formatDuree(repartition.reste)} restent à placer : aucun jour ouvert trouvé dans les semaines suivantes.
                            </div>
                          )}
                          <div className="im-repartition-aide">
                            Jours de fermeture, fériés et absences d'une journée entière sautés, pauses déduites.
                            Les autres dossiers du mécanicien ne sont pas vérifiés. Rien n'est enregistré avant « Enregistrer ».
                          </div>
                          <div className="im-repartition-ligne">
                            <button type="button" className="btn-secondary" onClick={() => setRepartition(null)}>Annuler</button>
                            <button type="button" className="btn-primary" onClick={appliquerRepartition}>Appliquer</button>
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  {/* Aperçu planning intervention — imbriqué dans la carte Travaux */}
                  <div className={`im-block-sub${showAtelierApercu ? '' : ' is-collapsed'}`}>
                    <div className="im-section-header">
                      <button type="button" className="im-section-link" onClick={() => setShowAtelierApercu(v => !v)}>
                        {showAtelierApercu ? 'Masquer' : 'Afficher'}
                      </button>
                    </div>
                    {showAtelierApercu && (
                      <MiniAtelierGrid
                        date={formData.date_intervention}
                        mecaniciens={mecaniciens}
                        groupes={groupesMecaniciens}
                        interventions={interventions}
                        heures={heures}
                        currentInterventionId={editingId?.interventionId}
                        currents={apercuCreneaux}
                        onMove={(interventionId, changes) => onQuickMoveIntervention && onQuickMoveIntervention(interventionId, changes)}
                        onPickSlot={(pickedDate, heure, mecaId) => {
                          const staff = mecaniciens.find(m => m.id === mecaId);
                          setFormData(prev => ({
                            ...prev, date_intervention: pickedDate, heure_debut: heure,
                            mecanicien_id: mecaId, mecanicien: staff ? getStaffLabel(staff) : '',
                            // La réception ne suit pas : elle ne change qu'via son propre
                            // sélecteur (véhicule souvent déjà réceptionné).
                          }));
                        }}
                      />
                    )}
                  </div>{/* fin im-block-sub */}
                  </div>{/* fin bloc Travaux */}
                  </fieldset>{/* fin im-fieldset-nu */}

                  {/* Commentaires internes */}
                  <div className="im-section im-comments-section">
                    <div className="im-section-header">
                      <span className="im-section-title">Commentaires internes</span>
                      {interventionCommentaires.length > 0 && (
                        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{interventionCommentaires.length} note{interventionCommentaires.length > 1 ? 's' : ''}</span>
                      )}
                    </div>
                    {interventionCommentaires.map(c => (
                      <div key={c.id} className="im-comment">
                        <div className="im-comment-content">
                          <div className="im-comment-meta">
                            <span className="im-comment-author">{[c.user_prenom, c.user_nom].filter(Boolean).join(' ') || '—'}</span>
                            <span className="im-comment-date">{timeAgo(c.created_at)}</span>
                            {editMode && user?.role === 'super-admin' && peut('dossier.commentaire_supprimer') && (
                              <button type="button" className="im-comment-delete" onClick={async () => {
                                if (!editingId) return;
                                try { await api.delete(`/planning/interventions/${editingId.interventionId}/commentaires/${c.id}`); setInterventionCommentaires(prev => prev.filter(x => x.id !== c.id)); } catch (err) { console.error(err); }
                              }}>✕</button>
                            )}
                          </div>
                          <div className="im-comment-text">{c.texte}</div>
                        </div>
                      </div>
                    ))}
                    {editingId ? (
                      <div className="im-comment-input">
                        <textarea value={newCommentText} onChange={(e) => setNewCommentText(e.target.value)}
                          placeholder="Ajouter un commentaire interne..." rows={1}
                        />
                        <button type="button" className="im-comment-send" disabled={!newCommentText.trim()}
                          onClick={async () => {
                            if (!newCommentText.trim() || !editingId) return;
                            try { const r = await api.post(`/planning/interventions/${editingId.interventionId}/commentaires`, { texte: newCommentText.trim() }); setInterventionCommentaires(prev => [...prev, r.data.commentaire]); setNewCommentText(''); } catch (err) { console.error(err); }
                          }}
                        >Envoyer</button>
                      </div>
                    ) : (
                      <>
                        <textarea value={formData.commentaire}
                          onChange={(e) => setFormData({ ...formData, commentaire: e.target.value })}
                          placeholder="Déclaration du client — ce qu'il signale, dans ses mots..." rows={3} style={{ width: '100%', borderRadius: 8, border: '1px solid var(--border-default)', padding: 10, fontSize: 13 }}
                        />
                        {formData.commentaire?.trim() && (
                          <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 4 }}>
                            Sera ajouté aux commentaires internes, et remonté dans Datacar en « Déclaration originale ».
                          </div>
                        )}
                      </>
                    )}
                  </div>

                </>
              )}

              {/* ═══ PIÈCES ═══ */}
              {modalTab === 'pieces' && (
                <div className="im-section">
                  <div className="im-section-title">Carnet de commande{formData.numero_or ? ` — OR ${formData.numero_or}` : ''}</div>
                  {!formData.numero_or && <div className="im-empty"><div className="im-empty-icon">📦</div>Saisissez un N° OR pour voir les pièces</div>}
                  {formData.numero_or && carnetLoading && <div className="im-empty">Chargement...</div>}
                  {formData.numero_or && !carnetLoading && !carnetData && <div className="im-empty">Aucune donnée carnet pour cet OR.</div>}
                  {formData.numero_or && !carnetLoading && carnetData && carnetData.lignes && (
                    /* Plus de `overflowX: auto` : le tableau tient dans la
                       largeur de la modale, c'était la demande. Fournisseur
                       supprimé, description passée sous la référence, Cdé et
                       Reçu regroupés en « Cdé / Livrée » — mêmes conventions
                       que le Carnet de commande du Suivi Pièces Atelier. */
                    <table className="im-pieces-table">
                      <colgroup>
                        <col />
                        <col className="im-pc-qty" />
                        <col className="im-pc-stock" />
                        <col className="im-pc-statut" />
                        <col className="im-pc-transport" />
                      </colgroup>
                      <thead>
                        <tr>
                          <th>Référence<span className="im-pc-sub">désignation · commande</span></th>
                          <th className="im-pc-center">Cdé / Livrée</th>
                          <th className="im-pc-center">Stock<span className="im-pc-sub">magasin</span></th>
                          <th>Livraison</th>
                          {/* Facturation retirée (2026-09-01, demande utilisateur) : au
                              chevet d'un véhicule, savoir si la pièce est FACTURÉE ne
                              sert à rien — savoir OÙ ELLE EST, si. La facturation reste
                              dans Suivi Pièces Atelier, écran de gestion. */}
                          <th>Transport<span className="im-pc-sub">🚢 transitaire</span></th>
                        </tr>
                      </thead>
                      <tbody>
                        {carnetData.lignes.map((l, i) => {
                          const slStyles = { livre: { label: 'Reçu', cls: 'stock' }, en_commande: { label: 'Commandée', cls: 'commande' } };
                          const sl = slStyles[l.statut_livraison] || slStyles.en_commande;
                          const cde = l.cde_qty ?? 0;
                          const liv = l.qte_livree ?? 0;
                          // Vert tout livré, ambre partiel, rouge rien reçu —
                          // la paire se lit sans avoir à comparer deux colonnes.
                          const kind = cde === 0 ? '' : (liv >= cde ? 'full' : (liv > 0 ? 'partial' : 'none'));
                          // Un seul trait entre deux pièces, et AUCUN entre une
                          // pièce et son anomalie : le filet du bas est retiré de
                          // la ligne quand une sous-ligne la suit, si bien que le
                          // groupe se referme une seule fois, après l'anomalie.
                          // (Un fond alterné a été essayé puis retiré — trop lourd.)
                          const ligne = (
                            <tr key={i} className={`im-pc-ligne${l.anomalie_active ? ' im-pc-row-anomalie' : ''}`}>
                              <td>
                                <div className="im-pc-ref">
                                  {l.reference || '—'}
                                  {l.ref_rempl && <span className="im-pc-rempl">→ {l.ref_rempl}</span>}
                                </div>
                                <div className="im-pc-desc">{l.description || '—'}</div>
                                {(l.type_commande || l.cde || l.date_envoi) && (
                                  <div className="im-pc-cmd">
                                    <span className="im-pc-cmd-tag">CMD</span>
                                    {l.type_commande && <span>{l.type_commande}</span>}
                                    {l.cde && <span>· {l.cde}</span>}
                                    {/* `date_envoi` = `wtCde.datEnvoi` côté DataCar :
                                        l'envoi de la COMMANDE au fournisseur, porté par
                                        l'en-tête — toutes les pièces d'une même commande
                                        partagent donc la date (vérifié : 2 790 commandes,
                                        un seul horodatage chacune). Vide tant que la
                                        commande est un brouillon non envoyé : on n'écrit
                                        alors rien, plutôt qu'un tiret de plus. */}
                                    {l.date_envoi && (
                                      <span title={`Commande n° ${l.cde || '—'} envoyée au fournisseur le ${formatDateCourte(l.date_envoi)}. La date porte sur la commande entière, pas sur cette pièce seule.`}>
                                        · commandée le {formatDateCourte(l.date_envoi)}
                                      </span>
                                    )}
                                  </div>
                                )}
                              </td>
                              <td className="im-pc-center">
                                <span className={`im-pc-qty-pair ${kind}`}>
                                  {cde}<span className="im-pc-qty-sep">/</span>{liv}
                                </span>
                              </td>
                              <td className="im-pc-center">
                                {l.reference
                                  ? <StockCell partRef={l.reference} stockMap={stockMap} loading={stockLoading} />
                                  : <span className="cc-stock-none">—</span>}
                              </td>
                              <td><span className={`im-pieces-status ${sl.cls}`}>{sl.label}</span></td>
                              <td>
                                {l.sifa
                                  ? <ExpeditionChip sifa={l.sifa} livree={l.statut_livraison === 'livre'} />
                                  : <span className="cc-stock-none">—</span>}
                              </td>
                            </tr>
                          );
                          // L'anomalie passe SOUS la ligne, sur toute la largeur
                          // (2026-09-01, demande utilisateur — patron de Suivi
                          // Pièces Atelier) : en colonne de droite elle rognait
                          // les autres et son badge se tronquait. En sous-ligne,
                          // elle a la place de dire ce qu'elle est, qui l'a
                          // signalée et quand — et rien ne s'affiche quand il
                          // n'y a rien à dire.
                          const anomalie = l.anomalie_active ? (
                            <tr key={`${i}-anomalie`} className="im-pc-anomalie-row">
                              <td colSpan={5}>
                                <div className="im-pc-anomalie-ligne">
                                  {anomalieBadge(l.anomalie_active)}
                                  {l.anomalie_active.commentaire && (
                                    <span className="im-pc-anomalie-comment">« {l.anomalie_active.commentaire} »</span>
                                  )}
                                  {l.anomalie_active.created_by_name && (
                                    <span className="im-pc-anomalie-meta">
                                      par {l.anomalie_active.created_by_name}
                                      {l.anomalie_active.created_at ? `, ${formatDateCourte(l.anomalie_active.created_at)}` : ''}
                                    </span>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ) : null;
                          return anomalie ? <React.Fragment key={i}>{ligne}{anomalie}</React.Fragment> : ligne;
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              )}

              {/* ═══ VÉHICULE ═══ */}
              {/* Colonne Durée retirée et largeurs contraintes (2026-09-01) : le
                  tableau débordait à droite et le n° d'OR — la seule colonne qui
                  sert à retrouver un dossier — sortait du cadre. Le TYPE est la
                  seule colonne élastique, et il se coupe proprement au lieu de
                  pousser les autres. */}
              {modalTab === 'vehicule' && (
                <div className="im-section">
                  <div className="im-section-title">Historique véhicule</div>
                  {vehiculeHistory.length === 0 ? (
                    <div className="im-empty"><div className="im-empty-icon">🚗</div>{formData.immatriculation ? 'Aucune autre intervention trouvée' : 'Renseignez l\'immatriculation'}</div>
                  ) : (
                    <table className="im-travaux-table im-histo-table">
                      <colgroup>
                        <col className="im-histo-date" />
                        <col />
                        <col className="im-histo-meca" />
                        <col className="im-histo-or" />
                      </colgroup>
                      <thead><tr><th>Date</th><th>Type</th><th>Mécanicien</th><th>OR</th></tr></thead>
                      <tbody>
                        {vehiculeHistory.map(i => (
                          <tr key={i.id}>
                            <td>{formatDateFr(i.date || i.date_intervention, { day: 'numeric', month: 'short', year: 'numeric' })}</td>
                            <td className="im-histo-type" title={i.description || i.type_intervention || ''}>
                              {i.description || i.type_intervention}
                            </td>
                            <td className="im-histo-cell">{i.mecanicien}</td>
                            <td className="im-histo-cell">{i.numero_or ? `#${i.numero_or}` : '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}

              {/* ═══ SUIVI ═══ */}

              {/* ═══ COURTOISIE ═══ */}
              {modalTab === 'courtoisie' && (
                <div className="im-section">
                  <div className="im-section-title">Véhicule de courtoisie{formData.numero_or ? ` — OR ${formData.numero_or}` : ''}</div>
                  {!formData.numero_or ? (
                    <div className="im-empty"><div className="im-empty-icon">🔑</div>Saisissez un N° OR pour gérer un prêt de courtoisie</div>
                  ) : pretCourtoisieLoading ? (
                    <div className="im-empty">Chargement…</div>
                  ) : showPretForm ? (
                    <>
                      <PretCourtoisieForm
                        pretMode={pretModeIntervention}
                        formPret={formPretIntervention}
                        setFormPret={setFormPretIntervention}
                        saving={savingPretIntervention}
                        vehiculesActifs={vehiculesCourtoisie}
                        loueurs={loueursCourtoisie}
                        pretFichiersBuffer={pretFichiersBufferIntervention}
                        addPretFichierBuffer={addPretFichierBufferIntervention}
                        removePretFichierBuffer={removePretFichierBufferIntervention}
                      />
                      <div style={{ display: 'flex', gap: 8, marginTop: 12, justifyContent: 'flex-end' }}>
                        <button className="btn-secondary" onClick={() => setShowPretForm(false)} disabled={savingPretIntervention}>Annuler</button>
                        <button
                          className="btn-primary"
                          onClick={handleSavePretIntervention}
                          disabled={savingPretIntervention || !formPretIntervention.client_nom || !formPretIntervention.date_debut || !formPretIntervention.date_fin_prevue}
                        >
                          {savingPretIntervention ? 'Enregistrement…' : '📄 Enregistrer le prêt'}
                        </button>
                      </div>
                    </>
                  ) : pretCourtoisie ? (
                    <div>
                      <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '0 0 12px' }}>
                        Récapitulatif du prêt — les documents et les données d'identité du client (naissance, permis) ne sont pas affichés ici.
                      </p>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 24px', fontSize: 13 }}>
                        <div><span style={{ color: 'var(--text-secondary)' }}>Véhicule</span><br />
                          <strong>
                            {pretCourtoisie.type_pret === 'location'
                              ? `${pretCourtoisie.loueur_immat || '—'} — ${pretCourtoisie.loueur_marque || pretCourtoisie.loueur_nom || ''}`
                              : `${pretCourtoisie.immatriculation || '—'} — ${pretCourtoisie.marque || ''} ${pretCourtoisie.modele || ''}`}
                          </strong>
                        </div>
                        <div><span style={{ color: 'var(--text-secondary)' }}>Statut</span><br /><strong>{pretCourtoisie.statut}</strong></div>
                        <div><span style={{ color: 'var(--text-secondary)' }}>Client</span><br />
                          <strong>{pretCourtoisie.client_nom} {pretCourtoisie.client_prenom}</strong>
                        </div>
                        <div><span style={{ color: 'var(--text-secondary)' }}>Contact</span><br />
                          <strong>{pretCourtoisie.client_telephone || '—'}{pretCourtoisie.client_email ? ` · ${pretCourtoisie.client_email}` : ''}</strong>
                        </div>
                        <div><span style={{ color: 'var(--text-secondary)' }}>Dates</span><br />
                          <strong>{pretCourtoisie.date_debut} → {pretCourtoisie.date_fin_prevue}{pretCourtoisie.date_retour ? ` (retourné le ${pretCourtoisie.date_retour})` : ''}</strong>
                        </div>
                        <div><span style={{ color: 'var(--text-secondary)' }}>Kilométrage</span><br />
                          <strong>{pretCourtoisie.km_depart ?? '—'}{pretCourtoisie.km_retour != null ? ` → ${pretCourtoisie.km_retour}` : ''}</strong>
                        </div>
                        <div><span style={{ color: 'var(--text-secondary)' }}>Motif</span><br /><strong>{pretCourtoisie.motif || '—'}</strong></div>
                        {pretCourtoisie.caution_type && (
                          <div><span style={{ color: 'var(--text-secondary)' }}>Caution</span><br />
                            <strong>{pretCourtoisie.caution_type === 'cheque' ? 'Chèque' : 'Empreinte CB'}{pretCourtoisie.caution_montant ? ` — ${pretCourtoisie.caution_montant} €` : ''}</strong>
                          </div>
                        )}
                        {pretCourtoisie.facturation && (
                          <div><span style={{ color: 'var(--text-secondary)' }}>Facturation</span><br />
                            <strong>{pretCourtoisie.facturation.jours} j · HT {pretCourtoisie.facturation.ht} € · TTC {pretCourtoisie.facturation.ttc} €</strong>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="im-empty">
                      <div className="im-empty-icon">🔑</div>
                      Aucun prêt de courtoisie pour cet OR.
                      <div style={{ display: 'flex', gap: 8, marginTop: 12, justifyContent: 'center' }}>
                        <button className="btn-secondary" onClick={() => handleOpenPretForm('interne')}>🏢 Prêt interne</button>
                        <button className="btn-primary" onClick={() => handleOpenPretForm('location')}>🤝 Prêt location</button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* ═══ SIGNATURE ═══ */}
              {modalTab === 'signature' && (
                <div className="im-section">
                  <div className="im-section-header">
                    <span className="im-section-title">Signature électronique{formData.numero_or ? ` — OR ${formData.numero_or}` : ''}</span>
                    {editingId && formData.numero_or && (
                      <button type="button" className="im-section-link" onClick={handleOpenEsignForm}>+ Envoyer un document</button>
                    )}
                  </div>

                  {!editingId && (
                    <div className="im-empty"><div className="im-empty-icon">✍️</div>Enregistrez d'abord le dossier pour envoyer un document en signature</div>
                  )}
                  {editingId && !formData.numero_or && (
                    <div className="im-empty"><div className="im-empty-icon">✍️</div>Saisissez un N° OR pour suivre les signatures de ce dossier</div>
                  )}

                  {editingId && formData.numero_or && esignSendResult && (
                    <div className="esig-result">
                      <div className="esig-result-title">
                        {(esignSendResult.sent?.email?.ok || esignSendResult.sent?.sms?.ok) ? '✅ Document envoyé' : '📄 Lien de signature généré'}
                      </div>
                      <div className="esig-result-lines">
                        {esignSendResult.email && (
                          <span className={esignSendResult.sent?.email?.ok ? 'esig-ok' : 'esig-ko'}>
                            {esignSendResult.sent?.email?.ok ? `✉️ Email envoyé à ${esignSendResult.email}` : `✉️ Email non envoyé${esignSendResult.sent?.email?.error ? ` (${esignSendResult.sent.email.error})` : ''}`}
                          </span>
                        )}
                        {esignSendResult.tel && (
                          <span className={esignSendResult.sent?.sms?.ok ? 'esig-ok' : 'esig-ko'}>
                            {esignSendResult.sent?.sms?.ok ? `📱 SMS envoyé au ${esignSendResult.tel}` : `📱 SMS non envoyé${esignSendResult.sent?.sms?.error ? ` (${esignSendResult.sent.sms.error})` : ''}`}
                          </span>
                        )}
                        {!esignSendResult.email && !esignSendResult.tel && (
                          <span className="esig-ko">Aucun email ni téléphone renseigné — partagez le lien manuellement.</span>
                        )}
                      </div>
                      <div className="esig-result-link">
                        <input type="text" readOnly value={esignSendResult.url} onFocus={(e) => e.target.select()} />
                        <button type="button" className="btn-icon" title="Copier le lien" onClick={() => copyEsignLink(esignSendResult.url, 'result')}>
                          {esignCopied === 'result' ? '✅' : '🔗'}
                        </button>
                      </div>
                    </div>
                  )}

                  {editingId && formData.numero_or && showEsignForm && (
                    <div className="esig-form">
                      <div className="esig-form-grid">
                        <label className="esig-fld esig-fld-full">
                          <span>Titre du document</span>
                          <input type="text" value={esignTitre} onChange={(e) => setEsignTitre(e.target.value)} placeholder="Ex : Ordre de réparation" />
                        </label>
                        <label className="esig-fld">
                          <span>Nom du client</span>
                          <input type="text" value={esignClientNom} onChange={(e) => setEsignClientNom(e.target.value)} />
                        </label>
                        <label className="esig-fld">
                          <span>Email</span>
                          <input type="email" value={esignClientEmail} onChange={(e) => setEsignClientEmail(e.target.value)} placeholder="pour l'envoi par email" />
                        </label>
                        <label className="esig-fld">
                          <span>Téléphone</span>
                          <input type="tel" value={esignClientTel} onChange={(e) => setEsignClientTel(e.target.value)} placeholder="pour l'envoi par SMS" />
                        </label>
                        <label className="esig-fld esig-fld-file esig-fld-full">
                          <span>Document PDF {esignFile && <em className="esig-filename">— {esignFile.name}</em>}</span>
                          <input type="file" accept="application/pdf" onChange={(e) => setEsignFile(e.target.files?.[0] || null)} />
                        </label>
                      </div>
                      <div className="esig-form-note">Le lien de signature sera envoyé par email et/ou SMS selon les coordonnées renseignées.</div>
                      {esignError && <div className="im-new-error">{esignError}</div>}
                      <div className="esig-form-actions">
                        <button type="button" className="btn-secondary" onClick={() => setShowEsignForm(false)}>Annuler</button>
                        <button type="button" className="btn-primary" disabled={esignSaving} onClick={handleSendEsignature}>
                          {esignSaving ? 'Envoi…' : '✉️ Envoyer au client'}
                        </button>
                      </div>
                    </div>
                  )}

                  {editingId && formData.numero_or && (
                    <div className="table-container">
                      <table className="esig-table">
                        <thead>
                          <tr>
                            <th>Titre</th>
                            <th>Client</th>
                            <th>Statut</th>
                            <th>Créé le</th>
                            <th>Expire le</th>
                            <th>Signé le</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {esignLoading && (
                            <tr><td colSpan="7" className="esig-empty">Chargement...</td></tr>
                          )}
                          {!esignLoading && esignList.length === 0 && (
                            <tr><td colSpan="7" className="esig-empty">Aucune demande de signature pour cet OR</td></tr>
                          )}
                          {!esignLoading && esignList.map(item => {
                            const s = ESIGN_STATUT_LABELS[item.statut] || ESIGN_STATUT_LABELS.en_attente;
                            return (
                              <tr key={item.id}>
                                <td className="esig-td-titre" onClick={() => handleViewEsignDetail(item.id)}>
                                  {item.titre}
                                  <span className="esig-doc-name">{item.document_name}</span>
                                </td>
                                <td>
                                  <div className="esig-client-info">
                                    <span className="esig-client-name">{item.client_nom}</span>
                                    {item.client_email && <span className="esig-client-email">{item.client_email}</span>}
                                  </div>
                                </td>
                                <td>
                                  <span className="esig-badge" style={{ color: s.color, background: s.bg }}>
                                    {s.label}
                                  </span>
                                </td>
                                <td className="esig-td-date">{formatEsignDate(item.created_at)}</td>
                                <td className="esig-td-date">{formatEsignDate(item.expires_at)}</td>
                                <td className="esig-td-date">{formatEsignDate(item.signed_at)}</td>
                                <td className="esig-td-actions">
                                  <button type="button" className="esig-btn-icon" onClick={() => handleViewEsignDetail(item.id)} title="Voir le détail">👁️</button>
                                  {item.statut === 'en_attente' && (
                                    <button type="button" className="esig-btn-icon" onClick={() => copyEsignLink(`${window.location.origin}/signer/${item.token}`, item.id)} title="Copier le lien">
                                      {esignCopied === item.id ? '✅' : '🔗'}
                                    </button>
                                  )}
                                  {(item.statut === 'expire' || item.statut === 'refuse') && (
                                    <button type="button" className="esig-btn-icon" onClick={() => handleRenvoyerEsign(item.id)} title="Regénérer le lien">🔄</button>
                                  )}
                                  {editMode && (
                                    <button type="button" className="esig-btn-icon esig-btn-delete" onClick={() => handleDeleteEsign(item.id)} title="Supprimer">🗑️</button>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {esignDetail && (
                    <div className="modal-overlay">
                      <div className="modal-content small" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                          <h2>📄 {esignDetail.esignature.titre}</h2>
                          <button className="modal-close" onClick={() => setEsignDetail(null)}>✕</button>
                        </div>
                        <div className="modal-body">
                          <div className="esig-detail-grid">
                            <div className="esig-detail-item"><span className="esig-detail-label">Client</span><span>{esignDetail.esignature.client_nom}</span></div>
                            <div className="esig-detail-item"><span className="esig-detail-label">Email</span><span>{esignDetail.esignature.client_email || '—'}</span></div>
                            <div className="esig-detail-item"><span className="esig-detail-label">Téléphone</span><span>{esignDetail.esignature.client_telephone || '—'}</span></div>
                            <div className="esig-detail-item">
                              <span className="esig-detail-label">Statut</span>
                              <span className="esig-badge" style={{
                                color: (ESIGN_STATUT_LABELS[esignDetail.esignature.statut] || {}).color,
                                background: (ESIGN_STATUT_LABELS[esignDetail.esignature.statut] || {}).bg,
                              }}>
                                {(ESIGN_STATUT_LABELS[esignDetail.esignature.statut] || {}).label}
                              </span>
                            </div>
                            <div className="esig-detail-item"><span className="esig-detail-label">Créé le</span><span>{formatEsignDate(esignDetail.esignature.created_at)}</span></div>
                            <div className="esig-detail-item"><span className="esig-detail-label">Expire le</span><span>{formatEsignDate(esignDetail.esignature.expires_at)}</span></div>
                            {esignDetail.esignature.signed_at && (
                              <div className="esig-detail-item"><span className="esig-detail-label">Signé le</span><span>{formatEsignDate(esignDetail.esignature.signed_at)}</span></div>
                            )}
                          </div>
                        </div>
                        <div className="modal-footer">
                          {esignDetail.document_url && (
                            <a className="btn-secondary" href={esignDetail.document_url} target="_blank" rel="noreferrer">📄 Document original</a>
                          )}
                          {esignDetail.signed_document_url && (
                            <a className="btn-primary" href={esignDetail.signed_document_url} target="_blank" rel="noreferrer">✍️ Document signé</a>
                          )}
                          {!esignDetail.signed_document_url && esignDetail.esignature.statut === 'en_attente' && (
                            <button type="button" className="btn-secondary" onClick={() => copyEsignLink(esignDetail.signature_url, 'modal')}>
                              {esignCopied === 'modal' ? '✅ Copié' : '🔗 Copier le lien'}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* ═══ ATELIER ═══ */}
              {modalTab === 'atelier' && (
                <div className="im-section">
                  <div className="im-section-title">🔧 Compte rendu d'atelier</div>
                  <div className="im-cr-aide">
                    Ce qui a réellement été fait sur le véhicule : constats, opérations,
                    points à surveiller. Visible par toute personne ouvrant le dossier.
                  </div>
                  {textesAtelier.length > 0 && peut('dossier.compte_rendu') && (
                    <div className="im-cr-types">
                      {textesAtelier.map(t => (
                        <button key={t.id} type="button" className="im-cr-type"
                          title={t.contenu} onClick={() => insererTexteAtelier(t.contenu)}>
                          {t.libelle}
                        </button>
                      ))}
                    </div>
                  )}
                  <textarea
                    ref={compteRenduRef2}
                    className="im-cr-zone"
                    value={compteRendu}
                    onChange={(e) => { setCompteRendu(e.target.value); setCompteRenduEtat(null); }}
                    disabled={!peut('dossier.compte_rendu')}
                    rows={14}
                  />
                  {peut('dossier.compte_rendu') && (
                    <div className="im-cr-barre">
                      {compteRenduEtat === 'erreur' && (
                        <span className="im-cr-etat im-cr-etat--erreur">Enregistrement impossible</span>
                      )}
                      {compteRenduEtat === 'ok' && compteRendu === compteRenduRef && (
                        <span className="im-cr-etat im-cr-etat--ok">Enregistré</span>
                      )}
                      <button type="button" className="btn-primary"
                        onClick={enregistrerCompteRendu}
                        disabled={compteRenduEtat === 'saving' || compteRendu === compteRenduRef}>
                        {compteRenduEtat === 'saving' ? 'Enregistrement…' : 'Enregistrer le compte rendu'}
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* ═══ PHOTOS ═══ */}
              {modalTab === 'photos' && (
                <div className="im-section">
                  {!formData.numero_or ? (
                    <div className="im-empty"><div className="im-empty-icon">📷</div>Saisissez un N° OR pour voir les photos</div>
                  ) : (
                    <>
                      {['reception', 'restitution'].map(type => {
                        const dossiers = tourVehiculeDossiers.filter(d => d.type === type);
                        const nbMedias = dossiers.reduce((n, d) => n + (d.photo_ids?.length || 0), 0);
                        const label = type === 'reception' ? '📥 Photos Tour du véhicule — Réception' : '📤 Photos Tour du véhicule — Restitution';
                        return (
                          <details key={type} style={{ marginBottom: 12 }}>
                            <summary style={{ cursor: 'pointer', fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', padding: '4px 0' }}>
                              {label} {dossiers.length > 0 && <span style={{ fontWeight: 400 }}>({dossiers.reduce((n, d) => n + (d.photo_ids?.length || 0), 0)})</span>}
                            </summary>
                            <div className="im-media-actions">
                              <button type="button" className="im-media-btn"
                                disabled={nbMedias === 0 || zipEnCours === type}
                                onClick={() => telechargerZip(type)}
                                title={nbMedias === 0 ? 'Aucun média' : `Télécharger les ${nbMedias} média${nbMedias > 1 ? 's' : ''} en ZIP`}>
                                {zipEnCours === type ? '⏳ Préparation…' : '📦 Télécharger (ZIP)'}
                              </button>
                              {peut('client.photos_partager') && (
                                <button type="button" className="im-media-btn"
                                  disabled={nbMedias === 0}
                                  onClick={() => ouvrirPartage(type)}
                                  title={nbMedias === 0 ? 'Aucun média' : 'Envoyer une sélection par e-mail'}>
                                  ✉️ Partager par mail
                                </button>
                              )}
                              {zipErreur?.rubrique === type && (
                                <span className="im-media-erreur">{zipErreur.msg}</span>
                              )}
                            </div>
                            {dossiers.length === 0 ? (
                              <div className="im-empty" style={{ padding: '8px 0' }}>Aucun dossier {type === 'reception' ? 'de réception' : 'de restitution'} pour cet OR</div>
                            ) : dossiers.map(dossier => (
                              <div key={dossier.id} style={{ margin: '8px 0' }}
                                onMouseEnter={() => loadTourVehiculeDossierPhotos(dossier.id, dossier.photo_ids)}>
                                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>
                                  {new Date(dossier.created_at).toLocaleDateString('fr-FR')} · {dossier.agent || '—'}
                                  {dossier.kilometrage != null ? ` · ${dossier.kilometrage} km` : ''}
                                </div>
                                <div className="atelier-photos-grid">
                                  {(dossier.photo_ids || []).map(photoId => {
                                    const key = `${dossier.id}__${photoId}`;
                                    const data = tourVehiculePhotoCache[key];
                                    return (
                                      <div key={photoId} className="atelier-photo-thumb">
                                        {data ? (
                                          <img src={`data:image/jpeg;base64,${data}`} alt="" loading="lazy"
                                            onClick={() => ouvrirLightbox(
                                              (dossier.photo_ids || []).map(pid => ({
                                                cle: `${dossier.id}__${pid}`,
                                                legende: `${new Date(dossier.created_at).toLocaleDateString('fr-FR')} · ${dossier.agent || '—'}`,
                                              })),
                                              (dossier.photo_ids || []).indexOf(photoId),
                                            )} />
                                        ) : (
                                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', fontSize: 20 }}>
                                            {data === false ? '⚠️' : '⏳'}
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            ))}
                          </details>
                        );
                      })}

                      <details open style={{ marginBottom: 12 }}>
                        <summary style={{ cursor: 'pointer', fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', padding: '4px 0' }}>
                          🔧 Photos & vidéos Atelier {atelierPhotos.length > 0 && <span style={{ fontWeight: 400 }}>({atelierPhotos.length})</span>}
                        </summary>
                            <div className="im-media-actions">
                              <button type="button" className="im-media-btn"
                                disabled={atelierPhotos.length === 0 || zipEnCours === 'atelier'}
                                onClick={() => telechargerZip('atelier')}
                                title={atelierPhotos.length === 0 ? 'Aucun média' : `Télécharger les ${atelierPhotos.length} média${atelierPhotos.length > 1 ? 's' : ''} en ZIP`}>
                                {zipEnCours === 'atelier' ? '⏳ Préparation…' : '📦 Télécharger (ZIP)'}
                              </button>
                              {peut('client.photos_partager') && (
                                <button type="button" className="im-media-btn"
                                  disabled={atelierPhotos.length === 0}
                                  onClick={() => ouvrirPartage('atelier')}
                                  title={atelierPhotos.length === 0 ? 'Aucun média' : 'Envoyer une sélection par e-mail'}>
                                  ✉️ Partager par mail
                                </button>
                              )}
                              {zipErreur?.rubrique === 'atelier' && (
                                <span className="im-media-erreur">{zipErreur.msg}</span>
                              )}
                            </div>
                        {atelierPhotos.length === 0 ? (
                          <div className="im-empty" style={{ padding: '8px 0' }}>Aucun média pour cet OR</div>
                        ) : (
                          <div className="atelier-photos-grid">
                            {atelierPhotos.map((photo, iPhoto) => {
                              const estVideo = (photo.content_type || '').startsWith('video/');
                              const duree = photo.duration_seconds != null
                                ? `${Math.floor(photo.duration_seconds / 60)}:${String(photo.duration_seconds % 60).padStart(2, '0')}`
                                : null;
                              return (
                                <div key={photo.id} className="atelier-photo-thumb">
                                  {editMode && user?.role === 'super-admin' && (
                                    <button type="button" className="atelier-photo-delete" title="Supprimer"
                                      onClick={async (e) => { e.stopPropagation(); if (!window.confirm('Supprimer ce média ?')) return; try { await api.delete(`/atelier-photos/${photo.id}`); setAtelierPhotos(prev => prev.filter(p => p.id !== photo.id)); } catch (err) { console.error(err); } }}
                                    >✕</button>
                                  )}
                                  {estVideo ? (
                                    // preload="metadata" affiche la 1re frame — pas de miniature serveur
                                    <div className="atelier-video-thumb" onClick={() => ouvrirLightbox(atelierPhotos.map(m => ({ photo: m })), iPhoto)}>
                                      <video src={photo.url} preload="metadata" muted playsInline />
                                      <span className="atelier-video-play">▶</span>
                                      {duree && <span className="atelier-video-duree">{duree}</span>}
                                    </div>
                                  ) : (
                                    <img src={photo.url} alt={photo.filename || ''} loading="lazy"
                                      onClick={() => ouvrirLightbox(atelierPhotos.map(m => ({ photo: m })), iPhoto)} />
                                  )}
                                  <div className="atelier-photo-meta">{photo.created_by || '—'} · {new Date(photo.created_at).toLocaleDateString('fr-FR')}</div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </details>

                      {partage && (() => {
                        const LABELS = {
                          reception: 'Photos Tour du véhicule — Réception',
                          restitution: 'Photos Tour du véhicule — Restitution',
                          atelier: 'Photos & vidéos Atelier',
                        };
                        const medias = partage.medias || [];
                        const choisis = medias.filter(m => partage.selection.has(m.cle));
                        const photos = choisis.filter(m => !m.estVideo);
                        const videos = choisis.filter(m => m.estVideo);
                        const poids = photos.reduce((n, m) => n + (m.taille || 0), 0);
                        const plafond = partage.plafond || 9 * 1024 * 1024;
                        const tropLourd = poids > plafond;
                        const mo = (o) => `${(o / 1024 / 1024).toFixed(1)} Mo`;
                        const fermer = () => setPartage(null);
                        return (
                          <div className="modal-overlay">
                            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                              <div className="modal-header">
                                <h2>✉️ Partager — {LABELS[partage.rubrique]}</h2>
                                <button type="button" className="modal-close" onClick={fermer}>✕</button>
                              </div>
                              <div className="modal-body">
                                {partage.etat === 'ok' ? (
                                  <div className="im-partage-fini">
                                    <div className="im-partage-fini-icone">✅</div>
                                    <p>
                                      Envoyé à <strong>{(partage.resultat?.destinataires || []).join(', ')}</strong>
                                      {' — '}{partage.resultat?.pieces_jointes || 0} photo{(partage.resultat?.pieces_jointes || 0) > 1 ? 's' : ''} en pièce jointe
                                      {partage.resultat?.liens > 0 && `, ${partage.resultat.liens} vidéo${partage.resultat.liens > 1 ? 's' : ''} par lien`}.
                                    </p>
                                  </div>
                                ) : medias === null || partage.medias === null ? (
                                  <div className="im-empty">Chargement…</div>
                                ) : medias.length === 0 ? (
                                  <div className="im-empty">Aucun média dans cette rubrique</div>
                                ) : (
                                  <>
                                    <div className="im-partage-entete">
                                      <span>Cochez les médias à transmettre</span>
                                      <button type="button" className="im-media-btn"
                                        onClick={() => setPartage(p => ({
                                          ...p,
                                          selection: p.selection.size === medias.length
                                            ? new Set()
                                            : new Set(medias.map(m => m.cle)),
                                        }))}>
                                        {partage.selection.size === medias.length ? 'Tout décocher' : 'Tout cocher'}
                                      </button>
                                    </div>
                                    <div className="im-partage-grille">
                                      {medias.map(m => {
                                        // Vignette : URL signée côté Atelier, cache local
                                        // côté Tour du véhicule (base64 déjà chargé).
                                        const cache = m.dossier_id != null
                                          ? tourVehiculePhotoCache[`${m.dossier_id}__${m.photo_id}`] : null;
                                        const src = m.vignette || (cache ? `data:image/jpeg;base64,${cache}` : null);
                                        const coche = partage.selection.has(m.cle);
                                        return (
                                          <label key={m.cle} className={`im-partage-item${coche ? ' coche' : ''}`}>
                                            <input type="checkbox" checked={coche}
                                              onChange={() => basculerSelection(m.cle)} />
                                            <div className="im-partage-vignette">
                                              {m.estVideo
                                                ? <><video src={m.vignette} preload="metadata" muted playsInline /><span className="atelier-video-play">▶</span></>
                                                : (src ? <img src={src} alt="" loading="lazy" /> : <span className="im-partage-sans-image">⏳</span>)}
                                            </div>
                                            <div className="im-partage-nom">{m.nom}</div>
                                            <div className="im-partage-legende">
                                              {m.estVideo ? 'vidéo — par lien' : (m.taille ? mo(m.taille) : '—')}
                                            </div>
                                          </label>
                                        );
                                      })}
                                    </div>

                                    <div className="im-partage-champ">
                                      <label>Destinataires</label>
                                      <input type="text" value={partage.destinataires}
                                        placeholder="client@exemple.fr, expert@assurance.fr"
                                        onChange={(e) => setPartage(p => ({ ...p, destinataires: e.target.value, erreur: null }))} />
                                      <small>Plusieurs adresses séparées par une virgule.</small>
                                    </div>
                                    <div className="im-partage-champ">
                                      <label>Message (facultatif)</label>
                                      <textarea rows={3} value={partage.message}
                                        placeholder="Bonjour, voici les photos de votre véhicule…"
                                        onChange={(e) => setPartage(p => ({ ...p, message: e.target.value }))} />
                                    </div>
                                    {/* Règle dite une fois, en clair : une vidéo d'atelier
                                        pèse à elle seule plus que le plafond d'un e-mail. */}
                                    <div className={`im-partage-bilan${tropLourd ? ' trop-lourd' : ''}`}>
                                      {choisis.length === 0 ? 'Aucun média sélectionné.' : (
                                        <>
                                          {photos.length > 0 && <>{photos.length} photo{photos.length > 1 ? 's' : ''} en pièce jointe ({mo(poids)}){videos.length > 0 && ' · '}</>}
                                          {videos.length > 0 && <>{videos.length} vidéo{videos.length > 1 ? 's' : ''} par lien de téléchargement (7 jours)</>}
                                          {tropLourd && ` — au-delà de ${mo(plafond)}, l'envoi sera refusé : retirez des photos ou envoyez-les en deux fois.`}
                                        </>
                                      )}
                                    </div>
                                    {partage.erreur && <div className="im-partage-erreur">{partage.erreur}</div>}
                                  </>
                                )}
                              </div>
                              <div className="modal-footer">
                                <button type="button" className="btn-secondary" onClick={fermer}>
                                  {partage.etat === 'ok' ? 'Fermer' : 'Annuler'}
                                </button>
                                {partage.etat !== 'ok' && (
                                  <button type="button" className="btn-primary"
                                    disabled={partage.etat === 'envoi' || choisis.length === 0
                                      || !partage.destinataires.trim() || tropLourd}
                                    onClick={envoyerPartage}>
                                    {partage.etat === 'envoi' ? 'Envoi…' : `Envoyer${choisis.length ? ` (${choisis.length})` : ''}`}
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })()}
                    </>
                  )}
                </div>
              )}

              {/* ═══ ÉCHANGES CLIENTS ═══
                  Fermé sans `client.echanges_voir` : l'onglet n'existe pas dans la barre
                  latérale, et le serveur refuse la route de toute façon. */}
              {modalTab === 'echanges' && peut('client.echanges_voir') && (
                <div className="ec">
                  {!editingId ? (
                    <div className="ec-vide">Enregistrez d'abord le dossier pour y consigner les échanges.</div>
                  ) : (
                    <>
                      <div className="ec-canaux">
                        {(peut('client.echanges_saisir') ? CANAUX : []).map(c => (
                          <button
                            key={`${c.id}-${c.sens}`}
                            type="button"
                            className="ec-canal"
                            disabled={lectureSeule}
                            onClick={() => ouvrirBrouillon(c.id, c.sens)}
                          >
                            <span className="ec-canal-ic">{c.icone}</span>{c.label}
                          </button>
                        ))}
                        {/* Envoyer POUR DE VRAI, et pas seulement consigner :
                            c'est la différence avec les boutons de gauche, qui
                            ouvrent une note. Grisé sans numéro de mobile — la
                            règle est la même que partout ailleurs. */}
                        {peut('client.sms_envoyer') && <button
                          type="button"
                          className="ec-canal ec-canal--envoi"
                          disabled={lectureSeule || !estMobileEnvoyable(formData.client_tel)}
                          title={estMobileEnvoyable(formData.client_tel)
                            ? `Envoyer un SMS au ${formData.client_tel}`
                            : 'Aucun numéro de mobile valide sur ce dossier'}
                          onClick={ouvrirComposeurSms}
                        >
                          <span className="ec-canal-ic">📲</span>Envoyer un SMS
                        </button>}
                      </div>
                      {smsEchange && (
                        <div className="ec-sms">
                          {/* Modèles de la concession. Le bouton REMPLACE le
                              contenu : un SMS fait 160 caractères, on n'en
                              empile pas deux — c'est l'inverse du compte rendu
                              d'atelier, où le texte s'insère au curseur. */}
                          {modelesSms === null ? (
                            <div className="ec-sms-modeles ec-sms-modeles--vide">Chargement des modèles…</div>
                          ) : modelesSms.length > 0 && (
                            <div className="ec-sms-modeles">
                              {modelesSms.map(m => (
                                <button key={m.id} type="button" className="ec-sms-modele"
                                  title={m.contenu}
                                  onClick={() => setSmsEchange(x => ({ ...x, message: m.contenu, envoi: null }))}>
                                  {m.libelle}
                                </button>
                              ))}
                            </div>
                          )}
                          <textarea
                            className="ec-saisie"
                            autoFocus
                            rows={3}
                            maxLength={320}
                            value={smsEchange.message}
                            placeholder={`SMS à ${formData.client_tel}…`}
                            onChange={(ev) => setSmsEchange(m => ({ ...m, message: ev.target.value, envoi: null }))}
                          />
                          <div className="ec-sms-pied">
                            <span className={`ec-sms-compte${smsEchange.message.length > 160 ? ' is-long' : ''}`}>
                              {smsEchange.message.length}/160
                              {smsEchange.message.length > 160 && ' — partira en plusieurs SMS'}
                            </span>
                            {smsEchange.envoi && <span className="ec-sms-erreur">{smsEchange.envoi}</span>}
                            <button type="button" className="btn-secondary" onClick={() => setSmsEchange(null)} disabled={smsEchangeBusy}>Annuler</button>
                            <button type="button" className="btn-primary" onClick={envoyerSmsEchange}
                              disabled={smsEchangeBusy || !smsEchange.message.trim()}>
                              {smsEchangeBusy ? 'Envoi…' : 'Envoyer'}
                            </button>
                          </div>
                        </div>
                      )}
                      {/* Le brouillon : visible, daté, mais PAS encore écrit.
                          Il disparaît sans laisser de trace si on l'annule. */}
                      {brouillonEchange && (() => {
                        const c = CANAUX.find(x => x.id === brouillonEchange.canal && x.sens === brouillonEchange.sens);
                        return (
                          <div className="ec-brouillon">
                            <div className="ec-brouillon-entete">
                              <span className="ec-brouillon-canal">
                                {c?.icone} {c?.label || brouillonEchange.canal}
                              </span>
                              <span className="ec-quand">{libelleQuand(brouillonEchange.quand)}</span>
                            </div>
                            <textarea
                              className="ec-saisie"
                              autoFocus
                              rows={2}
                              value={brouillonEchange.resume}
                              placeholder="Ce qui s'est dit… (facultatif)"
                              onChange={(ev) => setBrouillonEchange(b => ({ ...b, resume: ev.target.value }))}
                              onKeyDown={(ev) => {
                                if (ev.key === 'Enter' && (ev.ctrlKey || ev.metaKey)) { ev.preventDefault(); enregistrerBrouillon(); }
                                if (ev.key === 'Escape') { ev.preventDefault(); setBrouillonEchange(null); }
                              }}
                            />
                            <div className="ec-brouillon-pied">
                              <span className="ec-brouillon-note">
                                Rien n'est enregistré tant que vous n'avez pas validé. L'heure retenue est celle du clic.
                              </span>
                              <button type="button" className="btn-secondary" disabled={brouillonEchange.busy}
                                onClick={() => setBrouillonEchange(null)}>Annuler</button>
                              <button type="button" className="btn-primary" disabled={brouillonEchange.busy}
                                onClick={enregistrerBrouillon}>
                                {brouillonEchange.busy ? 'Enregistrement…' : 'Enregistrer'}
                              </button>
                            </div>
                          </div>
                        );
                      })()}
                      <div className="ec-aide">
                        Un clic sur le canal ouvre un brouillon, daté de maintenant : rien n'est
                        enregistré tant que vous n'avez pas validé.
                        Le bouton <b>Envoyer un SMS</b>, lui, l'envoie réellement au client.
                      </div>

                      {echangesCharges && echanges.length === 0 && (
                        <div className="ec-vide">Aucun échange enregistré sur ce dossier.</div>
                      )}

                      {echanges.map(e => {
                        const res = RESULTATS.find(r => r.id === e.resultat);
                        // ⚠️ Une ligne venue de `sms_history` ou `email_history`
                        // est un ENVOI RÉEL, pas une note : elle appartient à sa
                        // table d'origine et ne se corrige pas ici. La rendre
                        // éditable laisserait croire qu'on réécrit un message
                        // déjà parti chez le client.
                        const envoye = e.origine === 'sms' || e.origine === 'email';
                        const enEdition = echangeEdite === e.id && !envoye;
                        return (
                          <div
                            key={e.id}
                            className={`ec-ligne${echangeDrag === e.id ? ' ec-ligne--drag' : ''}${envoye ? ' ec-ligne--envoi' : ''}`}
                            onDragOver={(ev) => { ev.preventDefault(); setEchangeDrag(e.id); }}
                            onDragLeave={() => setEchangeDrag(null)}
                            onDrop={(ev) => { ev.preventDefault(); setEchangeDrag(null); joindreAEchange(e.id, ev.dataTransfer.files); }}
                          >
                            <div className={`ec-pastille ec-pastille--${e.sens}`}>
                              {ICONE_CANAL[e.canal] || '💬'}
                              <span className="ec-fleche">{e.sens === 'entrant' ? '↙' : '↗'}</span>
                            </div>

                            <div className="ec-corps">
                              <div className="ec-entete">
                                <span className="ec-quand">{libelleQuand(e.contact_le)}</span>
                                <span className="ec-qui">· {e.user_label || e.auteur_courant || '—'}</span>
                                {envoye && (
                                  <span className="ec-envoi-tag"
                                    title={`Envoyé depuis ce dossier · ${e.destinataire || ''}`}>
                                    {e.origine === 'sms' ? 'SMS envoyé' : 'E-mail envoyé'}
                                  </span>
                                )}
                                {e.resultat === 'echec' && (
                                  <span className="ec-envoi-echec" title={e.erreur || 'Échec'}>échec</span>
                                )}
                                {e.canal === 'appel' && (
                                  <select
                                    className={`ec-resultat${res ? ` ec-resultat--${res.ton}` : ''}`}
                                    value={e.resultat || ''}
                                    disabled={lectureSeule}
                                    onChange={(ev) => majEchange(e.id, { resultat: ev.target.value })}
                                  >
                                    <option value="">Résultat…</option>
                                    {RESULTATS.map(r => <option key={r.id} value={r.id}>{r.label}</option>)}
                                  </select>
                                )}
                                {e.a_rappeler_le && (
                                  <span className="ec-rappel">
                                    ⏰ Rappeler le {new Date(e.a_rappeler_le).toLocaleDateString('fr-FR')}
                                  </span>
                                )}
                              </div>

                              {enEdition ? (
                                <>
                                  <textarea
                                    className="ec-saisie"
                                    autoFocus
                                    defaultValue={e.resume || ''}
                                    placeholder="Ce qui s'est dit…"
                                    onBlur={(ev) => { setEchangeEdite(null); majEchange(e.id, { resume: ev.target.value }); }}
                                    onKeyDown={(ev) => {
                                      if (ev.key === 'Enter' && (ev.ctrlKey || ev.metaKey)) { ev.preventDefault(); ev.target.blur(); }
                                      if (ev.key === 'Escape') { ev.preventDefault(); setEchangeEdite(null); }
                                    }}
                                  />
                                  <div className="ec-consigne">
                                    S'enregistre quand vous quittez le champ, ou avec <b>Ctrl + Entrée</b>. Jamais pendant la frappe.
                                  </div>
                                </>
                              ) : (
                                <div
                                  className={`ec-texte${e.resume ? '' : ' ec-texte--vide'}`}
                                  onClick={() => !lectureSeule && !envoye && setEchangeEdite(e.id)}
                                  title={envoye ? 'Message envoyé — non modifiable' : (lectureSeule ? undefined : 'Cliquer pour corriger')}
                                >
                                  {e.resume || (envoye ? '' : 'Cliquez pour écrire ce qui s\'est dit…')}
                                </div>
                              )}

                              {(e.documents || []).map(d => (
                                <a
                                  key={d.id}
                                  className="ec-pj"
                                  href={d.url || undefined}
                                  target="_blank" rel="noreferrer"
                                  onClick={(ev) => ev.stopPropagation()}
                                >📎 {d.nom}</a>
                              ))}
                            </div>

                            {!lectureSeule && (
                              <div className="ec-outils">
                                <button type="button" className="ec-outil" title="Corriger le texte"
                                        onClick={() => setEchangeEdite(e.id)}>✏️</button>
                                <label className="ec-outil" title="Joindre un document" style={{ cursor: 'pointer' }}>
                                  📎
                                  <input type="file" multiple style={{ display: 'none' }}
                                         onChange={(ev) => { joindreAEchange(e.id, ev.target.files); ev.target.value = ''; }} />
                                </label>
                                <button type="button" className="ec-outil" title="Programmer un rappel"
                                  onClick={() => {
                                    const v = window.prompt('Rappeler le (JJ/MM/AAAA) — vide pour retirer :',
                                      e.a_rappeler_le ? new Date(e.a_rappeler_le).toLocaleDateString('fr-FR') : '');
                                    if (v === null) return;
                                    const t = v.trim();
                                    if (!t) { majEchange(e.id, { a_rappeler_le: '' }); return; }
                                    const m = t.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
                                    if (!m) { window.alert('Format attendu : JJ/MM/AAAA'); return; }
                                    majEchange(e.id, { a_rappeler_le: `${m[3]}-${m[2]}-${m[1]}` });
                                  }}>⏰</button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </>
                  )}
                </div>
              )}

              {/* ═══ DOCUMENTS ═══ */}
              {modalTab === 'documents' && !editingId && (
                <div className="im-empty"><div className="im-empty-icon">📎</div>Enregistrez le dossier pour ajouter des documents.</div>
              )}
              {modalTab === 'documents' && editingId && (
                <div className="im-section">
                  <div className="im-section-title">Documents</div>
                  <div className="planning-docs-grid">
                    {planningDocs.map(doc => {
                      const mime = doc.type_mime || '';
                      const isImage = mime.startsWith('image/');
                      const isPdf = mime === 'application/pdf';
                      const isRenaming = renamingDocId === doc.id;
                      return (
                        <div key={doc.id} className="planning-doc-thumb">
                          <button type="button" className="planning-doc-delete" title="Supprimer"
                            onClick={async (e) => { e.stopPropagation(); if (!window.confirm('Supprimer ce document ?')) return; try { await planningDocsApi.delete(editingId.interventionId, doc.id); setPlanningDocs(prev => prev.filter(d => d.id !== doc.id)); } catch (err) { console.error(err); } }}
                          >✕</button>
                          <div className="planning-doc-preview" onClick={() => setDocViewer({ url: doc.url, type: mime, name: doc.nom })}>
                            {isImage ? <img src={doc.url} alt={doc.nom} loading="lazy" /> : isPdf ? <PdfThumbnail url={doc.url} /> : <div className="planning-doc-icon">📎</div>}
                          </div>
                          <div className="planning-doc-name" onDoubleClick={() => setRenamingDocId(doc.id)}>
                            {isRenaming ? (
                              <input type="text" className="planning-doc-rename-input" defaultValue={doc.nom} autoFocus
                                onBlur={async (e) => { const newName = e.target.value.trim(); setRenamingDocId(null); if (newName && newName !== doc.nom) { try { await planningDocsApi.rename(editingId.interventionId, doc.id, newName); setPlanningDocs(prev => prev.map(d => d.id === doc.id ? { ...d, nom: newName } : d)); } catch (err) { console.error(err); } } }}
                                onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); if (e.key === 'Escape') setRenamingDocId(null); }}
                              />
                            ) : <span title="Double-clic pour renommer">{doc.nom}</span>}
                          </div>
                        </div>
                      );
                    })}
                    <div className={`planning-doc-dropzone${docDragging ? ' dragging' : ''}`}
                      onDragOver={(e) => { e.preventDefault(); setDocDragging(true); }} onDragLeave={() => setDocDragging(false)}
                      onDrop={async (e) => { e.preventDefault(); setDocDragging(false); const files = Array.from(e.dataTransfer.files); for (const file of files) { try { if (file.type === 'application/pdf') { const fd = new FormData(); fd.append('file', file); const r = await planningDocsApi.uploadFile(editingId.interventionId, fd); setPlanningDocs(prev => [...prev, r.data.document]); } else if (file.type.startsWith('image/')) { const compressed = await new Promise((resolve) => { const reader = new FileReader(); reader.onload = (ev) => { const img = new Image(); img.onload = () => { const MAX = 1600; let w = img.width, h = img.height; if (w > MAX || h > MAX) { const ratio = Math.min(MAX / w, MAX / h); w = Math.round(w * ratio); h = Math.round(h * ratio); } const canvas = document.createElement('canvas'); canvas.width = w; canvas.height = h; canvas.getContext('2d').drawImage(img, 0, 0, w, h); canvas.toBlob(resolve, 'image/jpeg', 0.82); }; img.src = ev.target.result; }; reader.readAsDataURL(file); }); const blob = compressed || file; const { data: presignData } = await planningDocsApi.presign(editingId.interventionId, { filename: file.name, contentType: 'image/jpeg' }); await fetch(presignData.uploadUrl, { method: 'PUT', body: blob, headers: { 'Content-Type': 'image/jpeg' } }); const { data: confirmData } = await planningDocsApi.confirm(editingId.interventionId, { key: presignData.key, filename: file.name, contentType: 'image/jpeg', size: blob.size }); setPlanningDocs(prev => [...prev, confirmData.document]); } else { const { data: presignData } = await planningDocsApi.presign(editingId.interventionId, { filename: file.name, contentType: file.type }); await fetch(presignData.uploadUrl, { method: 'PUT', body: file, headers: { 'Content-Type': file.type } }); const { data: confirmData } = await planningDocsApi.confirm(editingId.interventionId, { key: presignData.key, filename: file.name, contentType: file.type, size: file.size }); setPlanningDocs(prev => [...prev, confirmData.document]); } } catch (err) { console.error('[doc upload]', err); alert(`Erreur upload : ${file.name}`); } } }}
                      onClick={() => document.getElementById('intervention-modal-doc-file-input')?.click()}
                    >
                      <span>📎 Glisser des fichiers ici</span>
                      <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>ou cliquer pour parcourir</span>
                    </div>
                    <input id="intervention-modal-doc-file-input" type="file" multiple accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.msg" style={{ display: 'none' }}
                      onChange={async (e) => { const files = Array.from(e.target.files || []); for (const file of files) { try { if (file.type === 'application/pdf') { const fd = new FormData(); fd.append('file', file); const r = await planningDocsApi.uploadFile(editingId.interventionId, fd); setPlanningDocs(prev => [...prev, r.data.document]); } else { const { data: presignData } = await planningDocsApi.presign(editingId.interventionId, { filename: file.name, contentType: file.type }); await fetch(presignData.uploadUrl, { method: 'PUT', body: file, headers: { 'Content-Type': file.type } }); const { data: confirmData } = await planningDocsApi.confirm(editingId.interventionId, { key: presignData.key, filename: file.name, contentType: file.type, size: file.size }); setPlanningDocs(prev => [...prev, confirmData.document]); } } catch (err) { console.error('[doc upload]', err); } } e.target.value = ''; }}
                    />
                  </div>
                </div>
              )}

            </fieldset>{/* fin im-body */}

            {/* ═══ ANALYSE IA (super-admin) ═══
                Hors du <fieldset disabled> : le chat doit rester utilisable en
                mode consultation, il ne modifie pas le dossier. */}
            {modalTab === 'analyse-ia' && analyseIaVisible && (
              <div className="im-body im-body--analyse-ia">
                <AnalyseIADossier interventionId={editingId.interventionId} numeroOr={formData.numero_or} />
              </div>
            )}
            </div>{/* fin im-main */}
          </div>{/* fin im-body-row */}

          {/* ── Save error (pleine largeur) ── */}
          {saveError && <div className="im-save-error">{saveError}</div>}

          {/* ── Historique panel (pleine largeur) ── */}
          {showHistorique && editingId && (
            <div className="im-history-panel">
              <div className="im-history-title">Historique des modifications</div>
              {interventionLogs.length === 0 ? <div style={{ color: 'var(--text-muted)' }}>Aucun historique</div> : (
                <>
                  {logsToShow.map(log => (
                    <div key={log.id} className="im-history-row">
                      <span className="im-history-date">{new Date(log.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })} {new Date(log.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</span>
                      <span><strong>{log.user_name}</strong> — {log.action}{log.details && <span style={{ color: 'var(--text-secondary)', marginLeft: 4 }}>{log.details}</span>}</span>
                    </div>
                  ))}
                  {interventionLogs.length > 5 && <button type="button" className="im-history-more" onClick={() => setShowAllLogs(!showAllLogs)}>{showAllLogs ? 'Voir moins' : `Voir tout (${interventionLogs.length})`}</button>}
                </>
              )}
            </div>
          )}

          {/* ── Footer (pleine largeur) ──
              En consultation, seuls l'historique et Fermer subsistent : proposer
              « Enregistrer » à quelqu'un qui recevra un 403 serait trompeur. */}
          {/* Barre refondue (2026-08-15, demande utilisateur) : elle mélangeait
              quatre familles sans hiérarchie. Désormais UNE lecture — à gauche
              les outils du dossier en icônes (infobulles), à droite les seuls
              enregistrements. Retirés : la date de dernière modification (dans
              l'infobulle de l'historique, et en tête du panneau ouvert), le
              rappel « Esc fermer » et le bouton Fermer — la croix ✕ du bandeau
              fait ce travail, Échap fonctionne toujours. */}
          <div className={`im-footer${lectureSeule ? ' im-footer--lecture' : ''}`}>
            {editingId && (
              <div className="im-footer-tools">
                <button
                  type="button"
                  className={`btn-icon${showHistorique ? ' active' : ''}`}
                  title={`Historique des modifications${interventionLogs.length > 0
                    ? ` — dernière le ${new Date(interventionLogs[0].created_at).toLocaleDateString('fr-FR')} à ${new Date(interventionLogs[0].created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`
                    : ''}`}
                  onClick={() => setShowHistorique(!showHistorique)}
                >
                  🕐
                  {interventionLogs.length > 0 && <span className="im-footer-tool-badge">{interventionLogs.length}</span>}
                </button>
                {/* Impression de documents types (confirmation de RDV…). Toujours
                    le MENU, jamais d'impression directe (décision utilisateur) :
                    cliquer un document ouvre son aperçu, l'impression se fait là. */}
                {peut('dossier.document_imprimer') && <div className="im-print-wrap">
                  <button
                    type="button"
                    className={`btn-icon${printMenu.open ? ' active' : ''}`}
                    title="Imprimer un document pour le client"
                    onClick={ouvrirMenuImpression}
                  >
                    🖨️
                  </button>
                  {/* Voile invisible : cliquer n'importe où ailleurs referme le
                      menu — sans lui, il restait ouvert jusqu'à re-clic sur 🖨️. */}
                  {printMenu.open && (
                    <div className="im-print-backdrop" onClick={() => setPrintMenu(p => ({ ...p, open: false }))} />
                  )}
                  {printMenu.open && (
                    <div className="im-print-menu">
                      {printMenu.chargement && <div className="im-print-menu-vide">Chargement…</div>}
                      {!printMenu.chargement && printMenu.documents.length === 0 && (
                        <div className="im-print-menu-vide">
                          Aucun document configuré.<br />
                          <span style={{ fontSize: 11 }}>Configuration Planning › 📄 Documents</span>
                        </div>
                      )}
                      {printMenu.documents.map(d => (
                        <button key={d.id} type="button" className="im-print-menu-item" onClick={() => ouvrirApercuDocument(d)}>
                          📄 {d.libelle}
                        </button>
                      ))}
                    </div>
                  )}
                </div>}
                {onOuvrirPlanning && (
                  <button
                    type="button"
                    className="btn-icon"
                    title="Voir ce dossier dans la grille du Planning Atelier"
                    onClick={() => onOuvrirPlanning(editingId.interventionId)}
                  >
                    📅
                  </button>
                )}
                {/* La suppression reste à un clic mais s'écarte des outils de
                    consultation : un filet la sépare, et c'est la seule icône
                    au fond rouge au survol (btn-icon delete, classe partagée). */}
                {editMode && !lectureSeule && (
                  <>
                    <span className="im-footer-tools-sep" />
                    <button type="button" className="btn-icon delete" title="Supprimer l'intervention" onClick={handleDeleteIntervention}>🗑️</button>
                  </>
                )}
              </div>
            )}
            <div className="im-footer-spacer" />
            {pushResult && (
              <span className="im-footer-pushresult" style={{ color: pushResult.type === 'success' ? '#166534' : '#991b1b' }}>
                {pushResult.message}
              </span>
            )}
            {!isConfirmMode && !lectureSeule && !formData.nextlane_appointment_id && (
              <button
                type="button"
                className="im-footer-nextlane"
                disabled={!nextlaneReady || pushingToNextlane}
                onClick={handlePushToNextlane}
                title={nextlaneReady
                  ? 'Enregistre le dossier puis crée le RDV atelier dans le DMS Datacar'
                  : `Renseignez d'abord ${nextlaneMissing.join(', ')}`}
              >
                {/* « le RDV », pas « l'OR » : le bouton crée bien un rendez-vous
                    atelier dans le DMS, comme le disait déjà son infobulle. */}
                {pushingToNextlane ? 'Envoi vers Datacar…' : 'Enregistrer et créer le RDV dans Datacar'}
              </button>
            )}
            {lectureSeule ? (
              <span className="im-footer-lecture" title="Votre accès au Planning est en consultation">👁️ Consultation</span>
            ) : (
              <button type="submit" className="im-comment-send" style={{ padding: '6px 18px' }}>
                {isConfirmMode ? '✅ Confirmer le rendez-vous' : (editingId ? 'Enregistrer' : 'Créer le RDV')}
              </button>
            )}
          </div>
        </form>
      </div>

      {/* ── Aperçu d'un document imprimable ──
          Le rendu vient du serveur (page HTML autonome) et s'affiche dans une
          iframe `srcDoc` : l'API s'authentifie par header JWT, un simple
          window.open(url) partirait sans jeton. L'impression se déclenche sur
          le contenu de l'iframe — le document sort seul, pas l'écran. */}
      {docApercu && (
        <div className="im-lightbox" onClick={(e) => { e.stopPropagation(); setDocApercu(null); }}>
          <div className="im-docprint" onClick={(e) => e.stopPropagation()}>
            <div className="im-docprint-head">
              <span className="im-docprint-titre">📄 {docApercu.libelle}</span>
              <div className="im-docprint-actions">
                <button type="button" className="btn-secondary" onClick={() => setDocApercu(null)}>Fermer</button>
                <button type="button" className="btn-primary" onClick={imprimerDocApercu}>🖨️ Imprimer</button>
              </div>
            </div>
            {docApercu.chargement
              ? <div className="im-docprint-attente">Préparation du document…</div>
              : <iframe ref={docApercuFrameRef} title="Aperçu du document" className="im-docprint-frame" srcDoc={docApercu.html} />}
          </div>
        </div>
      )}

      {demandeOr && editingId?.interventionId && (
        <DemandeNumeroOr
          dossier={{
            id: editingId.interventionId,
            immatriculation: formData.immatriculation,
            marque: formData.marque, modele: formData.modele,
            client_nom: formData.client_nom, client_prenom: formData.client_prenom,
          }}
          onFerme={() => setDemandeOr(false)}
          onEnregistre={(num) => {
            // Le champ dédié de l'en-tête reçoit la valeur : l'utilisateur voit
            // le résultat là où il l'aurait saisi lui-même.
            setFormData(f => ({ ...f, numero_or: num }));
            setDemandeOr(false);
          }}
        />
      )}

      {/* ── Visionneuse photo / vidéo ──
          Elle vit à l'intérieur de .im-overlay, dont le onClick ferme le
          dossier : SANS stopPropagation, fermer la photo fermait aussi
          l'intervention. Chaque clic est donc arrêté ici. */}
      {lightbox && (() => {
        const items = lightbox.items || [];
        const courant = resoudreMedia(items[lightbox.index]);
        const estVideo = (courant?.type || '').startsWith('video/');
        return (
          <div className="im-lightbox im-lb" onClick={(e) => { e.stopPropagation(); setLightbox(null); }}>
            {/* La scène occupe toute la largeur : elle NE doit PAS arrêter le
                clic, sinon cliquer à côté de la photo ne ferme plus rien. Seuls
                le média lui-même et les commandes le font. */}
            <div className="im-lb-scene">
              {courant?.erreur ? (
                <div className="im-lb-attente">⚠️ Média indisponible</div>
              ) : !courant?.pret ? (
                <div className="im-lb-attente">⏳ Chargement…</div>
              ) : estVideo ? (
                // key : sans remontage, passer d'une vidéo à l'autre garde le
                // lecteur précédent et autoPlay ne se redéclenche pas.
                <video key={courant.url} src={courant.url} controls autoPlay playsInline
                  onClick={(e) => e.stopPropagation()} />
              ) : (
                <img src={courant.url} alt={courant.legende || ''} onClick={(e) => e.stopPropagation()} />
              )}
            </div>

            {lightbox.index > 0 && (
              <button type="button" className="im-lb-nav prec" title="Précédent (←)"
                onClick={(e) => { e.stopPropagation(); allerLightbox(-1); }}>‹</button>
            )}
            {lightbox.index < items.length - 1 && (
              <button type="button" className="im-lb-nav suiv" title="Suivant (→)"
                onClick={(e) => { e.stopPropagation(); allerLightbox(1); }}>›</button>
            )}

            <div className="im-lb-legende" onClick={(e) => e.stopPropagation()}>
              {items.length > 1 && <span className="im-lb-compteur">{lightbox.index + 1} / {items.length}</span>}
              {courant?.legende && <span>{courant.legende}</span>}
            </div>

            {items.length > 1 && (
              <div className="im-lb-bande" onClick={(e) => e.stopPropagation()}>
                {items.map((it, i) => {
                  const m = resoudreMedia(it);
                  const v = (m?.type || '').startsWith('video/');
                  return (
                    <button key={i} type="button"
                      ref={i === lightbox.index ? vignetteActiveRef : null}
                      className={`im-lb-vignette${i === lightbox.index ? ' active' : ''}`}
                      title={`${i + 1} / ${items.length}`}
                      onClick={() => setLightbox(l => ({ ...l, index: i }))}>
                      {!m?.pret ? (
                        <span className="im-lb-vignette-attente">{m?.erreur ? '⚠️' : '⏳'}</span>
                      ) : v ? (
                        <>
                          <video src={m.url} preload="metadata" muted playsInline />
                          <span className="im-lb-vignette-play">▶</span>
                        </>
                      ) : (
                        <img src={m.url} alt="" loading="lazy" />
                      )}
                    </button>
                  );
                })}
              </div>
            )}

            <button className="im-lightbox-close" title="Fermer (Échap)"
              onClick={(e) => { e.stopPropagation(); setLightbox(null); }}>✕</button>
          </div>
        );
      })()}

      {/* ── Document viewer ── */}
      {docViewer && (
        <div className="im-lightbox" onClick={(e) => { e.stopPropagation(); setDocViewer(null); }}>
          <div onClick={(e) => e.stopPropagation()} style={{ width: '90vw', height: '85vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {docViewer.type.startsWith('image/') ? <img src={docViewer.url} alt={docViewer.name} style={{ maxWidth: '95%', maxHeight: '95%', objectFit: 'contain', borderRadius: 8 }} />
            : docViewer.type === 'application/pdf' ? <embed src={docViewer.url} type="application/pdf" style={{ width: '100%', height: '100%', borderRadius: 8 }} />
            : <div style={{ textAlign: 'center', color: '#fff' }}><div style={{ fontSize: 48, marginBottom: 16 }}>📎</div><div style={{ fontSize: 16, marginBottom: 12 }}>{docViewer.name}</div><a href={docViewer.url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--color-accent)' }}>Télécharger</a></div>}
          </div>
          <button className="im-lightbox-close" onClick={(e) => { e.stopPropagation(); setDocViewer(null); }}>✕</button>
        </div>
      )}
    </div>
  );
}
